import axios from 'axios';
import type { Claim } from '@/types';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// withCredentials so the signed session cookie from /api/auth/verify-otp
// is sent on every subsequent call. Browsers also require this when the
// frontend and backend are on different ports (e.g. CRA 3000 + uvicorn 8000).
const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

// Global 401 interceptor: when the session expires mid-session, bounce
// the drafter back to the login screen instead of showing a wall of
// red errors. Relies on App.js reading localStorage to pick up the
// auth state change — store it as a plain key so every component can
// useSyncExternalStore if needed.
let _onUnauthenticated: (() => void) | null = null;
export const setUnauthenticatedHandler = (fn: () => void) => {
  _onUnauthenticated = fn;
};
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && _onUnauthenticated) {
      _onUnauthenticated();
    }
    return Promise.reject(err);
  },
);

// ─── Auth ────────────────────────────────────────────────────────
export interface MeResponse {
  authenticated: boolean;
  user?: { id: number; email: string; role: string; workspace_id: number | null };
}

export const getMe = (): Promise<MeResponse> =>
  api.get('/auth/me').then((r) => r.data);

export const requestOtp = (email: string) =>
  api.post('/auth/request-otp', { email }).then((r) => r.data);

export const verifyOtp = (email: string, code: string) =>
  api.post('/auth/verify-otp', { email, code }).then((r) => r.data);

export const logout = () => api.post('/auth/logout').then((r) => r.data);

// ─── Matter ──────────────────────────────────────────────────────
export interface MatterCreatePayload {
  title: string;
  jurisdiction: string;
  docket_id?: string;
  client_name?: string;
  client_reference?: string;
  invention_title?: string;
  technology_area?: string;
  tags?: string;
  filing_deadline?: string;
  priority_date?: string;
  /** 'spec_draft' starts the matter on the Specification-draft intake
   * (claims, IDF and drawings already exist, only the specification is
   * to be drafted). Omitted = the six-stage road. */
  entry_mode?: 'spec_draft';
}

export const createMatter = (
  titleOrPayload: string | MatterCreatePayload,
  jurisdiction?: string,
) => {
  const payload =
    typeof titleOrPayload === 'string'
      ? { title: titleOrPayload, jurisdiction: jurisdiction ?? 'US' }
      : titleOrPayload;
  return api.post('/matter', payload).then((r) => r.data);
};

export const getMatter = (id: string) =>
  api.get(`/matter/${id}`).then(r => r.data);

// ─── epi compliance: stage review acknowledgment ─────────────────
// Item 2 of the epi Guidelines UI Compliance addendum. Drafter
// toggles a per-stage "I have reviewed the content on this stage"
// checkbox; the freeze / Next button ANDs this with the existing
// gate conditions. Server persists acknowledged_at + acknowledged_by
// on the stage's stage_state.content (or stage_3's content with
// stage_4_* keys for Stage 4 since stage_state has no stage_4 row).
export type AckStage =
  | 'stage_1' | 'stage_2' | 'stage_2_5' | 'stage_3' | 'stage_4';

export interface StageAckResponse {
  stage: AckStage;
  storage_stage: string;
  acknowledged_at: string | null;
  acknowledged_by: number | null;
}

export const setStageAcknowledged = (
  matterId: string, stage: AckStage, acknowledged: boolean,
): Promise<StageAckResponse> =>
  api.post(`/matter/${matterId}/stage_state/${stage}/acknowledge`, {
    acknowledged,
  }).then(r => r.data);

// ─── epi compliance Item 4: matter activity aggregation ──────────
// Chronological event stream derived from stage_state, freeze_log,
// llm_call_log, and claim.version_history. Read-only endpoint —
// useful for the Diagnostics "Download activity JSON" button and
// as the source of truth for the export provenance block (Item 5).
export interface MatterActivityEvent {
  ts: string;
  type:
    | 'stage_transition'
    | 'stage_acknowledged'
    | 'ai_generation'
    | 'human_edit';
  stage?: string | null;
  from?: string | null;
  to?: string | null;
  user?: number | string | null;
  prompt_version_id?: string | null;
  operation?: string | null;
  model?: string | null;
  target?: string | null;
  claim_id?: string | null;
  action?: string | null;
  parse_status?: string | null;
  duration_ms?: number | null;
}

export const getMatterActivity = (
  matterId: string,
): Promise<MatterActivityEvent[]> =>
  api.get(`/matters/${matterId}/activity`).then(r => r.data);

export interface MatterListQuery {
  client?: string;
  status?: string;
  jurisdiction?: string;
  technology?: string;
  search?: string;
  archived?: 'false' | 'true' | 'only';
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export const listMatters = (query: MatterListQuery = {}) => {
  // Drop empty strings / undefined so the backend's default branches kick in.
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params[k] = v as string | number;
  }
  return api.get('/matters', { params }).then((r) => r.data);
};

export const listMatterClients = () =>
  api.get('/matters/clients').then((r) => r.data);

export type MatterPatch = Partial<Omit<MatterCreatePayload, 'title' | 'jurisdiction'>> & {
  title?: string;
  jurisdiction?: string;
  status?: string;
};

export const updateMatter = (id: string, patch: MatterPatch) =>
  api.patch(`/matter/${id}`, patch).then((r) => r.data);

export const archiveMatter = (id: string) =>
  api.post(`/matter/${id}/archive`).then((r) => r.data);

export const unarchiveMatter = (id: string) =>
  api.post(`/matter/${id}/unarchive`).then((r) => r.data);

export interface ResetMatterResult {
  reset: boolean;
  deleted_claims: number;
  deleted_frozen_indexes: number;
  /** Count of matter_figures rows wiped. Undefined on servers that
   *  predate the reset-cascade fix (older deployments may still be
   *  consuming this shape — field is optional for backward compat). */
  deleted_figures?: number;
  figure_files_unlinked?: number;
}

/** Drawing-side summary shape (matches the backend's
 *  ``compute_drawing_rollup`` payload). Carried inside the Phase 10
 *  ``drawings_present`` 409 detail so the dialog can render
 *  per-figure counts without an extra round-trip. */
export interface DrawingSummary {
  figures_count: number;
  frozen_count: number;
  unanalyzed_count: number;
  issues_count: number;
  active_figure_id: string | null;
  last_activity_at: string | null;
}

/** Body shape for the 409 the Phase 10 reset gate emits when the
 *  matter has drawing state and the drafter hasn't yet confirmed
 *  with ``confirm_drawings_will_be_deleted``. */
export interface ResetMatterDrawingsPresentDetail {
  error_code: 'drawings_present';
  message: string;
  drawing_summary: DrawingSummary;
  export_zip_url: string;
}

export interface ResetMatterRequestBody {
  /** Phase 10 — set true ONLY after the drafter checks the
   *  acknowledgement in the enhanced reset dialog. Backwards
   *  compatible: omitting it (or false) on a no-drawings matter
   *  works exactly like the pre-Phase-10 endpoint. */
  confirm_drawings_will_be_deleted?: boolean;
}

export const resetMatter = (
  id: string, body?: ResetMatterRequestBody,
): Promise<ResetMatterResult> =>
  api.post(`/matter/${id}/reset`, body || {}).then((r) => r.data);

// ─── Matter ACL — collaborators ────────────────────────────────────
export interface MatterCollaborator {
  user_id: number;
  email?: string | null;
  role?: string | null;
  granted_at?: string | null;
  granted_by_email?: string | null;
}
export interface MatterCollaboratorsResponse {
  matter_id: string;
  /** Owner identifiers — surfaced so the Sharing modal can render
   *  the owner email instead of "User #N". May be null for legacy
   *  matters created before owner_id was tracked. */
  owner_id?: number | null;
  owner_email?: string | null;
  collaborators: MatterCollaborator[];
}
export const listMatterCollaborators = (
  matterId: string,
): Promise<MatterCollaboratorsResponse> =>
  api.get(`/matter/${matterId}/collaborators`).then((r) => r.data);

export const addMatterCollaborator = (
  matterId: string,
  body: { user_id?: number; email?: string },
): Promise<{ matter_id: string; user_id: number; email?: string; role?: string }> =>
  api.post(`/matter/${matterId}/collaborators`, body).then((r) => r.data);

export const removeMatterCollaborator = (
  matterId: string, userId: number,
): Promise<void> =>
  api.delete(`/matter/${matterId}/collaborators/${userId}`).then((r) => r.data);

/** URL for the Phase 10 drawings export ZIP. Anchor href consumes
 *  it — no fetch needed; the browser handles the download via the
 *  endpoint's Content-Disposition: attachment header. */
export const drawingExportZipUrl = (matterId: string): string =>
  `/api/matter/${matterId}/drawing/export-zip`;

/** URL for the Phase 11 USPTO-compliant PDF export. Same anchor-
 *  href pattern as the ZIP endpoint. ``paper`` defaults to letter,
 *  ``include`` to frozen — both match the backend defaults so the
 *  query string is omitted when callers don't override them
 *  (keeps the URL tidy). */
export const drawingExportPdfUrl = (
  matterId: string,
  opts?: { paper?: 'letter' | 'a4'; include?: 'frozen' | 'all' },
): string => {
  const params: string[] = [];
  if (opts?.paper && opts.paper !== 'letter') {
    params.push(`paper=${opts.paper}`);
  }
  if (opts?.include && opts.include !== 'frozen') {
    params.push(`include=${opts.include}`);
  }
  const qs = params.length ? `?${params.join('&')}` : '';
  return `/api/matter/${matterId}/drawing/export-pdf${qs}`;
};

/** URL for the Phase 11 Provenance DOCX export. Always available
 *  (no frozen-figure gate); the document is the matter's drawing-
 *  side audit trail. */
export const drawingExportProvenanceDocxUrl = (matterId: string): string =>
  `/api/matter/${matterId}/drawing/export-provenance-docx`;

/** URL for the HR-P2.3 PDF inline-preview endpoint. Same renderer
 *  as ``drawingExportPdfUrl``; the only difference is
 *  ``Content-Disposition: inline`` so the browser previews the
 *  PDF in a tab instead of triggering a download. ``opts``
 *  matches the export shape so the menu can default the same
 *  way (paper=letter, include=frozen). */
export const drawingPreviewPdfUrl = (
  matterId: string,
  opts?: { paper?: 'letter' | 'a4'; include?: 'frozen' | 'all' },
): string => {
  const params: string[] = [];
  if (opts?.paper && opts.paper !== 'letter') {
    params.push(`paper=${opts.paper}`);
  }
  if (opts?.include && opts.include !== 'frozen') {
    params.push(`include=${opts.include}`);
  }
  const qs = params.length ? `?${params.join('&')}` : '';
  return `/api/matter/${matterId}/drawing/preview-pdf${qs}`;
};

// ─── PageIndex document status ──────────────────────────────────
export type DocumentStatus =
  | 'uploading'    // RQ async path — row pre-INSERTed before the
                   // worker picks the job up. Treated as in-flight
                   // by the UI bucket below.
  | 'uploaded'
  | 'extracting'
  | 'indexing'
  | 'processing'   // legacy rows indexed under the old pipeline,
                   // AND the RQ worker's "currently running" status
                   // after it picks the job up
  | 'indexed'
  | 'failed'
  | 'archived';

export interface MatterDocument {
  id: number;
  doc_name: string;
  doc_type: string;
  status: DocumentStatus;
  indexed_at: string | null;
  uploaded_at: string;
  has_images: boolean;
  image_count: number;
  vision_processed: boolean;
  // Format-specific metadata. file_format is the file extension
  // (pdf / docx / pptx / txt / png / jpg / …); slide_count is
  // populated for PPTX, page_count for PDF.
  file_format?: string | null;
  slide_count?: number | null;
  page_count?: number | null;
  /** Character count of the extracted text. Captured at extraction
   *  time. Nullable on legacy rows / brand-new uploads whose
   *  extraction hasn't finished yet — render no badge in that case.
   *  Used by the file list to surface a "Large document" / "Too
   *  large for full indexing" warning before the sync indexer hits
   *  its ~80k-char cap. */
  char_count?: number | null;
  /** Auto-classification (lite-tier LLM, runs after extraction). The
   *  raw classifier verdict (invention_disclosure / call_transcript
   *  / prior_art / supplementary). Independent of doc_type so the
   *  UI can show what the model thought even if the drafter
   *  overrode it via the dropdown. Null on legacy rows and on
   *  rows whose extraction hasn't completed yet. */
  classifier_label?: string | null;
  /** True only when the classifier called this doc an IDF but a
   *  primary IDF already exists for the matter — the upload
   *  pipeline downgrades the row to supplementary and surfaces a
   *  "Promote to primary?" affordance instead of silently
   *  archiving the existing primary. */
  classifier_conflict?: boolean | null;
  /** Backend-computed flag — true when status is non-terminal AND the
   *  uploaded_at timestamp is older than 5 minutes (i.e. the
   *  background indexer is stuck / crashed). The UI surfaces a Retry
   *  affordance for stale rows so the drafter can recover without
   *  re-uploading the file. */
  stale?: boolean;
}

export const listMatterDocuments = (matterId: string): Promise<{ documents: MatterDocument[] }> =>
  api.get(`/matters/${matterId}/documents`).then((r) => r.data);

export const retryMatterDocument = (matterId: string, docId: number) =>
  api.post(`/matters/${matterId}/documents/${docId}/retry`).then((r) => r.data);

// Drafter-controlled doc_type override. Closes the multi-document
// silent-degradation gap: a transcript that the upload-time auto-
// inference classified as the primary IDF can be moved to
// supplementary post-hoc, and the actual IDF promoted, without a
// re-upload + 409 confirm-replace round-trip. Server archives any
// existing primary on promotion.
export interface UpdateMatterDocumentBody {
  doc_type?: 'idf' | 'supplementary' | 'prior_art';
}
export interface UpdateMatterDocumentResp {
  status: 'updated';
  doc_type: string;
  /** True when this PATCH promoted a non-primary doc to ``idf`` AND
   *  another row was already the primary. The previous primary is
   *  DEMOTED to ``supplementary`` (not archived) so it stays in
   *  the document list + retrieval pool. UIs should surface a
   *  "Stage 1 may need rescoring" hint when this is true. */
  previous_primary_demoted: boolean;
  /** Row id of the demoted previous primary, or null when there
   *  was no previous primary. */
  previous_primary_id: number | null;
  warning?: string;
}
export const updateMatterDocument = (
  matterId: string,
  docId: number,
  body: UpdateMatterDocumentBody,
): Promise<UpdateMatterDocumentResp> =>
  api
    .patch(`/matters/${matterId}/documents/${docId}`, body)
    .then((r) => r.data);

// Generic document delete — works for primary IDF and supplementary
// uploads alike (delete_idf is keyed on idf_document.id and can't
// reach supplementary rows). Cascades matter_figures + matter_indexes
// + on-disk files. Backend refuses while Stage 1 is frozen.
export interface DeleteMatterDocumentResp {
  status: 'deleted';
  document_id: number;
  matter_figures_removed: number;
  figure_files_unlinked: number;
  idf_document_removed: number;
  source_file_unlinked: boolean;
}
export const deleteMatterDocument = (
  matterId: string, documentId: number,
): Promise<DeleteMatterDocumentResp> =>
  api.delete(`/matters/${matterId}/documents/${documentId}`).then((r) => r.data);

// ─── Persisted figures (Stage 1 extraction status) ──────────────
// The backend persists every embedded image to disk and records a
// ``matter_figures`` row with a caption from the vision pass (or
// empty when the image is an EMF/WMF with no decodable raster).
// Surfaced to Stage 1 as a compact "extracted / captioned" status
// line so the drafter knows whether the pipeline saw the figures
// in their IDF. The drawings stage consumes the same data later.
export interface MatterFigure {
  id: number;
  document_id: number | null;
  /** Display name of the source matter_documents row — populated via
   *  LEFT JOIN on the backend, so legacy figures with a stale
   *  document_id come back as null and the UI buckets them under an
   *  "Unknown source" group. */
  document_name: string | null;
  document_type: string | null;
  fig_number: string;
  fig_type: string | null;
  sequence_num: number | null;
  description: string;
  mime_type: string | null;
  width: number | null;
  height: number | null;
  source_label: string | null;
  status: string;
  /** Whether this figure's caption is pulled into build_idf_context's
   *  [FIGURES] block. Drafter-controlled via the include toggle in the
   *  Figures tab. Defaults TRUE for newly-analysed figures. */
  included_in_context: boolean;
  /** True when the backend has a browser-viewable PNG side-car for
   *  this figure (LibreOffice-rendered preview of an EMF/WMF vector).
   *  The ``url`` above serves the preview automatically when present,
   *  but the flag lets the UI drop the amber "vector placeholder"
   *  overlay and show the real thumbnail with a subtle "converted"
   *  note instead. */
  has_preview?: boolean;
  created_at: string | null;
  url: string;
}

export const listMatterFigures = (
  matterId: string,
): Promise<{ figure_count: number; figures: MatterFigure[] }> =>
  api.get(`/matter/${matterId}/figures`).then((r) => r.data);

// Discard a single persisted figure. Backend deletes the matter_figures
// row + (best-effort) unlinks the image on disk; returns 204.
export const deleteMatterFigure = (matterId: string, figureId: number): Promise<void> =>
  api.delete(`/matter/${matterId}/figures/${figureId}`).then(() => undefined);

// Run vision analysis on a single persisted figure. Backend writes
// the caption to matter_figures.description AND resets
// included_in_context=TRUE (a fresh caption implies the drafter
// wants the figure back in the context block).
export interface AnalyseMatterFigureResp {
  figure_id: number;
  description: string;
  included_in_context: boolean;
  status: 'ok';
}
export const analyseMatterFigure = (
  matterId: string, figureId: number,
): Promise<AnalyseMatterFigureResp> =>
  api.post(`/matter/${matterId}/figures/${figureId}/analyse`).then((r) => r.data);

// Mutate per-figure flags. Currently exposes only the
// included_in_context toggle; description is set via /analyse and
// not free-editable from the UI. Returns the updated figure object.
export interface MatterFigurePatch {
  included_in_context?: boolean;
}
export const patchMatterFigure = (
  matterId: string, figureId: number, patch: MatterFigurePatch,
): Promise<MatterFigure> =>
  api.patch(`/matter/${matterId}/figures/${figureId}`, patch).then((r) => r.data);

// ─── Active models ──────────────────────────────────────────────
export interface ActiveModels {
  tree_model: string;
  draft_model: string;
  fast_model: string;
  vision_model: string;
  qa_model: string;
  qc_model: string;
  retrieval_model: string;
  provenance_model: string;
  pageindex_enabled: boolean;
}

export const getActiveModels = (): Promise<ActiveModels> =>
  api.get('/settings/active-models').then((r) => r.data);

// ─── Context Budget ─────────────────────────────────────────────
export interface ContextBudget {
  estimated_tokens: number;
  context_window: number;
  model_name: string;
  provider: string;
  utilization_pct: number;
}

export const getContextBudget = (matterId: string): Promise<ContextBudget> =>
  api.get(`/matter/${matterId}/context_budget`).then(r => r.data);

export const deleteMatter = (matterId: string) =>
  api.delete(`/matter/${matterId}`).then(r => r.data);

// ─── Stage 1 ─────────────────────────────────────────────────────
/**
 * Upload a single IDF document. `useVision` maps to the backend's
 * `use_vision` form field:
 *   - "auto"  — run LLM vision over embedded images if an LLM key is set
 *   - "on"    — force vision (surface errors as warnings)
 *   - "off"   — skip vision entirely (fastest, text-only)
 * Callers that loop over a FileList should await each call to keep
 * the flow strictly one-file-at-a-time so each extract + classify
 * step runs sequentially.
 *
 * `docType` defaults to 'auto'. The backend resolves 'auto' against
 * the current matter state (no primary IDF yet → 'idf', otherwise
 * 'supplementary') inside its upload transaction, so concurrent
 * batches can't race into a 409. Callers that explicitly want to
 * replace an IDF should pass docType='idf' with confirmReplace=true.
 */
export type UploadDocType = 'auto' | 'idf' | 'supplementary' | 'prior_art';

/**
 * Sync (200) response shape — the existing inline-extract path.
 * Keys come from the legacy handler; the async branch never produces
 * this shape.
 */
export interface UploadIdfSyncResponse {
  id: string;
  document_id: number | null;
  filename: string;
  extracted_text: string;
  extraction_warnings: string[];
  embedded_image_count: number;
  vision_used: boolean;
}

/**
 * Async (202) response shape — the new RQ-worker path. Returned
 * when the use_async_upload flag is on; the document is queued and
 * the caller polls /api/jobs/{job_id}/status for completion.
 */
export interface UploadIdfAsyncResponse {
  status: 'processing';
  document_id: string;
  job_id: string;
  poll_url: string;
  message: string;
}

export type UploadIdfResult =
  | { status: 200; data: UploadIdfSyncResponse }
  | { status: 202; data: UploadIdfAsyncResponse };

/**
 * Upload an IDF / supplementary document.
 *
 * Returns ``{ status, data }`` so callers can branch on the HTTP
 * status — 200 means the existing inline path completed and the
 * UI should refresh the file list immediately, while 202 means the
 * RQ worker is processing in the background and the caller should
 * poll ``getJobStatus(data.job_id)`` until status='finished'.
 *
 * Pre-this-commit callers consumed only ``r.data``; the new shape
 * is a deliberate breaking change in service of the dual-path
 * frontend. The single in-tree caller (UploadZone) is updated in
 * the same commit.
 */
export const uploadIdf = (
  matterId: string,
  file: File,
  useVision: 'auto' | 'on' | 'off' = 'auto',
  docType: UploadDocType = 'auto',
  confirmReplace: boolean = false,
  // Optional AbortSignal so callers can cancel a hanging upload
  // (per-file timeout in handleUpload). Without this an axios POST
  // has no client-side timeout — a network blip / proxy idle drop
  // leaves the request hanging forever and the upload progress UI
  // stuck on "Uploading N of M".
  signal?: AbortSignal,
): Promise<UploadIdfResult> => {
  const form = new FormData();
  form.append('file', file);
  form.append('use_vision', useVision);
  form.append('doc_type', docType);
  if (confirmReplace) form.append('confirm_replace', 'true');
  return api.post(`/matter/${matterId}/idf`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
    // 202 has to pass through; axios's default validateStatus
    // already accepts 2xx so this line is informational, but
    // pinning it here keeps the contract explicit.
    validateStatus: (s) => s >= 200 && s < 300,
  }).then((r) => ({ status: r.status as 200 | 202, data: r.data })) as Promise<UploadIdfResult>;
};


/** Job-status response returned by ``GET /api/jobs/{id}/status``.
 *
 *  Two generations of fields:
 *    - Original (document ingestion): {job_id, status, enqueued,
 *      started, ended, result, error, progress, queue_position}
 *    - Sub-Chunk 3 enrichments (claims/drawing/spec): module,
 *      stage, action, final_provider, fallback_count, history_id,
 *      rq_status. All nullable so existing consumers keep working.
 */
export interface JobStatus {
  job_id: string;
  /** Durable status from stage_jobs when available, else raw RQ
   *  status. Module-aware values: 'queued' | 'running' |
   *  'succeeded' | 'failed' | 'refused_all_providers' | 'cancelled'.
   *  Original RQ values still surface for legacy document jobs:
   *  'started' | 'finished' | 'stopped' | 'deferred' | 'scheduled'. */
  status: string;
  /** Raw RQ-native status preserved separately. 'expired' when the
   *  RQ blob aged out but stage_jobs still has the durable row. */
  rq_status?: string;
  enqueued: string | null;
  started: string | null;
  ended: string | null;
  result: any;
  error: string | null;
  /** Standardized progress beacon contract. ``provider`` +
   *  ``fallback_count`` populated by claims/drawing/spec jobs;
   *  null/0 for legacy document jobs. */
  progress: {
    step: string;
    message: string;
    provider?: string | null;
    fallback_count?: number;
    updated_at?: string;
    ts?: number;  // legacy field on document jobs
  } | null;
  queue_position: number | null;
  /** Sub-Chunk 3 enrichments — null for legacy document jobs that
   *  predate the stage_jobs history table. */
  history_id?: string | null;
  module?: 'claims' | 'drawing' | 'spec' | 'document' | null;
  stage?: string | null;
  action?: string | null;
  final_provider?: string | null;
  fallback_count?: number;
}

export const getJobStatus = (jobId: string): Promise<JobStatus> =>
  api.get(`/jobs/${encodeURIComponent(jobId)}/status`).then((r) => r.data);

export const deleteIdf = (matterId: string, idfId: string) =>
  api.delete(`/matter/${matterId}/idf/${idfId}`).then(r => r.data);

export const updateDrafterNotes = (matterId: string, notes: string) =>
  api.put(`/matter/${matterId}/stage_1/notes`, { notes }).then(r => r.data);

// Stage 1 Summary-tab inline-edit endpoints.  Field keys are the stable
// API names the backend accepts (`overall`, `problem`, `solution`,
// `implementation`, `prior_art`) — internally the backend maps these
// to ``content.summary.{overview,problem,solution,implementation,
// prior_art_mentioned}``.
export type Stage1SummaryField =
  | 'overall' | 'problem' | 'solution' | 'implementation' | 'prior_art';

export interface Stage1SummaryPatchResponse {
  field: Stage1SummaryField;
  value: string | string[];
  saved_at: string;
  edited_fields: Stage1SummaryField[];
}

export const patchStage1Summary = (
  matterId: string, field: Stage1SummaryField, value: string | string[],
): Promise<Stage1SummaryPatchResponse> =>
  api.patch(`/matters/${matterId}/stage1/summary`, { field, value }).then(r => r.data);

export interface Stage1Snapshot {
  overall: string;
  problem: string;
  solution: string;
  implementation: string;
  prior_art: string[];
}

export const getStage1Snapshot = (
  matterId: string,
): Promise<{ snapshot: Stage1Snapshot }> =>
  api.get(`/matters/${matterId}/stage1/snapshot`).then(r => r.data);

export const regenerateStage1SummaryField = (
  matterId: string, field: Stage1SummaryField,
): Promise<{ field: Stage1SummaryField; suggestion: string | string[] }> =>
  api.post(`/matters/${matterId}/stage1/summary/${field}/regenerate`).then(r => r.data);

export const generateStage1TechnicalUnderstanding = (
  matterId: string,
): Promise<{ technical_understanding: any }> =>
  api.post(`/matter/${matterId}/stage_1/technical_understanding/generate`).then(r => r.data);

/** Analyze IDF / Rescore keep the drafter's edited summary fields;
 *  Re-extract passes ``replaceEdits`` after asking. */
export const scoreStage1 = (
  matterId: string, opts: { replaceEdits?: boolean } = {},
) =>
  api.post(
    `/matter/${matterId}/stage_1/score`,
    undefined,
    opts.replaceEdits ? { params: { replace_edits: true } } : undefined,
  ).then(r => r.data);

export const generateQa = (matterId: string) =>
  api.post(`/matter/${matterId}/stage_1/qa`).then(r => r.data);

export const submitAnswers = (matterId: string, answers: any[]) =>
  api.post(`/matter/${matterId}/stage_1/answers`, { answers }).then(r => r.data);

export const freezeStage1 = (matterId: string) =>
  api.post(`/matter/${matterId}/stage_1/freeze`).then(r => r.data);

// Phase 15 — idf_gap_analysis 10-dimension diagnostic panel.
// Backend caches results for 24h on stage_state.content.gap_analysis_cache.
// `prior_art_context` always invalidates the cache so drafter notes feed
// a fresh run; `force_refresh: true` re-runs even with no new context.
export interface GapAnalysisDimension {
  id: string;
  label: string;
  score: number;        // 1-5
  severity: 'ok' | 'warn' | 'block';
  finding: string;
  suggestion: string;
}
export interface GapAnalysisResult {
  dimensions: GapAnalysisDimension[];
  overall_readiness: 'ready' | 'needs_work' | 'incomplete';
  overall_score: number;
  cached_at: string;
  cache_hit: boolean;
}
export const runGapAnalysis = (
  matterId: string,
  opts: { prior_art_context?: string; force_refresh?: boolean } = {},
): Promise<GapAnalysisResult> =>
  api.post(`/matter/${matterId}/stage_1/gap_analysis`, opts).then(r => r.data);

// Stage 0 → Stage 1 promotion. Backend gates on at least one
// matter_documents row with status='indexed'; a 400 surfaces here
// as the message "Upload and process at least one document before
// continuing." Idempotent on stage_1+: server echoes the current
// stage instead of throwing if the matter has already advanced,
// so a replayed click after a network hiccup doesn't 400.
export const advanceStage0 = (matterId: string): Promise<{ current_stage: string }> =>
  api.post(`/matter/${matterId}/stage_0/advance`).then(r => r.data);

// Download the inventor questionnaire (Stage 1 Q&A) as a DOCX so the drafter
// can email it to the inventor or print it for a meeting. Mirrors the
// on-screen gamified view: current-score → 100 framing, target improvement,
// answer hints, and a blank write-in box per question.
export async function downloadQaQuestionnaire(matterId: string, fallbackTitle = 'matter') {
  const res = await api.get(`/matter/${matterId}/stage_1/qa/export.docx`, { responseType: 'blob' });
  const disp = (res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || '') as string;
  let filename = `${fallbackTitle.replace(/[^A-Za-z0-9._-]+/g, '_')}_inventor_questionnaire.docx`;
  const starMatch = /filename\*=UTF-8''([^;\n]+)/i.exec(disp);
  const plainMatch = /filename="?([^";\n]+)"?/i.exec(disp);
  if (starMatch?.[1]) {
    try { filename = decodeURIComponent(starMatch[1]); } catch { /* keep fallback */ }
  } else if (plainMatch?.[1]) {
    filename = plainMatch[1];
  }
  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// ─── Stage 2 ─────────────────────────────────────────────────────
export const generateStage2 = (matterId: string) =>
  api.post(`/matter/${matterId}/stage_2/generate`).then(r => r.data);

// ─── Resilient Stage 2 generation ────────────────────────────────
// Backend already runs novelty + inventive-step in parallel via
// asyncio.gather (server.py:7774), so the sum of the two calls
// doesn't matter — the max of the two does. When a single LLM
// call still exceeds Cloudflare's 100-second edge timeout, the
// HTTP response returns 524 even though the origin is still
// writing to the DB. In that case the write often SUCCEEDS
// shortly after CF gave up — the result sits in stage_2.content
// waiting to be displayed. This wrapper catches the timeout
// class (524/502/504/network), re-fetches the matter, and
// returns the saved result as if the original call had
// succeeded. Only retries once (at most) if the matter still
// shows no saved result — a second full-latency LLM call on a
// slow day would just time out again.

/** Thrown when the generation call timed out AND the backend
 *  hasn't persisted the result yet. Callers use this to render
 *  the inline "Try again / Check if saved" UI instead of a
 *  generic error toast. */
export class GenerationTimeoutError extends Error {
  public readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'GenerationTimeoutError';
    this.status = status;
  }
}

/** Phase 13B — thrown when the backend signalled
 *  ``error_code='prompt_too_large'`` (413). Callers can catch this
 *  type explicitly to render a model-specific suggestion ("Use a
 *  larger-context model / shorter document / fewer files") rather
 *  than a generic error toast.
 *
 *  ``getPromptTooLargeMessage(err)`` extracts a user-facing
 *  message + optional suggestion when an arbitrary error object
 *  matches the 413 prompt-too-large shape, regardless of whether
 *  the call site explicitly threw this class. Saves every endpoint
 *  caller from re-implementing the shape sniff. */
export class PromptTooLargeError extends Error {
  public readonly suggestion: string;
  public readonly provider: string;
  constructor(message: string, suggestion: string, provider: string) {
    super(message);
    this.name = 'PromptTooLargeError';
    this.suggestion = suggestion;
    this.provider = provider;
  }
}

export function getPromptTooLargeMessage(
  err: unknown,
): { message: string; suggestion: string; provider: string } | null {
  // Match any error shape carrying error_code='prompt_too_large'.
  // Includes our typed exception, fetch responses, axios errors,
  // plain objects from unwrapped JSON.
  const e = err as any;
  if (e && e.name === 'PromptTooLargeError') {
    return {
      message:    e.message,
      suggestion: e.suggestion,
      provider:   e.provider,
    };
  }
  const detail =
    e?.response?.data?.detail
    ?? e?.detail
    ?? e?.response?.data
    ?? null;
  if (detail && detail.error_code === 'prompt_too_large') {
    return {
      message:    detail.message ?? 'Prompt too large for the selected model.',
      suggestion: detail.suggestion ?? '',
      provider:   detail.provider ?? '',
    };
  }
  return null;
}

const _TIMEOUT_STATUSES = new Set([502, 503, 504, 524]);

/** The response is the app's own error — FastAPI's ``{detail: …}`` —
 * rather than a proxy's page or a bare "Internal Server Error". */
function _hasAppDetail(err: any): boolean {
  const data = err?.response?.data;
  return !!data && typeof data === 'object' && 'detail' in data
    && data.detail !== undefined && data.detail !== null;
}

function _isTimeoutLike(err: any): { timeout: boolean; status: number } {
  const status = err?.response?.status ?? err?.status ?? 0;
  if (_TIMEOUT_STATUSES.has(status)) return { timeout: true, status };
  // Axios + fetch surface network / connection drop errors without
  // a response. Treat those as timeouts for retry purposes — a
  // Cloudflare 524 sometimes arrives as a bare socket close on
  // long-running calls.
  const code = err?.code;
  const name = err?.name;
  const isNetwork = (
    code === 'ECONNABORTED' || code === 'ERR_NETWORK' ||
    code === 'ETIMEDOUT'    || name === 'TimeoutError' ||
    (name === 'AxiosError' && !err?.response)
  );
  return { timeout: isNetwork, status };
}

interface Stage2GenerationResult {
  novelty_text: string;
  inventive_step_text: string;
  /** True when we got the result via a post-timeout refetch rather
   *  than the original POST. Lets the UI show a "Generation
   *  completed — displaying saved result" note. */
  recovered_from_timeout?: boolean;
}

/** Call POST /stage_2/generate with timeout-aware recovery.
 *
 *  Flow:
 *    1. Try the generate call.
 *    2. On success → return result.
 *    3. On 524/502/503/504 or network error → GET /matter/{id}.
 *       If stage_2.content already has non-empty novelty_text +
 *       inventive_step_text → treat as recovered, return them.
 *    4. Otherwise wait ``backoffMs`` and retry the generate call
 *       one more time.
 *    5. If the retry also times out AND the matter still has no
 *       saved result → throw GenerationTimeoutError so the UI
 *       can render the inline retry state.
 *
 *  Non-timeout errors (400/401/403/500 with a JSON body)
 *  propagate immediately — they're not transient.
 */
export async function generateStage2Resilient(
  matterId: string,
  opts: { backoffMs?: number } = {},
): Promise<Stage2GenerationResult> {
  const backoffMs = opts.backoffMs ?? 2000;
  let lastStatus = 0;

  // First attempt.
  try {
    const result = await generateStage2(matterId);
    return {
      novelty_text: result?.novelty_text || '',
      inventive_step_text: result?.inventive_step_text || '',
    };
  } catch (e: any) {
    const { timeout, status } = _isTimeoutLike(e);
    lastStatus = status;
    if (!timeout) throw e;
  }

  // Check if the backend finished writing despite the edge timeout.
  const checkSaved = async (): Promise<Stage2GenerationResult | null> => {
    try {
      const matter = await getMatter(matterId);
      const s2 = (matter as any)?.stages?.stage_2?.content;
      const nov = (s2?.novelty_text || '').toString().trim();
      const inv = (s2?.inventive_step_text || '').toString().trim();
      if (nov && inv) {
        return {
          novelty_text: nov,
          inventive_step_text: inv,
          recovered_from_timeout: true,
        };
      }
    } catch { /* fall through to retry */ }
    return null;
  };

  const savedAfterFirst = await checkSaved();
  if (savedAfterFirst) return savedAfterFirst;

  // Backoff, then retry once.
  await new Promise((resolve) => setTimeout(resolve, backoffMs));
  try {
    const result = await generateStage2(matterId);
    return {
      novelty_text: result?.novelty_text || '',
      inventive_step_text: result?.inventive_step_text || '',
    };
  } catch (e: any) {
    const { timeout, status } = _isTimeoutLike(e);
    lastStatus = status || lastStatus;
    if (!timeout) throw e;
  }

  // One last check-if-saved after the retry — the retry might have
  // also timed out at the edge but succeeded at the origin.
  const savedAfterRetry = await checkSaved();
  if (savedAfterRetry) return savedAfterRetry;

  throw new GenerationTimeoutError(
    'Generation timed out — server is taking longer than expected.',
    lastStatus || 524,
  );
}

/** Standalone "did the backend actually save it?" check — used by
 *  the "Check if saved" button in the Stage 2 timeout error UI
 *  after a user-triggered retry also gives up. */
export async function checkStage2Saved(matterId: string): Promise<Stage2GenerationResult | null> {
  try {
    const matter = await getMatter(matterId);
    const s2 = (matter as any)?.stages?.stage_2?.content;
    const nov = (s2?.novelty_text || '').toString().trim();
    const inv = (s2?.inventive_step_text || '').toString().trim();
    if (nov || inv) {
      return {
        novelty_text: nov,
        inventive_step_text: inv,
        recovered_from_timeout: true,
      };
    }
  } catch { /* nothing saved */ }
  return null;
}

export const updateStage2Field = (matterId: string, field: string, text: string) =>
  api.put(`/matter/${matterId}/stage_2/field/${field}`, { text }).then(r => r.data);

export const freezeStage2 = (matterId: string) =>
  api.post(`/matter/${matterId}/stage_2/freeze`).then(r => r.data);

// Explicit, unidirectional unfreeze of a Stage 2 field. The
// toggle endpoint freezeStage2Field below also handles unfreeze
// when called on a frozen field, but the Unfreeze-button UI
// wants a one-shot call so the action's intent is unambiguous.
// Idempotent server-side.
export const unfreezeStage2Field = (matterId: string, field: string) =>
  api.post(`/matter/${matterId}/stage_2/unfreeze_field/${field}`).then(r => r.data);

export const freezeStage2Field = (matterId: string, field: string) =>
  api.post(`/matter/${matterId}/stage_2/freeze_field/${field}`).then(r => r.data);

// ─── Stage 2.5 ───────────────────────────────────────────────────
export const updateStrategy = (matterId: string, strategyText: string) =>
  api.put(`/matter/${matterId}/stage_2_5`, { strategy_text: strategyText }).then(r => r.data);

export const skipStrategy = (matterId: string) =>
  api.post(`/matter/${matterId}/stage_2_5/skip`).then(r => r.data);

export const confirmStrategy = (matterId: string) =>
  api.post(`/matter/${matterId}/stage_2_5/confirm`).then(r => r.data);

/** Centralised response shape for both ``suggestClaimCategory`` and
 *  ``confirmClaimCategory`` — Option B adds ``secondary_categories``
 *  to the existing primary-category fields. Legacy matters whose
 *  stage_2_5 content predates the secondary field receive an empty
 *  array; the frontend uses presence-of-non-empty to gate Block 2's
 *  saved chip row. */
export interface ClaimCategoryState {
  suggested_category: ClaimCategory | null;
  selected_category: ClaimCategory | null;
  category_suggestion_rationale: string;
  secondary_categories: ClaimCategory[];
  category_selected_at?: string | null;
}

/** Claim 1 category selector — runs the fast-tier classifier on the
 *  matter's stage_1 + stage_2 records and persists the suggestion to
 *  stage_2_5.content.suggested_category. Idempotent. Also returns
 *  any persisted secondary_categories so the frontend's mount-time
 *  fetch can rehydrate Block 2 without an extra round-trip. */
export const suggestClaimCategory = (
  matterId: string,
): Promise<ClaimCategoryState> =>
  api.post(`/matter/${matterId}/stage_2_5/category`).then(
    r => r.data as ClaimCategoryState,
  );

/** Lock the drafter's chosen Claim 1 category + optional list of
 *  additional independent-claim categories. Stage 3 fails closed
 *  on the primary until this resolves; secondary_categories drive
 *  Rule 1b in the claim_generation prompt (one extra independent +
 *  4-6 dependents per category, in the order listed).
 *
 *  Backend dedupes secondary_categories and excludes the primary
 *  from the list — passing duplicates / primary is safe but
 *  silently filtered. */
export const confirmClaimCategory = (
  matterId: string,
  category: ClaimCategory,
  secondaryCategories: ClaimCategory[] = [],
): Promise<{
  selected_category: ClaimCategory;
  secondary_categories: ClaimCategory[];
  category_selected_at: string;
}> =>
  api
    .post(`/matter/${matterId}/stage_2_5/confirm-category`, {
      category,
      secondary_categories: secondaryCategories,
    })
    .then(r => r.data);

export type ClaimCategory =
  | 'apparatus' | 'system' | 'method' | 'composition'
  | 'use' | 'kit' | 'crm';

export const CLAIM_CATEGORIES: ClaimCategory[] = [
  'apparatus', 'system', 'method', 'composition', 'use', 'kit', 'crm',
];

export interface StrategyCard {
  id: string;
  title: string;
  direction: string;
  rationale: string;
  priority: 'high' | 'medium' | 'low';
  comment: string;
  selected: boolean;
  /** Per-card freeze flag added with the Unfreeze-button work.
   *  Defaults to true after the bulk /cards/lock; flipped by the
   *  per-card unfreeze / freeze endpoints. Optional in the type
   *  so legacy lock payloads (no `frozen` key on each card) still
   *  parse — the UI treats an absent value as frozen. */
  frozen?: boolean;
}

export interface StrategyCardsResponse {
  cards?: StrategyCard[];
  error?: string;
  reason?: string;
  raw?: string;
}

export const generateStrategyCards = (
  matterId: string, body: { claim_categories?: string[]; additional_guidance?: string } = {},
): Promise<StrategyCardsResponse> =>
  api.post(`/matter/${matterId}/stage_2_5/cards/generate`, body).then(r => r.data);

export interface StrategyConversationResponse {
  response: string;
  revised_direction: string | null;
  error?: string;
}

export const strategyCardConversation = (
  matterId: string,
  payload: {
    card_id: string;
    card_content: string;
    message: string;
    history?: Array<{ role: 'drafter' | 'advisor'; text: string }>;
  },
): Promise<StrategyConversationResponse> =>
  api.post(`/matter/${matterId}/stage_2_5/cards/conversation`, payload).then(r => r.data);

// Per-card freeze / unfreeze / patch for Stage 2.5 — the bulk
// lockStrategyCards / unlockStrategyCards above flip the WHOLE
// stage's claim_strategy_frozen at once. The per-card endpoints
// below let the drafter unfreeze + edit + re-freeze a single
// card without unwinding the stage-level lock or recomputing
// the cards summary text. No cascade.
export const unfreezeStrategyCard = (matterId: string, cardId: string) =>
  api.post(`/matter/${matterId}/stage_2_5/cards/unfreeze/${cardId}`).then(r => r.data);

export const freezeStrategyCard = (matterId: string, cardId: string) =>
  api.post(`/matter/${matterId}/stage_2_5/cards/freeze/${cardId}`).then(r => r.data);

export const patchStrategyCard = (
  matterId: string, cardId: string,
  patch: Partial<Pick<StrategyCard, 'title' | 'direction' | 'rationale' | 'comment' | 'priority' | 'selected'>>,
) =>
  api.patch(`/matter/${matterId}/stage_2_5/cards/${cardId}`, patch).then(r => r.data);

export const lockStrategyCards = (matterId: string, cards: StrategyCard[]) =>
  api.post(`/matter/${matterId}/stage_2_5/cards/lock`, { cards }).then(r => r.data);

export const unlockStrategyCards = (matterId: string) =>
  api.post(`/matter/${matterId}/stage_2_5/cards/unlock`).then(r => r.data);

// ─── Stage 3 ─────────────────────────────────────────────────────

/** Soft validation warning surfaced by Stage 3 generate. Mirrors
 *  the backend ``generation_warnings`` list — currently the only
 *  emitter is the category_mismatch check, but the shape is
 *  intentionally open so additional non-fatal validators can
 *  ride the same surface without an API change. */
export interface Stage3GenerationWarning {
  code: string;
  message: string;
  locked_category?: string;
  actual_category?: string;
}

export interface Stage3GenerateResponse {
  claims: any[];
  secondary_categories?: string[];
  missing_secondary_categories?: string[];
  /** Non-fatal validation warnings (e.g. category_mismatch).
   *  Absent on a clean generate. */
  warnings?: Stage3GenerationWarning[];
}

// ─── Claim architecture (Stage-3 review panel, plan Phase 4) ──────
// The artifact the pre-pass builds before any claim text exists. The
// panel exists so the strategic calls it makes — which elements are
// essential, how far each term was abstracted, whether the field of
// use belongs in claim 1 — are visible and overridable rather than
// decided invisibly inside one LLM call.

export interface ArchEssentialElement {
  element_id: string;
  claim_term: string;
  as_disclosed: string;
  why_essential: string;
  actor_for_functions: string[];
  evidence: string;
}

export interface ArchOptionalElement {
  element_id: string;
  claim_term: string;
  why_not_essential: string;
  target: 'dependent' | 'independent' | 'omit';
}

export interface ArchLadder {
  element_id: string;
  rungs: string[];
  chosen_rung: string;
  chosen_index: number;
  support_species: string[];
  novelty_carrier: boolean;
  why: string;
}

export interface ArchCooperation {
  a: string;
  b: string;
  relationship: string;
  technical_effect: string;
}

export interface ArchFieldOfUse {
  disclosed?: string;
  required_by_novelty?: boolean;
  claim1_preamble?: string;
}

export interface ArchIndependentPlan {
  aspect: string;
  category: string;
  rationale: string;
}

export interface ArchFactualAssertion {
  assertion: string;
  supported: boolean;
  evidence: string;
  recast: string;
}

export interface ClaimArchitecture {
  schema_version?: string;
  technical_problem?: string;
  technical_solution?: string;
  technical_advancement?: string;
  inventive_core?: string;
  signal_path?: Array<{
    step: number; element_id: string; role: string; carries: string;
  }>;
  essential_elements: ArchEssentialElement[];
  optional_elements: ArchOptionalElement[];
  abstraction_ladder: ArchLadder[];
  cooperation_relations?: ArchCooperation[];
  field_of_use?: ArchFieldOfUse;
  independent_claim_plan?: ArchIndependentPlan[];
  dependent_ladder_plan?: Array<Record<string, any>>;
  factual_assertions?: ArchFactualAssertion[];
}

export interface ClaimArchitectureResponse {
  enabled: boolean;
  present: boolean;
  artifact: ClaimArchitecture | null;
  source: 'auto' | 'edited' | null;
  stage2_fingerprint?: string;
  current_fingerprint?: string;
  /** Stage 2 / 2.5 moved since this artifact was built. Never true for
   *  an edited artifact — the drafter's judgment wins by design. */
  is_stale?: boolean;
  summary: Record<string, any>;
}

export const getClaimArchitecture = (
  matterId: string,
): Promise<ClaimArchitectureResponse> =>
  api.get(`/matter/${matterId}/claim_architecture`).then(r => r.data);

/** Persist drafter overrides. Saves as source='edited', which
 *  `get_or_build` then returns unchanged on every later generate. */
export const saveClaimArchitecture = (
  matterId: string, artifact: ClaimArchitecture,
): Promise<{ artifact: ClaimArchitecture; source: string; summary: any }> =>
  api.put(`/matter/${matterId}/claim_architecture`, { artifact })
    .then(r => r.data);

/** Discard overrides and re-derive from current Stage 2 / 2.5. */
export const rebuildClaimArchitecture = (
  matterId: string,
): Promise<{ artifact: ClaimArchitecture; source: string; summary: any }> =>
  api.post(`/matter/${matterId}/claim_architecture/rebuild`).then(r => r.data);

export const generateClaims = (
  matterId: string,
): Promise<Stage3GenerateResponse> =>
  api.post(`/matter/${matterId}/stage_3/generate`).then(r => r.data);

// ─── Resilient Stage 3 generation ────────────────────────────────
// Claim generation is the longest LLM call in the product —
// measured at 380–460 s in production, against a ~100 s edge
// timeout. Two things went wrong at once.
//
// First, the inline path. The origin keeps working after the edge
// gives up, finishes, and writes the claims; the browser sees a
// dead socket and reported "Claim generation failed. Check LLM
// settings." — with nineteen freshly written claims sitting in the
// database and every LLM call logged as ok. The advice was wrong,
// the diagnosis was wrong, and a drafter following it would go and
// change working model settings.
//
// Second, the async path. The backend can already run this on the
// job queue and return 202 + poll_url (use_async_claims_stage_3),
// but the client read `r.data` regardless of status — so with the
// flag on it took the queue envelope for a result, found no claims
// in it, and silently showed nothing.
//
// This handles both: it follows the job when the backend queues
// one, and when the request dies at the edge it asks whether the
// claims actually landed before it calls anything a failure. Same
// shape as generateStage2Resilient, which has covered the Stage 2
// case for a while.

export interface Stage3GenerateResult extends Stage3GenerateResponse {
  /** The request died in transit but the backend had finished; the
   * claims shown were read back rather than returned. */
  recovered_from_timeout?: boolean;
  /** The backend queued the work and this followed the job. */
  ran_as_job?: boolean;
}

/** The claim ids a matter currently holds, or null if they could not
 * be read. Null matters: generation DELETEs every claim and inserts
 * new rows with new ids, so a changed id set is what proves a run
 * finished. Without a before-snapshot there is nothing to compare
 * against, and claiming recovery would just be showing the drafter
 * the previous run's claims. */
async function _claimIdsOrNull(matterId: string): Promise<string[] | null> {
  try {
    const matter = await getMatter(matterId);
    const claims = (matter as any)?.claims;
    if (!Array.isArray(claims)) return null;
    return claims.map((c: any) => c?.id).filter(Boolean);
  } catch {
    return null;
  }
}

function _sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((id) => seen.has(id));
}

/** Did a generation land since `beforeIds` was taken? Returns the
 * claims when it did. A different, non-empty id set is the proof:
 * empty would mean the delete ran and the insert did not, which is
 * not a success to report. */
export async function checkClaimsSaved(
  matterId: string, beforeIds: string[] | null,
): Promise<Stage3GenerateResult | null> {
  if (beforeIds === null) return null;
  try {
    const matter = await getMatter(matterId);
    const claims = (matter as any)?.claims;
    if (!Array.isArray(claims) || claims.length === 0) return null;
    const ids = claims.map((c: any) => c?.id).filter(Boolean);
    if (_sameIds(ids, beforeIds)) return null;
    return { claims, recovered_from_timeout: true };
  } catch {
    return null;
  }
}

/**
 * Generate claims, following the job when the backend queues one and
 * recovering the result when the request dies at the edge.
 *
 * `onPhase` reports what it is waiting on so the UI can say
 * something truer than a spinner: 'queued' and 'running' come from
 * the job, 'recovering' means the request was lost and this is
 * checking whether the work landed anyway.
 */
const _JOB_SUCCEEDED = new Set(['finished', 'succeeded']);
const _JOB_FAILED = new Set([
  'failed', 'refused_all_providers', 'cancelled', 'stopped',
]);

export async function generateClaimsResilient(
  matterId: string,
  opts: {
    onPhase?: (phase: 'queued' | 'running' | 'recovering') => void;
    pollMs?: number;
    maxWaitMs?: number;
  } = {},
): Promise<Stage3GenerateResult> {
  const pollMs = opts.pollMs ?? 3000;
  // Generous: the whole point is that this call legitimately takes
  // several minutes (measured 4.5-11 min on the current model). Past
  // this the UI stops waiting, but the work carries on and "Check if
  // saved" will still find it.
  const maxWaitMs = opts.maxWaitMs ?? 1_800_000;
  const phase = opts.onPhase ?? (() => {});

  const beforeIds = await _claimIdsOrNull(matterId);

  const attempt = async (): Promise<Stage3GenerateResult> => {
    const res = await api.post(
      `/matter/${matterId}/stage_3/generate`,
      undefined,
      // 202 has to reach us rather than being thrown: it is how the
      // backend says "queued", not an error.
      { validateStatus: (s: number) => s >= 200 && s < 300 },
    );
    if (res.status !== 202) return res.data as Stage3GenerateResult;

    const jobId = (res.data as any)?.job_id;
    if (!jobId) {
      throw new Error(
        'The server queued the generation but did not say which job to '
        + 'follow. Reload the page to see whether it finished.',
      );
    }
    const deadline = Date.now() + maxWaitMs;
    phase('queued');
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, pollMs));
      // eslint-disable-next-line no-await-in-loop
      const job = await getJobStatus(jobId);
      // 'finished' is RQ's word; 'succeeded' is what the durable
      // stage_jobs row says, and the one a module job is polled by
      // (its poll id is not the RQ id, so the route answers from
      // stage_jobs). Waiting only for 'finished' never saw the end:
      // generation completed on the server and the page waited out its
      // whole budget.
      if (_JOB_SUCCEEDED.has(job.status)) break;
      if (_JOB_FAILED.has(job.status)) {
        throw new Error(job.error || 'Claim generation failed on the server.');
      }
      phase(job.status === 'running' ? 'running' : 'queued');
      if (Date.now() > deadline) {
        throw new GenerationTimeoutError(
          'Claim generation is still running on the server. It has not '
          + 'been lost — check back in a few minutes.',
          0,
        );
      }
    }
    const saved = await checkClaimsSaved(matterId, beforeIds);
    if (saved) return { ...saved, ran_as_job: true, recovered_from_timeout: false };
    // The job finished but the claims did not change. Say so plainly
    // rather than showing the previous run's claims as if they were new.
    throw new Error(
      'The generation job finished without writing any claims. Check the '
      + 'job log in Stage jobs for what it reported.',
    );
  };

  let lastStatus = 0;
  try {
    return await attempt();
  } catch (e: any) {
    if (e?.name === 'GenerationTimeoutError') throw e;
    // The app's own answer — any status, 5xx included — carries a
    // ``detail`` saying what went wrong (the model failed, the reply
    // was unreadable, the anchors were empty). Show it as it is: read
    // as a lost request it used to come out as "the connection
    // dropped", hiding the reason.
    if (_hasAppDetail(e)) throw e;
    const { timeout, status } = _isTimeoutLike(e);
    lastStatus = status;
    // A 5xx without the app's explanation came from in front of it —
    // an edge or proxy giving up on a request that legitimately runs
    // for minutes, which some edges report as a 500 — or from a crash
    // too early to explain itself. Either way, ask whether the claims
    // landed before calling it a failure.
    if (!timeout && !(status >= 500)) throw e;
  }

  // The request was lost in transit. The origin usually finished.
  phase('recovering');
  const savedAfterFirst = await checkClaimsSaved(matterId, beforeIds);
  if (savedAfterFirst) return savedAfterFirst;

  throw new GenerationTimeoutError(
    lastStatus === 500
      ? 'The server answered HTTP 500 without saying why. That is usually '
        + 'the request being cut off in front of the server while the '
        + 'claims were still being written — check again in a minute or '
        + 'two. If nothing appears, the server log has the reason.'
      : 'The connection dropped while the claims were being generated. The '
        + 'server may still be working — this does not mean it failed.',
    lastStatus || 524,
  );
}


export const updateClaim = (claimId: string, data: { claim_text?: string; analysis?: any }) =>
  api.put(`/claim/${claimId}`, data).then(r => r.data);

export const regenerateClaim = (claimId: string) =>
  api.post(`/claim/${claimId}/regenerate`).then(r => r.data);

export const acceptRegeneration = (claimId: string, data: { claim_text: string; analysis: any }) =>
  api.post(`/claim/${claimId}/accept_regeneration`, data).then(r => r.data);

export const restoreClaimVersion = (claimId: string, versionIndex: number) =>
  api.post(`/claim/${claimId}/restore/${versionIndex}`).then(r => r.data);

export const adjustScope = (claimId: string, direction: string) =>
  api.post(`/claim/${claimId}/scope`, { direction }).then(r => r.data);

// ─── Stage 3 Steer ──────────────────────────────────────────────
// Free-text, dependency-aware claim editing — see
// docs/STAGE3_STEER_DESIGN.md. Two endpoints: a preview that does no
// DB writes and returns the proposed modified text + per-dependent
// suggestions; and an atomic-commit accept that applies the primary
// claim + checked dependent updates inside a single transaction.

export interface SteerAffectedDependent {
  id: string;
  claim_number: number;
  claim_type: string;
  category: string;
  original_text: string;
  suggested_text: string;
  /** A frozen dependent is shown but cannot be updated until unfrozen. */
  is_frozen?: boolean;
  /** Why it cannot be updated (set by the server when is_frozen). */
  cannot_update_reason?: string;
}

export interface SteerPreview {
  claim_id: string;
  claim_number: number;
  original: string;
  modified: string;
  changes_summary: string;
  affected_dependents: SteerAffectedDependent[];
}

export const steerClaim = (claimId: string, instruction: string): Promise<SteerPreview> =>
  api.post(`/claim/${claimId}/steer`, { instruction }).then(r => r.data);

export interface SteerAcceptBody {
  modified_text: string;
  modified_analysis?: Record<string, unknown>;
  accepted_dependent_updates: { claim_id: string; modified_text: string }[];
}

export interface SteerAcceptResponse {
  claims: Claim[];
  primary_claim_id: string;
  dependents_updated_count: number;
}

export const acceptSteer = (
  claimId: string, body: SteerAcceptBody,
): Promise<SteerAcceptResponse> =>
  api.post(`/claim/${claimId}/steer/accept`, body).then(r => r.data);

export const freezeClaim = (claimId: string) =>
  api.post(`/claim/${claimId}/freeze`).then(r => r.data);

// Explicit, unidirectional per-claim unfreeze. Like
// unfreezeStage2Field above, this is the spec's "Unfreeze button"
// counterpart to the legacy toggle-shaped freezeClaim. Backend
// guards on matter_id so a claim can't be unfrozen across matter
// boundaries; idempotent for already-unfrozen claims.
/**
 * Re-parse every claim's text on a matter and rewrite
 * depends_on_ids + parent_claim_number. Manual escape hatch for
 * the rare case the post-generate relink missed something —
 * e.g. a partial-JSON recovery left claims without their
 * dependency phrasing intact. Idempotent server-side.
 *
 * Response: { total, relinked, newly_linked }.
 *   relinked     — count of dependents currently linked to a parent
 *   newly_linked — count of claims that picked up a parent on this
 *                  call specifically (was empty, now non-empty)
 */
/** One entry of /claims/relink → unresolvable. Surfaces a claim
 *  whose text references a claim number that doesn't exist on the
 *  matter, so the drafter needs to edit the prose (not click
 *  Re-link again — the parse is deterministic and won't recover
 *  what's structurally missing). */
export interface RelinkUnresolvable {
  claim_id: string;
  claim_number: number;
  parent_claim_number: number | null;
  reason: string;
  claim_text_excerpt: string;
}

export interface RelinkClaimsResp {
  total: number;
  relinked: number;
  newly_linked: number;
  unresolvable: RelinkUnresolvable[];
}
export const relinkClaims = (matterId: string): Promise<RelinkClaimsResp> =>
  api.post(`/matter/${matterId}/claims/relink`).then(r => r.data);

export const unfreezeClaim = (matterId: string, claimId: string) =>
  api.post(`/matter/${matterId}/claims/${claimId}/unfreeze`).then(r => r.data);

/**
 * Delete a single claim.
 *
 * Without ``confirm``: the backend returns 409 if the claim has
 * dependents, and the response body carries
 * ``error: "has_dependents"`` plus a structured list of dependent
 * claim IDs / numbers. The caller is expected to show a confirmation
 * dialog and re-call with ``confirm=true``.
 *
 * With ``confirm=true``: the backend strips each dependent's
 * "The X of claim N, wherein …" preamble, promotes the dependents to
 * independent, deletes the target claim, then renumbers the remaining
 * claim set 1..N and rewrites every in-prose "claim N" reference to
 * the post-renumber number. Dependents are NOT deleted — they survive
 * as new independent claims.
 *
 * Frozen-dependent edge case: if any dependent is frozen, the backend
 * returns 409 with ``error: "frozen_dependents"`` regardless of the
 * confirm flag — frozen claim text can't be mutated.
 */
export const deleteClaim = (claimId: string, confirm = false) =>
  api.delete(
    `/claim/${claimId}${confirm ? '?confirm=true' : ''}`,
  ).then(r => r.data);

/**
 * BUG-018 B4 — dry-run the cascade. Returns the same shape the
 * DELETE endpoint's 409 body carries, without mutating anything.
 * The frontend calls this BEFORE firing the real DELETE so the
 * confirmation modal can list every claim the cascade will sweep.
 * Returns dependent_count=0 + dependents=[] for leaf claims; the
 * UI skips the modal in that case.
 */
export interface PreviewDeleteClaim {
  claim_id: string;
  claim_number: number;
  claim_type: 'independent' | 'dependent';
  category: string;
  is_frozen: boolean;
  dependent_count: number;
  dependents: Array<{
    id: string;
    claim_number: number;
    claim_type: 'independent' | 'dependent';
    category: string;
    is_frozen: boolean;
  }>;
  /** Surviving claims whose reference to the deleted claim is removed
   *  ("claim 1 or claim 2" → "claim 1"). Absent on older servers. */
  amended?: Array<{
    id: string;
    claim_number: number;
    claim_type: string;
    new_text: string;
    dangling: number[];
  }>;
  frozen_dependents: Array<{
    id: string;
    claim_number: number;
    claim_type: string;
  }>;
}

export const previewDeleteClaim = (claimId: string): Promise<PreviewDeleteClaim> =>
  api.get(`/claim/${claimId}/preview-delete`).then(r => r.data);

/**
 * Apply an inline QC fix to a single claim. Bypasses the Stage 3 freeze
 * gate — used by Stage 4's per-finding Fix modal so the drafter can
 * iterate QC → fix → re-run QC without walking back through Stage 3.
 * Re-freezes the claim if it was frozen on entry (unless the fix
 * introduces a new SPECIFY placeholder, in which case it stays open).
 */
export const fixClaimInline = (
  claimId: string,
  body: { claim_text: string; analysis?: any },
) =>
  api.post(`/claim/${claimId}/fix`, body).then(r => r.data);

export const addClaim = (matterId: string, body: {
  kind: 'independent' | 'dependent';
  category: string;
  parent_claim_number: number | null;
  instruction: string;
}) =>
  api.post(`/matter/${matterId}/claim/add`, body).then(r => r.data);

/**
 * Bulk-freeze every unfrozen claim that has no remaining SPECIFY
 * placeholders. Returns { frozen, skipped_with_placeholders }. Does NOT
 * advance to Stage 4 — the drafter still has to click Continue to QC.
 */
export const freezeAllClaims = (matterId: string) =>
  api.post(`/matter/${matterId}/stage_3/freeze_all_claims`).then(r => r.data);

/** /stage_3/freeze response. ep_multi_dep_warnings is populated
 *  only when the matter's jurisdiction is EP/GB/UPC AND one or
 *  more multi-dependent claims reference parents of mixed
 *  categories (EPC Rule 43(2) advisory). Non-blocking — freeze
 *  succeeds regardless; the field is a drafter-side review hint. */
export interface FreezeStage3Resp {
  status: string;
  next_stage: string;
  ep_multi_dep_warnings?: string[];
}
export const freezeStage3 = (matterId: string): Promise<FreezeStage3Resp> =>
  api.post(`/matter/${matterId}/stage_3/freeze`).then(r => r.data);

export const unfreezeStage3 = (matterId: string) =>
  api.post(`/matter/${matterId}/stage_3/unfreeze`).then(r => r.data);

// ─── Stage 4 ─────────────────────────────────────────────────────
export interface QcResult {
  findings: any[];
  reviewed_ids: string[];
  /** Deterministic checks that could not run on this matter's input. */
  checks_skipped?: import('@/types').SkippedCheck[];
  // New transparency fields — present on responses from the updated
  // backend; older servers simply omit them, hence the optionality.
  parse_status?: 'ok' | 'failed';
  reason?: string | null;
  raw_preview?: string | null;
  attempt_number?: number;
  model_used?: string;
  /** BUG-S4-001 — derived 0-100 QC health score. Computed
   *  server-side from severity-weighted finding counts (see
   *  server.py:_compute_qc_score). Optional for back-compat
   *  with older backends that omit the field; the FE always
   *  reads with a null fallback so missing values never
   *  render as "undefined". */
  score?: number;
}

// ─── Run QC, surviving the edge timeout ─────────────────────────
// QC can run past the ~100 s an edge proxy allows a request. The
// server carries on and stores the result (stage_3.content, stamped
// qc_run_at), but the page showed "The server took too long to answer"
// and the drafter ran it again (Arthur, 3 Nov 2026). Like Stage 3
// generation, this follows the job when the backend queues one and,
// when the request dies in transit, waits for the stored result.

export interface QcRunResult extends QcResult {
  /** The request was lost but the server finished; the result shown
   *  was read back from the matter rather than returned. */
  recovered_from_timeout?: boolean;
}

export type QcRunPhase = 'queued' | 'running' | 'recovering';

/** The stage_3 content a matter stores, or undefined if unreadable. */
async function _stage3ContentOrUndefined(matterId: string): Promise<any | undefined> {
  try {
    const matter = await getMatter(matterId);
    const content = (matter as any)?.stages?.stage_3?.content;
    return content && typeof content === 'object' ? content : {};
  } catch {
    return undefined;
  }
}

/** The stored QC result when it is newer than `before`, else null. */
function _storedQcResult(content: any, before: string | null): QcRunResult | null {
  const runAt = content?.qc_run_at;
  if (typeof runAt !== 'string' || !runAt || runAt === before) return null;
  return {
    findings: Array.isArray(content.qc_findings) ? content.qc_findings : [],
    checks_skipped: Array.isArray(content.qc_checks_skipped) ? content.qc_checks_skipped : [],
    reviewed_ids: [],
    parse_status: 'ok',
    score: typeof content.qc_score === 'number' ? content.qc_score : undefined,
    recovered_from_timeout: true,
  } as QcRunResult;
}

async function _runQcResilient(
  matterId: string,
  url: string,
  opts: { onPhase?: (phase: QcRunPhase) => void; pollMs?: number; maxWaitMs?: number } = {},
): Promise<QcRunResult> {
  const pollMs = opts.pollMs ?? 3000;
  // The server keeps going after the edge gives up; QC has taken a few
  // minutes on a slow model. Past this the page stops waiting.
  const maxWaitMs = opts.maxWaitMs ?? 600_000;
  const phase = opts.onPhase ?? (() => {});
  const beforeContent = await _stage3ContentOrUndefined(matterId);
  // Without a before-stamp a stored result cannot be told from the
  // previous run's, so there is nothing safe to recover.
  const before: string | null | undefined = beforeContent === undefined
    ? undefined
    : (typeof beforeContent.qc_run_at === 'string' ? beforeContent.qc_run_at : null);

  const waitForStored = async (): Promise<QcRunResult | null> => {
    if (before === undefined) return null;
    const deadline = Date.now() + maxWaitMs;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const content = await _stage3ContentOrUndefined(matterId);
      const stored = content === undefined ? null : _storedQcResult(content, before);
      if (stored) return stored;
      if (Date.now() > deadline) return null;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, pollMs));
    }
  };

  try {
    const res = await api.post(
      url,
      undefined,
      // 202 is the backend saying "queued", not an error.
      { validateStatus: (st: number) => st >= 200 && st < 300 },
    );
    if (res.status !== 202) return res.data as QcRunResult;
    const jobId = (res.data as any)?.job_id;
    if (jobId) {
      const deadline = Date.now() + maxWaitMs;
      phase('queued');
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, pollMs));
        // eslint-disable-next-line no-await-in-loop
        const job = await getJobStatus(jobId);
        if (_JOB_SUCCEEDED.has(job.status)) break;
        if (_JOB_FAILED.has(job.status)) {
          throw new Error(job.error || 'QC failed on the server.');
        }
        phase(job.status === 'running' ? 'running' : 'queued');
        if (Date.now() > deadline) break;
      }
    }
    const stored = await waitForStored();
    if (stored) return { ...stored, recovered_from_timeout: false };
    throw new GenerationTimeoutError(
      'QC is still running on the server. It has not been lost — reload '
      + 'the page in a minute or two to see the result.',
      0,
    );
  } catch (e: any) {
    if (e?.name === 'GenerationTimeoutError') throw e;
    // The app's own answer (a gate, a missing input) says what is wrong.
    if (_hasAppDetail(e)) throw e;
    const { timeout, status } = _isTimeoutLike(e);
    if (!timeout && !(status >= 500)) throw e;
    phase('recovering');
    const stored = await waitForStored();
    if (stored) return stored;
    throw new GenerationTimeoutError(
      'The connection dropped while QC was running. The server may still '
      + 'be working — reload the page in a minute or two to see the result '
      + 'before running QC again.',
      status || 524,
    );
  }
}

export const runQc = (
  matterId: string,
  opts?: { onPhase?: (phase: QcRunPhase) => void; pollMs?: number; maxWaitMs?: number },
): Promise<QcRunResult> => _runQcResilient(matterId, `/matter/${matterId}/stage_4/qc`, opts);

export const retryQc = (
  matterId: string,
  opts?: { onPhase?: (phase: QcRunPhase) => void; pollMs?: number; maxWaitMs?: number },
): Promise<QcRunResult> => _runQcResilient(matterId, `/matter/${matterId}/stage_4/qc/retry`, opts);

export interface QcLogRow {
  id: number;
  matter_id: string;
  parse_status: 'ok' | 'failed';
  reason: string | null;
  raw_response_preview: string | null;
  model_used: string | null;
  findings_count: number;
  attempt_number: number;
  actor_user_id: number | null;
  created_at: string;
}

export const diagListQcLog = (params: { matter_id?: string; limit?: number } = {}) => {
  const q: Record<string, string | number> = {};
  if (params.matter_id) q.matter_id = params.matter_id;
  if (params.limit) q.limit = params.limit;
  return api.get('/admin/diagnostic/qc-log', { params: q })
    .then(r => r.data as { rows: QcLogRow[]; summary: { total: number; failed: number }; limit: number });
};

// ─── Diagnostic: matter export + session replay ─────────────────
export const diagExportMatter = async (matterId: string): Promise<{
  filename: string; blob: Blob;
}> => {
  const resp = await api.get(`/admin/diagnostic/matters/${matterId}/export`, {
    responseType: 'blob',
  });
  const dispo = (resp.headers['content-disposition'] || '').toString();
  const m = /filename="?([^"]+)"?/i.exec(dispo);
  const filename = m ? m[1] : `matter_${matterId}.json`;
  return { filename, blob: resp.data as Blob };
};

// Bulk variant — every matter in the admin's workspace zipped into
// one archive with a ``_manifest.json`` index. URL sits OUTSIDE the
// /matters/ namespace to avoid colliding with the path-parameterised
// /matters/{matter_id} route.
export const diagExportAllMatters = async (): Promise<{
  filename: string; blob: Blob;
}> => {
  const resp = await api.get('/admin/diagnostic/export-all-matters', {
    responseType: 'blob',
  });
  const dispo = (resp.headers['content-disposition'] || '').toString();
  const m = /filename="?([^"]+)"?/i.exec(dispo);
  const filename = m ? m[1] : 'matters_export.zip';
  return { filename, blob: resp.data as Blob };
};

export interface UserSessionRow {
  session_id: string;
  user_id: number | null;
  email: string | null;
  workspace_id: number | null;
  role: string | null;
  started_at: string;
  last_active_at: string;
  ended_at: string | null;
  total_duration_ms: number | null;
  event_count: number;
  matters_touched: string[];
}

export const diagListSessions = (params: { limit?: number; user_id?: number } = {}) => {
  const q: Record<string, string | number> = {};
  if (params.limit) q.limit = params.limit;
  if (params.user_id) q.user_id = params.user_id;
  return api.get('/admin/diagnostic/sessions', { params: q })
    .then(r => r.data as { rows: UserSessionRow[]; limit: number });
};

export interface UserEventRow {
  id: number;
  sequence_number: number;
  event_type: string;
  event_category: 'navigation' | 'interaction' | 'llm' | 'system';
  element_id: string | null;
  page: string | null;
  matter_id: string | null;
  event_data: Record<string, any> | null;
  duration_ms: number | null;
  cumulative_ms: number | null;
  created_at: string;
}

export interface SessionEventsResponse {
  session: UserSessionRow & { device_info?: Record<string, any> };
  events: UserEventRow[];
  summary: {
    total_events: number;
    llm_calls: number;
    avg_llm_duration_ms: number;
    regenerations: number;
    manual_edits: number;
    qa_submits: number;
    long_pauses: Array<{ sequence_number: number; gap_ms: number }>;
  };
}

export const diagSessionEvents = (sessionId: string): Promise<SessionEventsResponse> =>
  api.get(`/admin/diagnostic/sessions/${sessionId}/events`).then(r => r.data);

// ─── Client-side behaviour events ───────────────────────────────
export interface ClientEventPayload {
  event_type: string;
  event_category: 'navigation' | 'interaction' | 'llm' | 'system';
  matter_id?: string;
  element_id?: string;
  event_data?: Record<string, any>;
  duration_ms?: number;
}

export const postEvent = (payload: ClientEventPayload) =>
  // Fire-and-forget: logging failure must never surface to the user.
  api.post('/events', payload).catch(() => undefined);

export const updateReviewedFindings = (matterId: string, reviewedIds: string[]) =>
  api.put(`/matter/${matterId}/stage_4/reviewed`, { reviewed_ids: reviewedIds }).then(r => r.data);

/**
 * Persist the drafter's per-finding notes. Object keyed by client-computed
 * findingId (see src/lib/qcFindings.ts). Empty-string values are stripped
 * server-side so the store stays tidy.
 */
export const updateFindingNotes = (matterId: string, notes: Record<string, string>) =>
  api.put(`/matter/${matterId}/stage_4/finding_notes`, { notes }).then(r => r.data);

export const exportDocxUrl = (matterId: string) =>
  `${BACKEND_URL}/api/matter/${matterId}/export/docx`;

export const exportJsonUrl = (matterId: string) =>
  `${BACKEND_URL}/api/matter/${matterId}/export/json`;

/**
 * Robust docx download via blob. An <a target="_blank"> link is popup-blocked
 * by some browsers and produces an empty tab; fetching as a blob and triggering
 * a synthetic click on a hidden anchor works reliably across Chrome/Safari/FF.
 */
async function _downloadDocxBlob(
  url: string, fallbackFilename: string,
): Promise<void> {
  const res = await api.get(url, { responseType: 'blob' });
  // Derive filename from Content-Disposition (RFC 5987 filename*) with a safe fallback.
  const disp = (res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || '') as string;
  let filename = fallbackFilename;
  const starMatch = /filename\*=UTF-8''([^;\n]+)/i.exec(disp);
  const plainMatch = /filename="?([^";\n]+)"?/i.exec(disp);
  if (starMatch?.[1]) {
    try { filename = decodeURIComponent(starMatch[1]); } catch { /* keep fallback */ }
  } else if (plainMatch?.[1]) {
    filename = plainMatch[1];
  }
  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a tick to start the download before we revoke the URL.
  setTimeout(() => URL.revokeObjectURL(href), 1500);
}

export async function downloadDocxExport(matterId: string, fallbackTitle = 'claims') {
  const safe = fallbackTitle.replace(/[^A-Za-z0-9._-]+/g, '_');
  await _downloadDocxBlob(
    `/matter/${matterId}/export/docx`,
    `${safe}_claims.docx`,
  );
}

export async function downloadClaimsOnlyDocx(matterId: string, fallbackTitle = 'claims') {
  const safe = fallbackTitle.replace(/[^A-Za-z0-9._-]+/g, '_');
  await _downloadDocxBlob(
    `/matter/${matterId}/export/docx/claims-only`,
    `${safe}_claims_only.docx`,
  );
}

export async function downloadJsonExport(matterId: string, fallbackTitle = 'claims') {
  const res = await api.get(`/matter/${matterId}/export/json`);
  const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fallbackTitle.replace(/[^A-Za-z0-9._-]+/g, '_')}_claims.json`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// ─── Health ──────────────────────────────────────────────────────
export const getLlmHealth = () =>
  api.get('/health/llm').then(r => r.data);

// ─── Prompt versioning admin ─────────────────────────────────────
export const activatePromptVersion = (promptId: string, version: number) =>
  api.post(`/prompts/${promptId}/activate`, { version }).then(r => r.data);

export const testPrompt = (promptId: string, variables: Record<string, any>, opts?: { jurisdiction?: string; tier?: 'fast' | 'capable'; json_mode?: boolean }) =>
  api.post(`/prompts/${promptId}/test`, { variables, ...(opts || {}) }).then(r => r.data);

export const exportPromptLibrary = () =>
  api.get('/prompts/export').then(r => r.data);

export const importPromptLibrary = (payload: any, activate = false) =>
  api.post('/prompts/import', { payload, activate }).then(r => r.data);

// ─── Settings ────────────────────────────────────────────────────
export const getSettings = () =>
  api.get('/settings').then(r => r.data);

export const updateSettings = (settings: Record<string, string>) =>
  api.put('/settings', { settings }).then(r => r.data);

// ─── Prompts ─────────────────────────────────────────────────────
export const getPrompts = () =>
  api.get('/prompts').then(r => r.data);

export interface PromptValidationIssue {
  type: 'placeholder' | 'section' | 'json_instruction';
  severity?: 'required' | 'optional';
  item: string;
  message: string;
}

export interface PromptValidationResult {
  valid: boolean;
  blocking: PromptValidationIssue[];
  warnings: PromptValidationIssue[];
  unknown: PromptValidationIssue[];
  expects_json: boolean;
  protection: {
    required_placeholders: string[];
    optional_placeholders: string[];
    required_sections: string[];
    optional_sections: string[];
    required_json_instructions: string[];
  };
}

export const validatePromptContent = (
  promptId: string, template: string,
): Promise<PromptValidationResult> =>
  api.post(`/prompts/${promptId}/validate`, { template }).then(r => r.data);

export const updatePrompt = (
  promptId: string,
  template: string,
  notes?: string,
  system_message?: string,
) =>
  api.put(`/prompts/${promptId}`, {
    template,
    notes,
    // Only send system_message when the caller passed one — ``undefined``
    // leaves the stored value untouched server-side. Empty string is a
    // legitimate "clear" request and is forwarded as-is.
    ...(system_message !== undefined ? { system_message } : {}),
  }).then(r => r.data);

export const restorePromptDefault = (promptId: string) =>
  api.post(`/prompts/${promptId}/restore`).then(r => r.data);

export interface PromptHistoryEntry {
  edited_by: string | null;
  edited_at: string;
  previous_content: string;
  validation_at_save: {
    warnings?: PromptValidationIssue[];
    unknown?: PromptValidationIssue[];
    restored?: boolean;
  };
}

export const getPromptHistory = (promptId: string): Promise<{ history: PromptHistoryEntry[] }> =>
  api.get(`/prompts/${promptId}/history`).then(r => r.data);

// ─── Runtime system configuration ────────────────────────────────
// Admin-only: shape matches backend/config_db.list_all. Sensitive
// values arrive masked ('••••••'); send a fresh value via PATCH to
// overwrite. "source" tells the UI whether the current value came
// from the DB, .env, or the hardcoded default.
export interface SystemConfigRow {
  key: string;
  label: string;
  description: string;
  category: string;
  type: 'string' | 'integer' | 'boolean' | 'secret';
  is_sensitive: boolean;
  /** A stored secret the server's encryption key can't decrypt. Shown
   * as "unreadable — re-enter"; a blank save leaves it untouched. */
  unreadable?: boolean;
  value: string;
  default_value: string;
  updated_by: number | null;
  updated_at: string | null;
  source: 'db' | 'env' | 'default';
}

export const getSystemConfig = (): Promise<{
  categories: Record<string, SystemConfigRow[]>;
  total_keys: number;
}> => api.get('/admin/config').then((r) => r.data);

export const patchSystemConfig = (key: string, value: string) =>
  api.patch(`/admin/config/${key}`, { value }).then((r) => r.data);

export const testModelSetting = (model_key: string) =>
  api.post('/admin/config/test-model', { model_key }).then((r) => r.data);

export interface TestModelResult {
  ok: boolean;
  latency_ms: number;
  error: string | null;
  // Echoed for display / logging; not part of the minimum contract.
  provider?: string;
  model?: string;
  sample?: string;
  status?: number | null;
}

export const testModel = (provider: string, model: string): Promise<TestModelResult> =>
  api.post('/admin/models/test', { provider, model }).then((r) => r.data);

export const testEmailSetting = () =>
  api.post('/admin/config/test-email').then((r) => r.data);

export const reloadSystemConfig = () =>
  api.post('/admin/config/reload').then((r) => r.data);

export interface ApiKeyStatus {
  anthropic:  { configured: boolean; source: 'db' | 'env' | null };
  openai:     { configured: boolean; source: 'db' | 'env' | null };
  google:     { configured: boolean; source: 'db' | 'env' | null };
  openrouter: { configured: boolean; source: 'db' | 'env' | null };
  fireworks:  { configured: boolean; source: 'db' | 'env' | null };
}

export const getApiKeyStatus = (): Promise<ApiKeyStatus> =>
  api.get('/admin/config/api-keys/status').then((r) => r.data);

// ─── Skill Studio (admin) ────────────────────────────────────────
/** Shape returned by GET/PUT/reset on /api/admin/skill/document. */
export interface SkillDocResponse {
  exists: boolean;
  doc_name: string;
  content: string | null;
  version_hash: string | null;
  word_count: number;
  indexed: boolean;
  indexed_at: string | null;
  pageindex_node_count: number;
  /** True when content has been re-saved since the last reindex. */
  indexed_is_stale: boolean;
  content_hash_at_index: string | null;
  sections_detected: {
    part1: boolean;
    T1_SOFTWARE: boolean;
    T2_MEDTECH: boolean;
    T3_MECHANICAL: boolean;
    T4_CHEMISTRY: boolean;
    T5_ELECTRONICS: boolean;
  };
  skill_guidance_enabled: boolean;
}

export interface SkillReindexResponse {
  status: 'ok' | 'error' | 'not_loaded';
  node_count?: number;
  indexed_at?: string;
  message?: string;
}

export interface SkillSyncPromptsResponse {
  synced: string[];
  skipped: string[];
  not_in_seed: string[];
}

export const getSkillDocument = (): Promise<SkillDocResponse> =>
  api.get('/admin/skill/document').then((r) => r.data);

export const putSkillDocument = (content: string): Promise<SkillDocResponse> =>
  api.put('/admin/skill/document', { content }).then((r) => r.data);

export const reindexSkillDocument = (): Promise<SkillReindexResponse> =>
  api.post('/admin/skill/reindex').then((r) => r.data);

export const syncSkillPrompts = (): Promise<SkillSyncPromptsResponse> =>
  api.post('/admin/skill/sync-prompts').then((r) => r.data);

export const setSkillEnabled = (enabled: boolean): Promise<{ enabled: boolean }> =>
  api.patch('/admin/skill/enabled', { enabled }).then((r) => r.data);

export const resetSkillDocument = (): Promise<SkillDocResponse> =>
  api.post('/admin/skill/reset').then((r) => r.data);

// ─── Phase 14 Skill Studio Assistant ──────────────────────────────

export interface SkillHeading {
  anchor:        string;
  level:         number;
  heading_text:  string;
  line_number:   number;
  line_offset:   number;
}

export interface SkillSectionNode extends SkillHeading {
  children: SkillSectionNode[];
}

export interface SkillSectionsResponse {
  flat:  SkillHeading[];
  tree:  SkillSectionNode[];
  total: number;
}

/** Phase 14A — pure-deterministic parse of the current skill doc.
 * Used by the Assistant tab's "Place in" dropdown (Phase 14C).
 * No LLM call; admin-only. */
export const getSkillSections = (): Promise<SkillSectionsResponse> =>
  api.get('/admin/skill/sections').then((r) => r.data);

export interface SkillAssistRequest {
  user_request: string;
  mode:         'add' | 'modify' | 'find';
}

export interface SkillSuggestion {
  intent:           'add' | 'modify' | 'find' | 'question';
  section_anchor:   string | null;
  section_heading:  string | null;
  is_new_section:   boolean;
  insert_after:     string | null;
  content:          string;
  why:              string;
  found_anchor?:    string | null;
  found_excerpt?:   string | null;
}

export interface SkillAssistResponse {
  ok: boolean;
  suggestion?: SkillSuggestion;
  error_code?: string;
  raw?: string;
}

/** Phase 14B — LLM-backed assistant call. No document write.
 * Returns a structured suggestion the operator reviews + edits
 * before clicking Apply. */
export const postSkillAssist =
  (body: SkillAssistRequest): Promise<SkillAssistResponse> =>
    api.post('/admin/skill/assist', body).then((r) => r.data);

export interface SkillApplyRequest {
  insert_after_anchor:   string | null;
  content:               string;
  is_new_section:        boolean;
  new_section_heading:   string | null;
}

export interface SkillApplyResponse {
  ok:                boolean;
  version_hash:      string;
  applied_anchor:    string;
  applied_line:      number | null;
  chars_added:       number;
  reindex_required:  boolean;
}

/** Phase 14B — deterministic insert with integrity check. Mutates
 * the skill_documents row + clears pageindex_tree (re-index
 * required after every apply). Surfaces 422 with structured
 * error_code='integrity_check_failed' + issue array on
 * pre-flight failure. */
export const postSkillApply =
  (body: SkillApplyRequest): Promise<SkillApplyResponse> =>
    api.post('/admin/skill/apply', body).then((r) => r.data);

/** Phase 13B-second Chunk 2 — hard-reset every prompt to the seed
 * v3 default. Requires confirm=true; preserves edit_history via a
 * marker entry per row. Used by the PromptAdmin "Reset all" header
 * button. Returns counts so the toast can report "Reset N prompts;
 * M skipped (admin-added rows)." */
export interface PromptsResetAllResponse {
  ok: boolean;
  prompts_reset: number;
  skipped: string[];
  history_preserved: boolean;
}

export const resetAllPromptsToSeed =
  (): Promise<PromptsResetAllResponse> =>
    api
      .post('/admin/prompts/reset-all', { confirm: true })
      .then((r) => r.data);

// ─── User management (admin only) ────────────────────────────────
export interface WorkspaceUser {
  id: number;
  email: string;
  role: 'admin' | 'drafter' | 'reviewer';
  is_active: boolean;
  active: boolean;
  workspace_id: number | null;
  last_login: string | null;
  created_at: string;
  // Draft-adoption metric — matters owned by this user and the
  // timestamp of the most recent one. See GET /users.
  drafts_count: number;
  last_draft_at: string | null;
}

export const listUsers = (): Promise<WorkspaceUser[]> =>
  api.get('/users').then((r) => r.data);

export const getUser = (id: number): Promise<WorkspaceUser> =>
  api.get(`/users/${id}`).then((r) => r.data);

export const createUser = (email: string, role: string): Promise<WorkspaceUser> =>
  api.post('/users', { email, role }).then((r) => r.data);

export const patchUser = (
  id: number,
  body: { role?: string; is_active?: boolean },
): Promise<WorkspaceUser> =>
  api.patch(`/users/${id}`, body).then((r) => r.data);

export const deactivateUser = (id: number) =>
  api.delete(`/users/${id}`).then((r) => r.data);

// ─── Admin diagnostic page ───────────────────────────────────────
export const diagOverview = () =>
  api.get('/admin/diagnostic/overview').then((r) => r.data);

export const diagMatters = () =>
  api.get('/admin/diagnostic/matters').then((r) => r.data);

export const diagMatterDetail = (matterId: string) =>
  api.get(`/admin/diagnostic/matters/${matterId}`).then((r) => r.data);

export const diagDocumentTree = (matterId: string, documentId: number) =>
  api.get(`/admin/diagnostic/matters/${matterId}/tree/${documentId}`).then((r) => r.data);

export interface LlmCallRow {
  id: number;
  prompt_key: string | null;
  stage: string | null;
  model: string;
  tier: string | null;
  system_message: string | null;
  final_prompt: string;
  placeholders_used: Record<string, number>;
  blank_placeholders: string[];
  response_text: string | null;
  prompt_tokens_estimate: number | null;
  response_tokens_estimate: number | null;
  parse_status: string;
  parse_error: string | null;
  duration_ms: number | null;
  created_at: string;
}

export interface LlmCallsResponse {
  matter_id: string;
  calls: LlmCallRow[];
  summary: {
    total: number;
    avg_duration_ms: number;
    blank_incidents: number;
    parse_failures: number;
  };
}

export const diagMatterLlmCalls = (
  matterId: string,
  opts?: {
    blank_only?: boolean;
    stage?: string;
    parse_status?: string;
    /** Cue: extends ``stage`` for spec/drawings calls. The
     *  prompts table stores ``spec_*`` / ``drawing_*`` keys
     *  without a stage column entry, so the FE filter chip
     *  routes through this prefix instead. */
    prompt_prefix?: string;
    limit?: number;
  },
): Promise<LlmCallsResponse> => {
  const params: Record<string, any> = {};
  if (opts?.blank_only) params.blank_only = 'true';
  if (opts?.stage) params.stage = opts.stage;
  if (opts?.parse_status) params.parse_status = opts.parse_status;
  if (opts?.prompt_prefix) params.prompt_prefix = opts.prompt_prefix;
  if (opts?.limit) params.limit = opts.limit;
  return api.get(`/admin/diagnostic/matters/${matterId}/llm_calls`, { params })
    .then((r) => r.data);
};


// ─── Spec sections + figures diagnostic surface (admin tabs) ─────

export interface SpecSectionSummary {
  id: string;
  section_kind: string;
  sequence_num: number;
  title: string;
  statute_ref: string | null;
  is_auto_generated: boolean;
  word_count: number;
  is_frozen: boolean;
  frozen_at: string | null;
  is_excluded: boolean;
  paragraph_count: number;
  last_generated_at: string | null;
  last_generated_prompt_key: string | null;
  created_at: string;
  updated_at: string;
}

export interface SpecParagraphRow {
  id: string;
  paragraph_number: string;
  sequence_num: number;
  body_text: string;
  body_html: string;
  word_count: number;
  state: string;
  last_generated_by_llm: boolean;
  last_generated_prompt_key: string | null;
  last_generated_at: string | null;
}

export interface RelatedLlmCallRow {
  id: number;
  prompt_key: string | null;
  model: string | null;
  tier: string | null;
  parse_status: string | null;
  parse_error: string | null;
  duration_ms: number | null;
  created_at: string;
}

export interface SpecSectionDetailResponse {
  matter_id: string;
  section: {
    id: string;
    section_kind: string;
    sequence_num: number;
    title: string;
    statute_ref: string | null;
    is_auto_generated: boolean;
    word_count: number;
    is_frozen: boolean;
    frozen_at: string | null;
    is_excluded: boolean;
  };
  paragraphs: SpecParagraphRow[];
  related_llm_calls: RelatedLlmCallRow[];
}

export interface FigureSummary {
  id: string;
  fig_label: string;
  sequence_num: number;
  origin: string | null;
  generator: string | null;
  is_vectorized: boolean;
  is_frozen: boolean;
  frozen_at: string | null;
  raster_path: string | null;
  svg_path: string | null;
  has_analyze: boolean;
  has_claim_prime: boolean;
  imported_description: string | null;
  numeral_count: number;
  created_at: string;
  updated_at: string;
}

export interface FigureDetailResponse {
  matter_id: string;
  figure: {
    id: string;
    fig_label: string;
    sequence_num: number;
    origin: string | null;
    generator: string | null;
    is_vectorized: boolean;
    is_frozen: boolean;
    frozen_at: string | null;
    raster_path: string | null;
    svg_path: string | null;
    imported_description: string | null;
    drafters_note: string | null;
    analyze_bundle_json: any;
    claim_prime_json: any;
    source_snapshot_json: any;
  };
  canvas_counts: Record<string, number>;
  related_llm_calls: RelatedLlmCallRow[];
  stub_prompt_keys: string[];
}

export const diagMatterSpecSections = (matterId: string) =>
  api.get<{ matter_id: string; sections: SpecSectionSummary[] }>(
    `/admin/diagnostic/matters/${matterId}/spec_sections`,
  ).then((r) => r.data);

export const diagMatterSpecSectionDetail = (
  matterId: string, sectionKind: string,
) =>
  api.get<SpecSectionDetailResponse>(
    `/admin/diagnostic/matters/${matterId}/spec_sections/${sectionKind}`,
  ).then((r) => r.data);

export const diagMatterFigures = (matterId: string) =>
  api.get<{ matter_id: string; figures: FigureSummary[]; stub_prompt_keys: string[] }>(
    `/admin/diagnostic/matters/${matterId}/figures`,
  ).then((r) => r.data);

export const diagMatterFigureDetail = (matterId: string, figureId: string) =>
  api.get<FigureDetailResponse>(
    `/admin/diagnostic/matters/${matterId}/figures/${figureId}`,
  ).then((r) => r.data);

export const diagLiveQuery = (
  matterId: string,
  body: { query: string; doc_types?: string[]; max_nodes?: number },
) => api.post(`/admin/diagnostic/matters/${matterId}/query`, body).then((r) => r.data);

export const diagTables = () =>
  api.get('/admin/diagnostic/tables').then((r) => r.data);

export const diagTableSample = (table: string, limit = 20) =>
  api.get(`/admin/diagnostic/tables/${table}/sample`, { params: { limit } }).then((r) => r.data);

export const diagReindex = (matterId: string) =>
  api.post(`/admin/diagnostic/matters/${matterId}/reindex`).then((r) => r.data);

// ─── Admin database cleanup ─────────────────────────────────────
// Two-step surface (preview → execute) on the admin Diagnostic page.
// Preview is safe to call any time; execute mutates the DB and is
// gated behind a confirm() in the UI.
export interface CleanupCategory {
  count: number;
  description: string;
  /** Per-table breakdown for orphaned_child_rows. */
  tables?: Record<string, number>;
  /** Estimated bytes for old_llm_call_log. */
  estimated_bytes?: number;
  /** Per-source breakdown for stale_generation_job_blobs. */
  drawing_jobs?: number;
  spec_jobs?: number;
}
export interface CleanupReport {
  total_rows_to_delete: number;
  total_blobs_to_clear: number;
  estimated_size_bytes: number;
  categories: Record<string, CleanupCategory>;
  generated_at: string;
}
export interface CleanupExecuteResult {
  status: 'completed';
  deleted: Record<string, number>;
  preview_before: CleanupReport;
  executed_at: string;
  executed_by: string;
}
export const cleanupPreview = (): Promise<CleanupReport> =>
  api.get('/admin/cleanup/preview').then((r) => r.data);
export const cleanupExecute = (): Promise<CleanupExecuteResult> =>
  api.post('/admin/cleanup/execute').then((r) => r.data);

// ─── Drawing module (Phase 3 read endpoints + Phase 3B admin) ─────
// Locked-state contract: every matter-scoped read returns either the
// real payload or {locked: true, locked_reason: 'claims_not_frozen'}.
// Callers must branch on `locked` before consuming the rest.

export interface DrawingStateResponse {
  locked: boolean;
  locked_reason?: string;
  figures_count?: number;
  frozen_count?: number;
  unanalyzed_count?: number;
  issues_count?: number;
  active_figure_id?: string | null;
  last_activity_at?: string | null;
}

export const getDrawingState = (matterId: string): Promise<DrawingStateResponse> =>
  api.get(`/matter/${matterId}/drawing/state`).then((r) => r.data);

/** Per-figure summary returned by GET /matter/{id}/drawing/figures.
 * `claim_prime_summary` and `analyze_bundle_summary` are optional —
 * `null` until the corresponding LLM call has run for the figure. */
export interface DrawingFigureSummary {
  id: string;
  fig_label: string | null;
  sequence_num: number;
  caption: string | null;
  origin: string;
  generator: string | null;
  is_frozen: boolean;
  is_vectorized: boolean;
  is_bw_exported: boolean;
  // CHUNK 1 simplification — editable description + scratch
  // notes. Optional to match the DrawingFigureFull override
  // below (which exists because the detail endpoint can return
  // the field absent on freshly-imported rows). The single
  // consumer (FigureGallery.tsx) reads it as
  // ``figure.imported_description || ''`` so the optional
  // signature is safe at runtime.
  imported_description?: string;
  drafters_note?: string;
  claim_prime_summary: { prime_confidence?: string; unmapped_count?: number } | null;
  analyze_bundle_summary: { extracted_numerals_count?: number; missing_coverage_count?: number } | null;
  thumb_url: string;
  full_url: string;
  numerals_count: number;
  unmapped_terms_count: number;
  source_stale: boolean;
  source_stale_reason?: string | null;
  /** WISHLIST_REGENERATE_FIGURE Phase 14 — source-quality hint
   *  for the Regenerate-Image affordance. One of:
   *    'color_photo' | 'screenshot' | 'sketch' | 'clean' | 'unknown'.
   *  Default 'unknown' for figures created before the column shipped. */
  source_quality?: string;
  /** Path to archived original bytes preserved before the first
   *  regeneration accepted on this figure. NULL until the drafter
   *  has clicked Regenerate at least once. Drives the "Restore
   *  original" overflow menu item. */
  source_image_original_url?: string | null;
  /** Monotonic counter — accepted regenerations on this figure.
   *  Drives the "Regenerated (original preserved)" badge. */
  regeneration_count?: number;
  width?: number | null;
  height?: number | null;
  created_at: string;
  updated_at: string;
}

export type DrawingFiguresResponse =
  | { locked: true; locked_reason: string }
  | DrawingFigureSummary[];

export const getDrawingFigures = (matterId: string): Promise<DrawingFiguresResponse> =>
  api.get(`/matter/${matterId}/drawing/figures`).then((r) => r.data);

/** CHUNK 1 simplification — drafter-editable figure metadata.
 * Either field may be omitted to leave it unchanged; an empty
 * string clears the column. Frozen figures reject with 409
 * ``error_code='figure_frozen'``. */
export interface DrawingFigureMetadataPatch {
  imported_description?: string;
  drafters_note?: string;
}

export const patchDrawingFigure = (
  matterId: string,
  figureId: string,
  body: DrawingFigureMetadataPatch,
): Promise<{ figure: DrawingFigureSummary }> =>
  api
    .patch(`/matter/${matterId}/drawing/figures/${figureId}`, body)
    .then((r) => r.data);

/** BUG-032 — opt-in raster→SVG tracing. Runs the Phase 7B tracer
 * over the figure's existing raster, persists the SVG, sets
 * is_vectorized + svg_path on the row. Failures surface as a
 * 422 with ``error_code="trace_failed"`` (figure stays
 * raster-only, no row change); 409 when the figure is frozen. */
export const vectorizeDrawingFigure = (
  matterId: string,
  figureId: string,
): Promise<{ figure: DrawingFigureSummary; tracing_meta: Record<string, unknown> }> =>
  api
    .post(`/matter/${matterId}/drawing/figures/${figureId}/vectorize`)
    .then((r) => r.data);

/** BUG-032 — fetch the persisted SVG bytes as text so the canvas
 *  can inline it for click-select + delete editing.
 *
 *  ``cacheBust`` (chunk-3-followup-2) — when supplied, appended
 *  as a query param so each PATCH-bumped fetch gets a unique
 *  URL and bypasses the browser's HTTP cache. The GET endpoint's
 *  FileResponse otherwise serves an ETag/Last-Modified-keyed
 *  response that the browser may treat as fresh even after the
 *  underlying file changed (the URL is identical across PATCH
 *  calls). KonvaCanvas's useFigureSvg passes the per-figure
 *  ``svgRefreshByFigure`` counter; legacy callers omit the param
 *  and keep their existing semantics. */
export const getDrawingFigureSvg = (
  matterId: string,
  figureId: string,
  cacheBust?: number | string,
): Promise<string> =>
  api
    .get(`/matter/${matterId}/drawing/figures/${figureId}/svg`,
      {
        responseType: 'text',
        transformResponse: (d) => d,
        params: cacheBust !== undefined ? { _v: cacheBust } : undefined,
      })
    .then((r) => r.data as string);

/** BUG-032 — overwrite the figure's SVG after a drafter edit
 * (e.g. after deleting a stray path). The svg_path column is
 * unchanged; updated_at advances so the two-tab last-write-wins
 * detector picks up the change. */
export const patchDrawingFigureSvg = (
  matterId: string,
  figureId: string,
  svg: string,
): Promise<{ figure: DrawingFigureSummary }> =>
  api
    .patch(`/matter/${matterId}/drawing/figures/${figureId}/svg`, { svg })
    .then((r) => r.data);

/** Source figure (from matter_figures, eligible to import) returned by
 * GET /matter/{id}/drawing/source-figures. */
export interface DrawingSourceFigure {
  id: number;
  fig_label: string | null;
  source_label: string | null;
  description: string | null;
  thumb_url: string;
  full_url: string;
}

export type DrawingSourceFiguresResponse =
  | { locked: true; locked_reason: string }
  | DrawingSourceFigure[];

export const getDrawingSourceFigures = (matterId: string): Promise<DrawingSourceFiguresResponse> =>
  api.get(`/matter/${matterId}/drawing/source-figures`).then((r) => r.data);

/** Why the import picker has nothing to offer.
 *
 * The list above is a bare array and a bare array cannot say why it
 * is empty. A drafter whose disclosure plainly contained 34 drawings
 * opened the picker, read "every IDF figure has been imported (or
 * none were detected)", and reasonably concluded the feature was
 * broken — while the real cause was figure extraction failing during
 * indexing. Called only when the list comes back empty. */
export interface DrawingSourceFiguresWhyEmpty {
  reason:
    | 'no_documents' | 'still_indexing' | 'extraction_failed'
    | 'all_imported' | 'no_images_in_documents' | 'excluded';
  message: string;
  total_figures: number;
  already_imported: number;
  documents: Array<{
    doc_name: string | null;
    status: string | null;
    image_count: number;
    figures_stored: number;
    figures_error: string | null;
  }>;
}

export const getDrawingSourceFiguresWhyEmpty = (
  matterId: string,
): Promise<DrawingSourceFiguresWhyEmpty> =>
  api
    .get(`/matter/${matterId}/drawing/source-figures/why-empty`)
    .then((r) => r.data);

export interface DrawingNumeralRow {
  id: string;
  numeral: string;
  label: string;
  bound_claim_id: string | null;
  bound_claim_term: string | null;
  is_stale: boolean;
  /** Drawing figure ids the numeral is placed on (joined from drawing_canvas_state). */
  figure_ids: string[];
  created_at: string;
  updated_at: string;
}

export type DrawingNumeralsResponse =
  | { locked: true; locked_reason: string }
  | DrawingNumeralRow[];

export const getDrawingNumerals = (matterId: string): Promise<DrawingNumeralsResponse> =>
  api.get(`/matter/${matterId}/drawing/numerals`).then((r) => r.data);

export interface DrawingTermMappingRow {
  id: string;
  claim_id: string;
  claim_term: string;
  drawing_numeral_id: string | null;
  /** Phase 4 — the figure a mapping was pinned to, when the drafter
   * overrode the numeral's own figure list. Null for most rows. The
   * GET returns the full row, so this is present; the drawing store
   * filters on it when a figure is deleted. */
  figure_id_override?: string | null;
  mapping_source: string;
  confidence: string | null;
  created_at: string;
  updated_at: string;
}

export type DrawingTermMappingsResponse =
  | { locked: true; locked_reason: string }
  | DrawingTermMappingRow[];

export const getDrawingTermMappings = (matterId: string): Promise<DrawingTermMappingsResponse> =>
  api.get(`/matter/${matterId}/drawing/term-mappings`).then((r) => r.data);

/** PD-002 — drafter-driven term ↔ numeral binding. Used by the
 *  Claims-tab guided mapping panel to resolve claim_no_numeral
 *  verify issues left over after Auto-map. Supply either
 *  ``drawing_numeral_id`` to bind, or ``unbind: true`` to clear. */
export const patchDrawingTermMapping = (
  matterId: string,
  termMappingId: string,
  body: { drawing_numeral_id?: string | null; unbind?: boolean },
): Promise<{ term_mapping: DrawingTermMappingRow }> =>
  api
    .patch(
      `/matter/${matterId}/drawing/term-mappings/${termMappingId}`,
      body,
    )
    .then((r) => r.data);

// ─── Drawing module — admin endpoints (Phase 3B) ──────────────────

export interface DrawingTierConfig {
  provider: string;
  model: string;
  fallback_provider?: string;
  fallback_model?: string;
}

/** Post-walk image-gen guard surface — emitted only on PATCH
 * responses (never on GET) when the validator returned `None`.
 * Frontend renders an amber banner per warning. */
export interface DrawingConfigWarning {
  code: 'image_gen_unverifiable' | string;
  side?: 'primary' | 'fallback';
  provider?: string;
  model?: string;
  message: string;
}

export interface DrawingConfigResponse {
  tiers: {
    drawing_vision: DrawingTierConfig;
    drawing_capable: DrawingTierConfig;
    drawing_fast: DrawingTierConfig;
    drawing_fallback: DrawingTierConfig;
    drawing_figure_gen: DrawingTierConfig;
    drawing_bootstrap?: DrawingTierConfig;
    [key: string]: DrawingTierConfig | undefined;
  };
  /** Optional warnings list — present only on PATCH responses
   * that ran the figure_gen guard and got an uncertain verdict. */
  warnings?: DrawingConfigWarning[];
}

export const getDrawingAdminConfig = (): Promise<DrawingConfigResponse> =>
  api.get('/admin/drawings/config').then((r) => r.data);

export interface DrawingTierPatch {
  tier: string;
  provider: string;
  model: string;
  fallback_provider?: string;
  fallback_model?: string;
}

export const patchDrawingAdminConfig = (
  body: DrawingTierPatch,
): Promise<DrawingConfigResponse> =>
  api.patch('/admin/drawings/config', body).then((r) => r.data);

export interface DrawingProviderModel {
  id: string;
  tier_hint: string;
}

export interface DrawingProvider {
  id: string;
  models: DrawingProviderModel[];
}

export interface DrawingModelsAvailableResponse {
  providers: DrawingProvider[];
}

export const getDrawingModelsAvailable = (): Promise<DrawingModelsAvailableResponse> =>
  api.get('/admin/drawings/models/available').then((r) => r.data);

export interface DrawingPromptListItem {
  key: string;
  label: string | null;
  tier: string | null;
  stage: string | null;
  is_active: boolean;
  last_edited_at: string | null;
  qa_passed: boolean;
}

export const getDrawingPrompts = (): Promise<{ prompts: DrawingPromptListItem[] }> =>
  api.get('/admin/drawings/prompts').then((r) => r.data);

// ─── Drawing module — figure-gen test (BUG-016 part D) ───────────
//
// Per-tier dry-run that fires a real (small, B&W) image-generation
// call through the configured adapter and returns the resulting PNG
// as base64 so the admin Settings UI can render a thumbnail. The
// backend returns 200 with `ok:false` on provider/config errors so
// the caller can render a clean red banner — only invalid tier
// shape raises a 4xx.
export type DrawingFigureGenTestTier =
  | 'drawing_figure_gen'
  | 'drawing_figure_gen_fallback';

export type DrawingFigureGenTestResponse =
  | {
      ok: true;
      image_b64: string;
      mime: string;
      provider: string;
      model: string;
      duration_ms: number;
      request_id: string;
    }
  | {
      ok: false;
      error_code: string;
      detail: string;
      provider?: string;
    };

export const testDrawingFigureGen = (
  tier: DrawingFigureGenTestTier,
): Promise<DrawingFigureGenTestResponse> =>
  api.post('/admin/drawings/figure-gen/test', { tier }).then((r) => r.data);

// ─── Drawing module — Phase 5 write endpoints ─────────────────────

export interface DrawingFigureFull extends DrawingFigureSummary {
  /** Full row from the detail endpoint shape — extends the summary
   * with state that's only present after import / prime. The
   * imported_description override matches the parent's optional
   * signature exactly so TS doesn't flag a property-optionality
   * widening (TS2430). */
  imported_description?: string;
  source_matter_figure_id?: number | null;
  source_snapshot_json?: Record<string, unknown> | null;
  claim_prime_json?: Record<string, unknown> | null;
  analyze_bundle_json?: Record<string, unknown> | null;
  drafters_note?: string;
  raster_path?: string | null;
  /** Set by the backend when the LLM call after import / sync
   * failed; the import itself is still durable. */
  prime_error?: string;
  /** NB-08 — on the response to picking a generated candidate: the
   * figure label the image prints itself ("FIG. 1"), if any. */
  prints_figure_label?: string;
}

export interface DrawingImportResponse { figure: DrawingFigureFull }

export const importDrawingFromIDF = (
  matterId: string,
  sourceMatterFigureId: number,
): Promise<DrawingImportResponse> =>
  api
    .post(`/matter/${matterId}/drawing/import-from-idf`, {
      source_matter_figure_id: sourceMatterFigureId,
    })
    .then((r) => r.data);

export const uploadDrawingFigure = (
  matterId: string,
  file: File,
): Promise<DrawingImportResponse> => {
  const form = new FormData();
  form.append('file', file);
  return api
    .post(`/matter/${matterId}/drawing/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => r.data);
};

export interface DrawingSyncResponse {
  figure: DrawingFigureFull;
  divergence: { old_description: string; new_description: string } | null;
}

export const syncDrawingFigureSource = (
  matterId: string,
  drawingFigureId: string,
): Promise<DrawingSyncResponse> =>
  api
    .post(`/matter/${matterId}/drawing/figures/${drawingFigureId}/sync-source`)
    .then((r) => r.data);

export const discardDrawingFigure = (
  matterId: string,
  drawingFigureId: string,
): Promise<void> =>
  api
    .delete(`/matter/${matterId}/drawing/figures/${drawingFigureId}`)
    .then(() => undefined);

export const reprimeDrawingFigure = (
  matterId: string,
  drawingFigureId: string,
): Promise<DrawingImportResponse> =>
  api
    .post(`/matter/${matterId}/drawing/figures/${drawingFigureId}/reprime`)
    .then((r) => r.data);

export const acknowledgeDrawingSourceRemoved = (
  matterId: string,
  drawingFigureId: string,
): Promise<DrawingImportResponse> =>
  api
    .post(
      `/matter/${matterId}/drawing/figures/${drawingFigureId}/acknowledge-source-removed`,
    )
    .then((r) => r.data);

// ─── Drawing module — Phase 6 canvas + numeral CRUD ────────────────

export interface CanvasPrimitive {
  id: string;
  drawing_figure_id: string;
  primitive_type: 'numeral' | 'arrow' | 'curved_arrow' | 'text' | 'lasso';
  primitive_data_json: Record<string, unknown>;
  z_index: number;
  drawing_numeral_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CanvasPrimitiveInput {
  /** Existing rows include `id`; new rows omit it. */
  id?: string;
  primitive_type: 'numeral' | 'arrow' | 'curved_arrow' | 'text' | 'lasso';
  primitive_data_json: Record<string, unknown>;
  z_index?: number;
  drawing_numeral_id?: string | null;
}

export interface CanvasGetResponse {
  figure_id: string;
  primitives: CanvasPrimitive[];
  /** HR-P2.2 — figure's last-touched timestamp (ISO 8601 UTC).
   *  Cached client-side after every read/write; on focus
   *  refetch, a server value newer than the cached one signals
   *  another tab wrote → toast + hydrate. */
  figure_updated_at?: string | null;
}

export type CanvasResponse =
  | { locked: true; locked_reason: string }
  | CanvasGetResponse;

export const getDrawingCanvas = (
  matterId: string,
  figureId: string,
): Promise<CanvasResponse> =>
  api
    .get(`/matter/${matterId}/drawing/figures/${figureId}/canvas`)
    .then((r) => r.data);

export interface CanvasPostResponse {
  primitives: CanvasPrimitive[];
  /** HR-P2.2 — echo of the figure's updated_at after the
   *  server applied this write. Cached client-side; see
   *  ``CanvasGetResponse.figure_updated_at`` for the
   *  cross-tab compare on focus refetch. */
  figure_updated_at?: string | null;
}

export const saveDrawingCanvas = (
  matterId: string,
  figureId: string,
  primitives: CanvasPrimitiveInput[],
  replaceAll: boolean,
): Promise<CanvasPostResponse> =>
  api
    .post(`/matter/${matterId}/drawing/figures/${figureId}/canvas`, {
      primitives,
      replace_all: replaceAll,
    })
    .then((r) => r.data);

export const deleteDrawingCanvasPrimitive = (
  matterId: string,
  figureId: string,
  primitiveId: string,
): Promise<void> =>
  api
    .delete(
      `/matter/${matterId}/drawing/figures/${figureId}/canvas/${primitiveId}`,
    )
    .then(() => undefined);

export interface NumeralCreateBody {
  numeral: string;
  label: string;
  bound_claim_id?: string | null;
  bound_claim_term?: string | null;
}

export interface NumeralPatchBody {
  label?: string;
  bound_claim_id?: string | null;
  bound_claim_term?: string | null;
  /** Atomic clear of both binding columns; the backend ignores
   * other binding fields when this is set. */
  unbind?: boolean;
}

export interface NumeralResponse { numeral: DrawingNumeralRow }

export const createDrawingNumeral = (
  matterId: string,
  body: NumeralCreateBody,
): Promise<NumeralResponse> =>
  api.post(`/matter/${matterId}/drawing/numerals`, body).then((r) => r.data);

export const patchDrawingNumeral = (
  matterId: string,
  numeralId: string,
  body: NumeralPatchBody,
): Promise<NumeralResponse> =>
  api
    .patch(`/matter/${matterId}/drawing/numerals/${numeralId}`, body)
    .then((r) => r.data);

export const deleteDrawingNumeral = (
  matterId: string,
  numeralId: string,
): Promise<void> =>
  api
    .delete(`/matter/${matterId}/drawing/numerals/${numeralId}`)
    .then(() => undefined);

// ─── Drawing module — Phase 7 AI figure generation ─────────────────

/** Style preset options exposed to the modal — matches the backend
 * 5-preset enum in :class:`GenerateAiRequest` exactly. */
export type GenerateAiStyle =
  | 'schematic'
  | 'flowchart'
  | 'cross_section'
  | 'block_diagram'
  | 'system_overview';

export interface GenerateAiRequest {
  intent_text: string;
  /** When empty / omitted the backend treats this as "all frozen
   *  claims for this matter" — the field is OPTIONAL since the
   *  workflow-audit retired the modal's per-claim chip selector
   *  (the prompt's "every claim element must be illustrated"
   *  mandate is silently violated by any subset selection).
   *  Programmatic callers that want subset grounding can still
   *  pass an explicit list. */
  grounding_claim_ids?: string[];
  style: GenerateAiStyle;
  /** 1..3, defaults to 3 server-side. */
  candidate_count: number;
  /** BUG-013 Phase 11 — optional drawing_figures.id to scope
   *  generation to a specific figure. When set, the worker
   *  matches the figure's fig_label against drawing_plan's
   *  fig_number entries and picks that figure_spec instead of
   *  always plan[0]. Null (or omitted) preserves legacy
   *  behaviour (first figure in the plan wins). */
  figure_id?: string | null;
}

export interface GenerateAiQueueResponse {
  job_id: string;
  status: 'queued';
}

/** One element of `candidates` in the polling response. Slice 7E.2
 * persists this shape; slice 7G's gallery reads it verbatim. */
export interface GenerateAiCandidate {
  temp_id: string;
  raster_path: string;
  svg_path: string | null;
  is_vectorized: boolean;
  plan_summary: string;
  provider: string;
  model: string;
  seed: number;
  duration_ms: number;
  request_id: string;
  tracing_error: string | null;
  /** NB-08 — set (to the text found) when the vision pass that reads
   * the numerals off a picked candidate also found a figure label the
   * image model drew into it ("FIG. 1"). */
  prints_figure_label?: string;
}

export type GenerateAiJobStatus =
  | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface GenerateAiJobState {
  job_id: string;
  status: GenerateAiJobStatus;
  progress: { step: string; percent: number };
  candidates: GenerateAiCandidate[];
  // Cue A2 — temp_ids the drafter has already promoted into
  // drawing_figures rows. The modal renders these with a
  // "Used in FIG. N" badge and disables their pick button so
  // the server-side ``candidate_already_consumed`` 409 never
  // fires from a UI click.
  consumed_candidate_temp_ids?: string[];
  error: string | null;
  created_at: string | null;
  completed_at: string | null;
  /** BUG-003 — drafter-dismissal timestamp. NULL until the
   *  drafter clicks the X on the GenerationJobPill; ISO string
   *  after. Surfaced on every job-status response so the
   *  frontend can self-suppress the toast without an extra
   *  round-trip even before the FE-side dismissedJobIds set
   *  hydrates from sessionStorage. */
  acknowledged_at?: string | null;
  /** NB-21 — the style / candidate count the job was submitted
   *  with (from its stored request). Absent on older deploys. */
  style?: GenerateAiStyle | null;
  candidate_count?: number | null;
}

export const postGenerateAi = (
  matterId: string,
  body: GenerateAiRequest,
): Promise<GenerateAiQueueResponse> =>
  api
    .post(`/matter/${matterId}/drawing/generate-ai`, body)
    .then((r) => r.data);

/** WISHLIST_REGENERATE_FIGURE Phase 16 — kick off a regenerate-
 *  from-image job. The backend enqueues a single job that runs
 *  Tier 1 (vision analysis) once + Tier 2 (SVG generation) three
 *  times with seeds 1/2/3, producing 3 candidates in
 *  ``drawing_generation_jobs.candidates_json``. The frontend
 *  then routes through the same polling + candidate-selection
 *  paths as the AI-generate flow. */
export interface RegenerateFromImageRequest {
  /** One of: 'schematic' | 'isometric' | 'exploded'. */
  style: string;
  /** Optional steering hint (≤1000 chars). Empty string when the
   *  drafter clicked Regenerate without a note. */
  drafter_intent?: string;
}

export interface RegenerateFromImageResponse {
  job_id: string;
  status: 'queued';
  candidate_count: number;
}

export const postRegenerateFigureFromImage = (
  matterId: string,
  figureId: string,
  body: RegenerateFromImageRequest,
): Promise<RegenerateFromImageResponse> =>
  api
    .post(
      `/matter/${matterId}/drawing/figures/${figureId}/regenerate-from-image`,
      body,
    )
    .then((r) => r.data);

export const getGenerateAiJob = (
  matterId: string,
  jobId: string,
): Promise<GenerateAiJobState> =>
  api
    .get(`/matter/${matterId}/drawing/generate-ai/${jobId}`)
    .then((r) => r.data);

/** Resume-on-refresh: returns the most recent active (queued /
 *  running) figure-generation job for the matter, or a recently-
 *  completed job from the last 24 hours. ``{job: null}`` when
 *  nothing relevant is in flight. Called on DrawingPage mount so
 *  the GenerationJobPill survives a page refresh mid-generation. */
export const getActiveGenerateAiJob = (
  matterId: string,
): Promise<{ job: GenerateAiJobState | null }> =>
  api
    .get(`/matter/${matterId}/drawing/generate-ai`)
    .then((r) => r.data);

/** BUG-003 — mark a terminal generation job as drafter-
 *  acknowledged so the GenerationJobPill toast doesn't reappear
 *  on the next Drawing-module mount. Idempotent server-side
 *  (COALESCE preserves the original ack timestamp). Best-effort
 *  caller: a network failure should NOT block the local
 *  dismissal — the FE's sessionStorage-backed dismissedJobIds
 *  set keeps the toast hidden for the current session regardless. */
export const postAcknowledgeGenerateAiJob = (
  matterId: string,
  jobId: string,
): Promise<{ status: string; job_id: string }> =>
  api
    .post(`/matter/${matterId}/drawing/generate-ai/${jobId}/acknowledge`)
    .then((r) => r.data);

/** Recent Generations panel — chunk-2 durable surface. Returns
 *  the N most-recent jobs for this matter regardless of age,
 *  tagged with disk-presence + commit linkage so the panel can
 *  show the right status icon + action button per row. */
export interface GenerationHistoryEntry {
  id: string;
  status: GenerateAiJobStatus;
  created_at: string | null;
  completed_at: string | null;
  candidate_count: number;
  // Cue A2 — multi-pick from candidate set. consumed_count tracks
  // promotions; available_count = candidate_count − consumed_count;
  // committed_figure_ids carries the full list of figures created
  // from this job (one per consumed candidate). committed_figure_id
  // is the singular back-compat alias (first committed wins).
  consumed_count?: number;
  available_count?: number;
  committed_figure_ids?: string[];
  has_candidates_on_disk: boolean;
  committed_figure_id: string | null;
  error: string | null;
}

export const getGenerationHistory = (
  matterId: string,
  limit: number = 10,
): Promise<{ jobs: GenerationHistoryEntry[] }> =>
  api
    .get(`/matter/${matterId}/drawing/generation-history`, {
      params: { limit },
    })
    .then((r) => r.data);

export const cancelGenerateAi = (
  matterId: string,
  jobId: string,
): Promise<GenerateAiJobState> =>
  api
    .post(`/matter/${matterId}/drawing/generate-ai/${jobId}/cancel`)
    .then((r) => r.data);

export const selectGenerateAiCandidate = (
  matterId: string,
  jobId: string,
  selectedTempId: string,
): Promise<DrawingImportResponse> =>
  api
    .post(
      `/matter/${matterId}/drawing/generate-ai/${jobId}/select`,
      { selected_temp_id: selectedTempId },
    )
    .then((r) => r.data);

/** URL helper for the slice 7G.3 candidate gallery. Mirrors the
 * existing `/figures/{id}/raster` URL pattern (Canvas.tsx:540) so
 * the same axios baseURL prefix applies. Returns the raster path
 * by default; pass `'svg'` for the traced SVG when the candidate
 * has one. */
export const generateAiCandidateAssetUrl = (
  matterId: string,
  jobId: string,
  tempId: string,
  asset: 'raster' | 'svg' = 'raster',
): string =>
  `/api/matter/${matterId}/drawing/generate-ai/${jobId}/candidates/${tempId}/${asset}`;

// ─── Drawing module — Phase 8 analyze + verify + auto-map + freeze ─

export interface NumeralLabelConflict {
  numeral: string;
  current_label: string;
  bundle_label: string;
  drawing_numeral_id: string;
}

export interface AnalyzeBundle {
  fig_number?: string;
  extracted_numerals?: Array<{ numeral: string; label: string }>;
  numeral_to_claim_map?: Array<{
    numeral: string; claim_id: string;
    claim_clause: string; mapping_type?: string; rationale?: string;
  }>;
  missing_coverage?: Array<Record<string, unknown>>;
  drafters_note_verbatim?: string;
  technical_anchors?: string[];
  [key: string]: unknown;
}

export interface AnalyzeFigureResponse {
  figure: DrawingFigureFull;
  bundle: AnalyzeBundle;
  numeral_label_conflicts: NumeralLabelConflict[];
  term_mappings_upserted: number;
}

export const analyzeFigure = (
  matterId: string,
  drawingFigureId: string,
  draftersNote: string,
): Promise<AnalyzeFigureResponse> =>
  api
    .post(
      `/matter/${matterId}/drawing/figures/${drawingFigureId}/analyze`,
      { drafters_note: draftersNote },
    )
    .then((r) => r.data);

// ─── Phase 8 — Verify / Auto-map / Freeze / Unfreeze ──────────────

export type VerifyIssueKind =
  | 'unmapped_term' | 'orphan_numeral' | 'numeral_no_claim'
  | 'claim_no_numeral' | 'duplicate_label' | 'stale_numeral';

/** Per-issue detail shape varies by kind; the action bench reads the
 * common fields (kind + label / claim hints) and renders kind-
 * specific details inline. Slice 8B.1 locks the field set. */
export type VerifyIssue =
  | { kind: 'unmapped_term'; term_mapping_id: string;
      claim_id: string; claim_term: string }
  | { kind: 'orphan_numeral'; primitive_id: string; figure_id: string;
      label_hint: string | null; numeral_hint: string | null }
  | { kind: 'numeral_no_claim'; numeral_id: string;
      numeral: string; label: string }
  | { kind: 'claim_no_numeral'; claim_id: string; claim_number: number }
  | { kind: 'duplicate_label'; label: string;
      numeral_ids: string[]; numerals: string[] }
  | { kind: 'stale_numeral'; numeral_id: string;
      numeral: string; label: string };

/** NB-36 — advisory: two numerals that name one element. Not an
 * issue; freezing is not refused for it. */
export interface VerifyWarning {
  kind: 'similar_label';
  severity: 'warning';
  numerals: string[];
  labels: string[];
  message: string;
}

export interface VerifyFigureResponse {
  figure_id: string;
  issues: VerifyIssue[];
  counts: Record<VerifyIssueKind, number>;
  total: number;
  warnings?: VerifyWarning[];
}

export const verifyFigure = (
  matterId: string,
  figureId: string,
): Promise<VerifyFigureResponse> =>
  api
    .post(`/matter/${matterId}/drawing/figures/${figureId}/verify`)
    .then((r) => r.data);

export interface AutoMapDetail {
  term_mapping_id: string;
  claim_id: string;
  claim_term: string;
  bucket: 'high' | 'ambiguous' | 'low' | 'no_match';
  best_ratio: number;
  candidates: Array<{ numeral_id: string; label: string; ratio: number }>;
  bound_numeral_id: string | null;
  /** Slice 8B.2.b LLM-fallback fields, present only on
   * ambiguous / low rows. */
  llm_outcome?: 'resolved' | 'null' | 'invalid_id' | 'missing_response';
  llm_confidence?: 'high' | 'medium' | 'low';
  llm_rationale?: string | null;
}

export interface AutoMapResponse {
  auto_mapped: number;
  ambiguous: number;
  low_confidence: number;
  no_match: number;
  llm_resolved: number;
  llm_unresolved: number;
  llm_error: string | null;
  total_unmapped: number;
  details: AutoMapDetail[];
  /** BUG-007 — diagnostic counters surfaced alongside the
   *  standard auto-map result so the FE toast can distinguish
   *  "nothing to map because Analyze hasn't run" from
   *  "everything is already mapped". Optional for back-compat
   *  with deploys still on the older response shape. */
  vision_extracted_count?: number;
  /** NB-09 — of the numerals vision found, how many were new to
   *  the registry and how many were newly recorded as on this
   *  figure (the numeral is printed in the image). */
  vision_new_count?: number;
  vision_placed_count?: number;
  vision_error?: string | null;
  term_mappings_total?: number;
  registry_size?: number;
  /** Single drafter-readable hint string the FE renders
   *  verbatim when the standard counts produce a confusing
   *  "Nothing to auto-map" message. Null when the counts
   *  already tell the story (something mapped, or unmapped
   *  rows remain). */
  hint?: string | null;
}

export const autoMapMatter = (
  matterId: string,
  figureId?: string | null,
): Promise<AutoMapResponse> =>
  api
    .post(`/matter/${matterId}/drawing/auto-map`,
          { figure_id: figureId ?? null })
    .then((r) => r.data);

export interface FreezeFigureResponse {
  figure: DrawingFigureFull;
  content_hash: string;
  issues_acknowledged: boolean;
  issue_count: number;
}

export interface UnfreezeFigureResponse {
  figure: DrawingFigureFull;
  prior_content_hash: string | null;
}

export const freezeFigure = (
  matterId: string,
  figureId: string,
  acknowledgeWarnings: boolean,
): Promise<FreezeFigureResponse> =>
  api
    .post(
      `/matter/${matterId}/drawing/figures/${figureId}/freeze`,
      { acknowledge_warnings: acknowledgeWarnings },
    )
    .then((r) => r.data);

export const unfreezeFigure = (
  matterId: string,
  figureId: string,
): Promise<UnfreezeFigureResponse> =>
  api
    .post(`/matter/${matterId}/drawing/figures/${figureId}/unfreeze`)
    .then((r) => r.data);

// ─── Drawing module — Phase 9 minor-edit + impact + revert + resolve ─
//
// Backend slices:
//   9A  validator (drawing_edit_validator.py).
//   9B  POST /drawing/claims/{id}/edit + /rerun-impact.
//   9C  POST /drawing/claims/{id}/revert.
//   9D  POST /drawing/impact-reports/{id}/resolve.
//
// Phase 9 audit log shape — single source of truth for the
// `kind` field is `backend/drawing_edit_validator.py`.

export type DrawingClaimEditKind = 'edited' | 'reverted' | 'flagged';

/** Locked classification enum — drafter-self-reported metadata
 * on the slice 9B request body. Not honoured by the validator
 * (which always runs the same rules); stored verbatim on the
 * audit log row. */
export type ClaimEditClassification =
  | 'typo'
  | 'antecedent'
  | 'formatting'
  | 'reword';

/** `drawing_claim_edit_log.edit_entry_json` — per-kind payload.
 * Optional fields are present per kind:
 *   edited:   prior_text, new_text, diff, classification,
 *             runs_impact_analysis, validator_violations,
 *             needs_llm_check, triggered_by_email.
 *   reverted: reverted_log_id, restored_to_text,
 *             prior_text_at_revert, triggered_by_email.
 *   flagged:  reason, flag_id, redirect_url, triggered_by_email
 *             (slice 9F). */
export interface DrawingClaimEditEntry {
  version: number;
  kind: DrawingClaimEditKind;
  // edited
  prior_text?: string;
  new_text?: string;
  diff?: string;
  classification?: ClaimEditClassification | null;
  runs_impact_analysis?: boolean;
  validator_violations?: string[];
  needs_llm_check?: boolean;
  // reverted
  reverted_log_id?: string | null;
  restored_to_text?: string;
  prior_text_at_revert?: string;
  // flagged (slice 9F — fields reserved here so the type is stable)
  reason?: string;
  flag_id?: string;
  redirect_url?: string;
  // common
  triggered_by_email?: string;
}

export interface DrawingClaimEditLogRow {
  id: string;
  workspace_id: number;
  matter_id: string;
  claim_id: string;
  claim_version_id: string | null;
  edit_entry_json: DrawingClaimEditEntry;
  triggered_by: number | null;
  created_at: string;
}

export const getDrawingEditLog = (
  matterId: string,
): Promise<DrawingClaimEditLogRow[]> =>
  api.get(`/matter/${matterId}/drawing/edit-log`).then((r) => r.data);

/** `drawing_impact_reports.report_json._resolution` — slice 9D
 * resolution metadata merged into the report dict on resolve.
 * `null` until the row is resolved. */
export interface ImpactReportResolution {
  resolved_by_email: string;
  resolution_note: string | null;
  resolved_at: string;
}

/** Parsed impact-report row. The `report` object carries the LLM
 * payload from `drawing_claim_edit_impact` plus a possible
 * `_resolution` block once the row has been resolved. */
export interface DrawingImpactReport {
  id: string;
  matter_id: string;
  drawing_claim_edit_log_id: string;
  status: 'open' | 'resolved';
  report: {
    diff_classification?: string;
    affected_figures?: unknown[];
    stale_numerals?: unknown[];
    affected_dependent_claims?: unknown[];
    qc_must_rerun?: boolean;
    qc_rerun_rationale?: string;
    revert_risk?: string;
    revert_risk_rationale?: string;
    claim_edit_log_entry?: string;
    open_questions?: unknown[];
    _resolution?: ImpactReportResolution;
    [key: string]: unknown;
  };
  created_at: string;
  updated_at: string;
}

export const getDrawingImpactReports = (
  matterId: string,
  options?: { include?: 'resolved' },
): Promise<DrawingImpactReport[]> => {
  const params: Record<string, string> = {};
  if (options?.include === 'resolved') params.include = 'resolved';
  return api
    .get(`/matter/${matterId}/drawing/impact-reports`, { params })
    .then((r) => r.data);
};

export interface ClaimEditRequestBody {
  new_text: string;
  edit_classification?: ClaimEditClassification;
}

/** 400 ``error_code='scope_change_detected'`` response shape
 * (axios surfaces it as an AxiosError with `response.data.detail`).
 * Carries the violations list the right-panel renders inline. */
export interface ClaimEditScopeChangeDetail {
  error_code: 'scope_change_detected';
  detail: string;
  violations: string[];
  redirect_hint: 'flag_for_revision';
}

export interface DrawingClaimEditResponse {
  /** Claim row as serialised by `_build_claim_response_dict`. */
  claim: Record<string, unknown> & {
    id: string;
    claim_text: string;
    version_history: Array<{
      claim_text: string;
      analysis?: unknown;
      generated_at: string;
    }>;
    ai_generated?: boolean | number;
  };
  edit_log_id: string;
  impact_report: DrawingImpactReport | null;
  /** Set when the impact-analysis LLM crashed; the edit itself
   * still persisted (see 9B.3 test). UI can offer rerun. */
  impact_error: { detail: string } | null;
}

export const drawingClaimEdit = (
  matterId: string,
  claimId: string,
  body: ClaimEditRequestBody,
): Promise<DrawingClaimEditResponse> =>
  api
    .post(`/matter/${matterId}/drawing/claims/${claimId}/edit`, body)
    .then((r) => r.data);

export interface DrawingClaimRerunImpactResponse {
  edit_log_id: string;
  impact_report: DrawingImpactReport;
}

export const drawingClaimRerunImpact = (
  matterId: string,
  claimId: string,
): Promise<DrawingClaimRerunImpactResponse> =>
  api
    .post(`/matter/${matterId}/drawing/claims/${claimId}/rerun-impact`)
    .then((r) => r.data);

export interface DrawingClaimRevertResponse {
  claim: DrawingClaimEditResponse['claim'];
  edit_log_id: string;
  reverted_log_id: string | null;
}

export const drawingClaimRevert = (
  matterId: string,
  claimId: string,
): Promise<DrawingClaimRevertResponse> =>
  api
    .post(`/matter/${matterId}/drawing/claims/${claimId}/revert`)
    .then((r) => r.data);

export interface ImpactReportResolveBody {
  resolution_note?: string;
}

export const drawingImpactReportResolve = (
  matterId: string,
  reportId: string,
  body: ImpactReportResolveBody = {},
): Promise<DrawingImpactReport> =>
  api
    .post(
      `/matter/${matterId}/drawing/impact-reports/${reportId}/resolve`,
      body,
    )
    .then((r) => r.data);

export interface ClaimFlagRequestBody {
  reason?: string;
}

export interface DrawingClaimFlagResponse {
  claim: DrawingClaimEditResponse['claim'];
  edit_log_id: string;
  flag_id: string;
  /** Stage-3 path the right-panel should send the drafter to. */
  redirect_url: string;
  /** Ids of impact reports auto-closed by this flag. Empty when
   * the claim had no open reports. */
  auto_resolved_report_ids: string[];
  /** True when the claim was frozen pre-flag (typical case);
   * false signals the unfreeze step was a no-op (claim was
   * already unfrozen — flagging an unfrozen claim still records
   * the audit row so the drafter's intent is preserved). */
  was_frozen: boolean;
}

export const drawingClaimFlag = (
  matterId: string,
  claimId: string,
  body: ClaimFlagRequestBody = {},
): Promise<DrawingClaimFlagResponse> =>
  api
    .post(`/matter/${matterId}/drawing/claims/${claimId}/flag`, body)
    .then((r) => r.data);


// ═══════════════════════════════════════════════════════════════════
//  Spec module — Phase 4 Slice 4B API helpers
//  Read endpoints + the lazy-seed POST + admin config GET/PATCH for
//  the settings page. Locked-state contract mirrors Drawing's:
//  matter-scoped reads return either the real payload or
//  {locked: true, locked_reason: 'drawings_not_frozen'}.
// ═══════════════════════════════════════════════════════════════════

export interface SpecDocument {
  id: string;
  workspace_id: number;
  matter_id: string;
  jurisdiction: string;
  locale: string;
  target_word_count_min: number;
  target_word_count_max: number;
  overall_completeness_pct: number;
  total_word_count?: number;
  last_recomputed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SpecSectionSummary {
  total: number;
  drafted: number;
  with_issues: number;
  empty: number;
  auto: number;
  frozen: number;
}

export interface SpecStateResponse {
  locked: boolean;
  locked_reason?: string;
  spec_document?: SpecDocument | null;
  section_summary?: SpecSectionSummary;
  active_section_id?: string | null;
  last_activity_at?: string | null;
}

export type SpecSectionUiState = 'done' | 'warn' | 'active' | 'empty' | 'locked';

export interface SpecSectionRow {
  id: string;
  section_kind: string;
  sequence_num: number;
  statute_ref: string | null;
  title: string;
  is_auto_generated: boolean;
  word_count: number;
  is_frozen: boolean;
  frozen_at: string | null;
  paragraph_count: number;
  ui_state: SpecSectionUiState;
  meta_text: string;
  /** BUG-033 — drafter override. When true, the section is hidden
   * from DOCX/PDF/bundle exports (the row stays in the DB so the
   * drafter can re-include later). */
  is_excluded?: boolean;
  /** BUG-033 — drafter-controlled display order; null/undefined
   * falls back to sequence_num. Used by the rail and the export
   * pipeline. */
  display_order?: number | null;
  /** BUG-048 — drafter-renamed section heading. Display layer
   * uses ``custom_title ?? title`` everywhere. Null means "use
   * the seeded title"; setting null via the rename endpoint
   * resets to the catalogue default. */
  custom_title?: string | null;
  updated_at: string;
}

/** BUG-033 — PATCH the drafter exclude / reorder fields. Both
 * are optional; sending one keeps the other unchanged. */
export const patchSpecSectionVisibility = (
  matterId: string,
  sectionId: string,
  body: { is_excluded?: boolean; display_order?: number },
): Promise<{ sections: SpecSectionRow[] }> =>
  api
    .patch(
      `/matter/${matterId}/spec/sections/${sectionId}/visibility`,
      body,
    )
    .then((r) => r.data);

/** BUG-048 — drafter-renamed section heading. ``customTitle ===
 * null`` resets to the seeded default; sending a string saves
 * the override (max 120 chars after whitespace strip). */
export const patchSpecSectionRename = (
  matterId: string,
  sectionId: string,
  customTitle: string | null,
): Promise<{ sections: SpecSectionRow[] }> =>
  api
    .patch(
      `/matter/${matterId}/spec/sections/${sectionId}/rename`,
      { custom_title: customTitle },
    )
    .then((r) => r.data);

/** BUG-039 — live IDF coverage panel. Mirrors the §A.10 export
 * appendix audit so drafters can see missing inventor-disclosed
 * concepts as they draft. Pure SQL + string matching server-
 * side; cached for 5 minutes per matter. ``forceRefresh`` bypasses
 * the cache for the manual refresh button. */
export interface SpecIdfConcept {
  concept: string;
  status: 'covered' | 'missing';
  found_in_sections: string[];
  idf_source: string;
  first_paragraph_number: string | null;
  match_kind: string;
}

/** BUG-040 — claim-side coverage block carried alongside the IDF
 * block in the same endpoint response. Same field shape so the
 * dock's renderer can take either one. */
export interface SpecCoverageBlock {
  total_concepts: number;
  covered: number;
  missing: number;
  coverage_pct: number;
  concepts: SpecIdfConcept[];
}

export interface SpecIdfCoverageResponse {
  total_concepts: number;
  covered: number;
  missing: number;
  coverage_pct: number;
  concepts: SpecIdfConcept[];
  /** BUG-040 — parallel audit against frozen claim terms. Optional
   * for backward compat with deploys still on the BUG-039 shape. */
  claim_coverage?: SpecCoverageBlock;
  last_checked: string;
  has_idf_summary: boolean;
  has_drafted_content: boolean;
  has_frozen_claims?: boolean;
}

export const getSpecIdfCoverage = (
  matterId: string,
  forceRefresh: boolean = false,
): Promise<SpecIdfCoverageResponse> =>
  api
    .get(`/matter/${matterId}/spec/idf-coverage`, {
      params: forceRefresh ? { force_refresh: true } : undefined,
    })
    .then((r) => r.data);

/** NB-23 — the audit again if it was run on this matter before;
 *  ``null`` when it never was (the dock keeps its Run button). */
export const getSpecLastIdfCoverage = (
  matterId: string,
): Promise<SpecIdfCoverageResponse | null> =>
  api
    .get(`/matter/${matterId}/spec/idf-coverage`, {
      params: { last_run_only: true },
    })
    .then((r) => (r.data && r.data.never_run ? null : r.data));

export type SpecParagraphState = 'idle' | 'active' | 'warn' | 'empty';

export interface SpecParagraph {
  id: string;
  spec_section_id: string;
  paragraph_number: string;
  sequence_num: number;
  body_html: string;
  body_text: string;
  word_count: number;
  state: SpecParagraphState;
  /** Paragraph-level freeze. When true, the next section
   *  regenerate preserves this paragraph verbatim instead of
   *  overwriting it. Toggled via patchSpecParagraphLock. */
  is_locked?: boolean;
  last_generated_by_llm: boolean;
  last_generated_prompt_key: string | null;
  last_generated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SpecSectionDetail {
  section: Omit<SpecSectionRow, 'paragraph_count' | 'ui_state' | 'meta_text'>;
  paragraphs: SpecParagraph[];
}

export interface SpecSeedResponse {
  spec_document: SpecDocument | null;
  sections: SpecSectionRow[];
  seeded: boolean;
}

export interface SpecFeatureMapping {
  id: string;
  workspace_id: number;
  matter_id: string;
  feature_tag: string;
  feature_text: string;
  source_claim_id: string | null;
  anchor_paragraph_id: string | null;
  created_at: string;
  updated_at: string;
}

export type SpecExaminerFlagKind =
  | 'claim_only'
  | 'spec_only'
  | 'numeral_undefined'
  | 'figure_undescribed'
  | 'antecedent_basis';

export type SpecExaminerFlagStatus =
  | 'open'
  | 'acknowledged'
  | 'resolved'
  | 'dismissed';

export interface SpecExaminerFlag {
  id: string;
  workspace_id: number;
  matter_id: string;
  flag_kind: SpecExaminerFlagKind;
  term: string;
  detail_json: string;
  status: SpecExaminerFlagStatus;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SpecReferenceSign {
  numeral: string;
  label: string;
  bound_claim_id: string | null;
  source: 'drawing' | 'claim' | 'both';
  appears_in_figures: string[];
}

export interface SpecDrawingSummary {
  drawing_figure_id: string;
  fig_label: string | null;
  summary_short: string;
  technical_anchors: unknown[];
  extracted_numerals: string[];
}

export interface SpecRevisionProposal {
  id: string;
  workspace_id: number;
  matter_id: string;
  target_kind: 'claim' | 'drawing';
  target_claim_id: string | null;
  target_drawing_figure_id: string | null;
  proposed_change_text: string;
  rationale: string | null;
  status: 'draft' | 'sent' | 'consumed' | 'cancelled';
  sent_at: string | null;
  consumed_at: string | null;
  triggered_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface SpecEditLogEntry {
  id: string;
  workspace_id: number;
  matter_id: string;
  spec_paragraph_id: string | null;
  spec_section_id: string | null;
  event_kind: string;
  event_data_json: string;
  triggered_by: number | null;
  created_at: string;
}

// ─── Read endpoints ──────────────────────────────────────────────

export const getSpecState = (matterId: string): Promise<SpecStateResponse> =>
  api.get(`/matter/${matterId}/spec/state`).then((r) => r.data);

export const getSpecSections = (
  matterId: string,
): Promise<SpecSectionRow[] | SpecStateResponse> =>
  api.get(`/matter/${matterId}/spec/sections`).then((r) => r.data);

export const getSpecSectionDetail = (
  matterId: string,
  sectionId: string,
): Promise<SpecSectionDetail | SpecStateResponse> =>
  api
    .get(`/matter/${matterId}/spec/sections/${sectionId}`)
    .then((r) => r.data);

export const postSpecSeed = (
  matterId: string,
): Promise<SpecSeedResponse | SpecStateResponse> =>
  api.post(`/matter/${matterId}/spec/seed`).then((r) => r.data);

export const getSpecFeatureMappings = (
  matterId: string,
): Promise<SpecFeatureMapping[] | SpecStateResponse> =>
  api.get(`/matter/${matterId}/spec/feature-mappings`).then((r) => r.data);

/** BUG-042 — manual re-extract trigger for spec_feature_mappings.
 * Hook for the "Extract from frozen claims" button in the
 * Feature → Spec map dock when the freeze-time auto-extract
 * silently failed. Returns the new + total counts. */
export const postSpecExtractFeatures = (
  matterId: string,
): Promise<{ inserted: number; total: number }> =>
  api.post(`/matter/${matterId}/spec/extract-features`).then((r) => r.data);

// ─── Spec Term/Phrase Web (docs/SPEC_TERM_WEB_DESIGN.md) ─────────
// Human-like specification generation: an ordered web of claim Terms
// (structural/noun limitations) and Phrases (functional/verb
// limitations); each generates one 6-sentence paragraph chained to
// the previous terms in the web.

export interface SpecTermWebEntry {
  id: string;
  term_text: string;
  term_kind: 'term' | 'phrase';
  source_claim_numbers: number[];
  sequence_num: number;
  status: 'suggested' | 'selected' | 'rejected';
  origin: 'auto' | 'manual';
  paragraph_id: string | null;
  notes: string | null;
  /** DD workbench — clause anchoring (null on legacy claim-level rows). */
  claim_number?: number | null;
  clause_index?: number | null;
  created_at: string;
  updated_at: string;
}

export const getSpecTerms = (
  matterId: string,
): Promise<{ terms: SpecTermWebEntry[] }> =>
  api.get(`/matter/${matterId}/spec/terms`).then((r) => r.data);

export const postSpecTermsExtract = (
  matterId: string, autoSelect = false,
): Promise<{ inserted: number; total: number; mode: string; terms: SpecTermWebEntry[] }> =>
  api.post(`/matter/${matterId}/spec/terms/extract`, { auto_select: autoSelect })
    .then((r) => r.data);

export const postSpecTermCreate = (
  matterId: string,
  body: { term_text: string; term_kind?: 'term' | 'phrase'; source_claim_numbers?: number[]; notes?: string },
): Promise<SpecTermWebEntry> =>
  api.post(`/matter/${matterId}/spec/terms`, body).then((r) => r.data);

export const patchSpecTerm = (
  matterId: string, termId: string,
  body: { status?: 'suggested' | 'selected' | 'rejected'; term_kind?: 'term' | 'phrase'; notes?: string },
): Promise<SpecTermWebEntry> =>
  api.patch(`/matter/${matterId}/spec/terms/${termId}`, body).then((r) => r.data);

export const postSpecTermsReorder = (
  matterId: string, orderedIds: string[],
): Promise<{ terms: SpecTermWebEntry[] }> =>
  api.post(`/matter/${matterId}/spec/terms/reorder`, { ordered_ids: orderedIds })
    .then((r) => r.data);

export const postSpecTermsGenerate = (
  matterId: string,
  body: { overwrite?: boolean; batch_size?: number } = {},
): Promise<{
  status: string; paragraph_count: number; terms_generated: number;
  terms_linked: number; batches: number; duration_ms: number;
}> =>
  api.post(`/matter/${matterId}/spec/terms/generate`, body).then((r) => r.data);

export const getSpecTermsVerify = (
  matterId: string,
): Promise<{
  ok: boolean; selected_count: number;
  missing_paragraph: { id: string; term_text: string; term_kind: string }[];
  unsupported_terms: { id: string; term_text: string; term_kind: string }[];
  template_violations: { id: string; term_text: string; term_kind: string; sentence_count: number }[];
}> =>
  api.get(`/matter/${matterId}/spec/terms/verify`).then((r) => r.data);

// ─── DD workbench — clause backbone + enablement (DD-1/DD-3) ─────
// docs/DETAILED_DESCRIPTION_WORKBENCH_PLAN.md + docs/api-contracts.md.

export interface SpecClauseLinkedParagraph { id: string; body_text: string }

export interface SpecClaimClause {
  id: string;
  claim_id: string | null;
  claim_number: number;
  clause_index: number;
  clause_text: string;
  origin: 'auto' | 'llm' | 'manual';
  status: 'pending' | 'enabled';
  updated_at: string;
  /** Present on GET /clauses — the clause's persisted DOE blocks, in
   * reading order: restatement → definitions → clause paragraph →
   * claim benefit. */
  linked?: {
    /** The clause restated as the block's opening sentence. Absent
     * on clauses accepted before the agreed format existed. */
    restatement?: SpecClauseLinkedParagraph | null;
    definitions: SpecClauseLinkedParagraph[];
    clause_paragraph: SpecClauseLinkedParagraph | null;
    benefit_paragraph: SpecClauseLinkedParagraph | null;
  };
  /** Text generated for this clause and waiting for the drafter to
   * accept it. Nothing of it is in the Detailed Description yet, so a
   * proposed clause has an empty `linked`. */
  proposed?: SpecClauseEnableResult | null;
  proposed_at?: string | null;
}

/** Ensure-and-list: segments un-split frozen claims, silently
 * re-segments amended ones (check resplit_claims). */
export const getSpecClauses = (
  matterId: string,
): Promise<{
  clauses: SpecClaimClause[];
  resplit_claims: number[];
  /** Claims generating right now on the server. */
  generating_claims?: number[];
}> =>
  api.get(`/matter/${matterId}/spec/clauses`).then((r) => r.data);

export const postSpecClausesResplit = (
  matterId: string, claimNumber: number, useLlm = false,
): Promise<{ clauses: SpecClaimClause[]; llm_split_rejected?: boolean }> =>
  api.post(
    `/matter/${matterId}/spec/claims/${claimNumber}/clauses/resplit`,
    { use_llm: useLlm },
  ).then((r) => r.data);

export const postSpecClauseMerge = (
  matterId: string, claimNumber: number, clauseIndex: number,
): Promise<{ clauses: SpecClaimClause[] }> =>
  api.post(
    `/matter/${matterId}/spec/claims/${claimNumber}/clauses/merge`,
    { clause_index: clauseIndex },
  ).then((r) => r.data);

export const postSpecClauseSplit = (
  matterId: string, claimNumber: number, clauseIndex: number, offset: number,
): Promise<{ clauses: SpecClaimClause[] }> =>
  api.post(
    `/matter/${matterId}/spec/claims/${claimNumber}/clauses/split`,
    { clause_index: clauseIndex, offset },
  ).then((r) => r.data);

export interface SpecClauseEnableResult {
  claim_number: number;
  clause_index: number;
  /** The clause restated as a description sentence — the first
   * paragraph of the block. Deterministic, editable before Accept. */
  clause_restatement?: string;
  definitions: { term: string; paragraph: string }[];
  definition_gaps: { term: string; reason: string }[];
  clause_paragraph: string;
  claim_benefit_paragraph: string | null;
  flags: { issue: string; detail: string }[];
  tiers_used: string[];
  duration_ms: number;
}

/** `propose: true` generates the text but leaves the description
 * untouched — the workbench shows it and the drafter accepts or
 * discards. Omitted/false keeps the original straight-to-description
 * behaviour, which is what the bulk enables use. */
export const postSpecClauseEnable = (
  matterId: string, claimNumber: number, clauseIndex: number,
  terms: { text: string; kind: 'term' | 'phrase' }[],
  propose = false,
): Promise<SpecClauseEnableResult | SpecEnableAllQueued> =>
  api.post(
    `/matter/${matterId}/spec/claims/${claimNumber}/clauses/${clauseIndex}/enable`,
    { terms, propose },
    { validateStatus: (s) => s >= 200 && s < 300 },
  ).then((r) => r.data);

/** The drafter's edits to a proposal. Omitted fields keep the
 * generated wording, so an untouched Accept commits what was shown. */
export interface SpecClauseEdits {
  definitions?: { term: string; paragraph: string }[];
  clause_restatement?: string;
  clause_paragraph?: string;
  claim_benefit_paragraph?: string;
}

/** Anchor the drafter's selected terms/phrases to a clause the moment
 * they are selected. Every generation path reads the anchored rows,
 * so a selection made here is defined whichever button generates the
 * claim — before this the selections never left the page. Returns the
 * clause's full anchored-term list. */
export const postSpecClauseTerms = (
  matterId: string, claimNumber: number, clauseIndex: number,
  terms: { text: string; kind: 'term' | 'phrase' }[],
): Promise<{ terms: SpecTermWebEntry[] }> =>
  api.post(
    `/matter/${matterId}/spec/claims/${claimNumber}/clauses/${clauseIndex}/terms`,
    { terms },
  ).then((r) => r.data);

/** Take a term off a clause: a term typed in the workbench with no
 * definition yet is deleted; a term-web row is un-anchored. */
export const deleteSpecClauseTerm = (
  matterId: string, claimNumber: number, clauseIndex: number,
  termId: string,
): Promise<{ removed: 'deleted' | 'unanchored'; term_id: string }> =>
  api.delete(
    `/matter/${matterId}/spec/claims/${claimNumber}/clauses/${clauseIndex}/terms/${termId}`,
  ).then((r) => r.data);

/** Write a clause's proposed text into the Detailed Description. No
 * model call — the text was generated by the enable that proposed it. */
export const postSpecClauseAccept = (
  matterId: string, claimNumber: number, clauseIndex: number,
  edits: SpecClauseEdits = {},
): Promise<SpecClauseEnableResult> =>
  api.post(
    `/matter/${matterId}/spec/claims/${claimNumber}/clauses/${clauseIndex}/accept`,
    edits,
  ).then((r) => r.data);

/** Accept every proposal on one claim, in clause order. */
export const postSpecClaimAccept = (
  matterId: string, claimNumber: number,
  clauses: Array<SpecClauseEdits & { clause_index: number }> = [],
): Promise<{ claim_number: number; accepted: SpecClauseEnableResult[] }> =>
  api.post(
    `/matter/${matterId}/spec/claims/${claimNumber}/accept`,
    { clauses },
  ).then((r) => r.data);

/** Throw away every proposal on one claim. */
export const deleteSpecClaimProposals = (
  matterId: string, claimNumber: number,
): Promise<{ discarded: boolean; clauses: number }> =>
  api.delete(
    `/matter/${matterId}/spec/claims/${claimNumber}/proposals`,
  ).then((r) => r.data);

/** Throw a clause's proposed text away. */
export const deleteSpecClauseProposal = (
  matterId: string, claimNumber: number, clauseIndex: number,
): Promise<{ discarded: boolean }> =>
  api.delete(
    `/matter/${matterId}/spec/claims/${claimNumber}/clauses/${clauseIndex}/proposal`,
  ).then((r) => r.data);

/** With `propose`, the server starts the claim in the background and
 * answers 202 immediately — a claim is minutes of model time per
 * clause, far past any proxy timeout. Watch the proposals arrive by
 * polling the clause list. */
export const postSpecClaimEnable = (
  matterId: string, claimNumber: number, propose = false,
): Promise<
  { enabled: SpecClauseEnableResult[]; skipped_already_enabled: number }
  | SpecEnableAllQueued
  | { status: 'running'; claim_number: number }
> =>
  api.post(
    `/matter/${matterId}/spec/claims/${claimNumber}/enable`, { propose },
    // 202 (job queued) must pass through axios's default validateStatus.
    // One clause measured at up to 266s on a thinking model, so the
    // per-claim enable is job-backed like enable-all.
    { validateStatus: (s) => s >= 200 && s < 300 },
  ).then((r) => r.data);

export interface SpecEnableAllQueued {
  job_id: string;
  poll_url: string;
  status: 'queued';
}

export interface SpecEnableAllResult {
  status: 'ok' | 'stopped_at_cost_cap' | 'locked';
  claims_enabled?: Array<{ claim_number: number }>;
  claims_failed?: Array<{ claim_number: number }>;
  stopped_at_claim?: number;
}

/**
 * Matter-wide enable. Runs as ONE background job when the async
 * path is on (202 + poll URL), so the run outlives the request.
 *
 * The workbench used to loop this client-side, one request per
 * claim, each running up to two 8k-token LLM calls per clause —
 * which on a thinking model outlives the ~100s proxy timeout and
 * died with no JSON body. Callers must handle BOTH shapes: a 202
 * envelope to poll, or the finished result when async is off.
 */
export const postSpecEnableAllClaims = (
  matterId: string, claimNumbers?: number[], propose = false,
): Promise<
  SpecEnableAllQueued | SpecEnableAllResult
  | { status: 'running'; claims_started: number[] }
> =>
  api.post(
    `/matter/${matterId}/spec/claims/enable-all`,
    claimNumbers ? { claim_numbers: claimNumbers, propose } : { propose },
    // 202 must pass through axios's default validateStatus.
    { validateStatus: (s) => s >= 200 && s < 300 },
  ).then((r) => r.data);

export interface SpecClaimQcFinding {
  category: 'contamination' | 'repetition' | 'terminology'
    | 'enablement_gap' | 'unsupported';
  severity: 'blocker' | 'warning' | 'info';
  clause_index: number | null;
  message: string;
  suggested_fix: string;
}

/** QC a claim's description blocks. `clauses` carries the drafter's
 * in-page edits to the proposals (the accept payload's shape) so QC
 * grades what is about to be accepted, not the stored wording. */
export const postSpecClaimQc = (
  matterId: string, claimNumber: number,
  clauses: Array<SpecClauseEdits & { clause_index: number }> = [],
): Promise<{
  claim_number: number;
  findings: SpecClaimQcFinding[];
  summary: { pass: boolean; blockers: number; warnings: number; info: number };
  /** Per clause index: 'proposal' (graded before Accept) or
   * 'accepted' (graded from the document). */
  sources?: Record<string, 'proposal' | 'accepted'>;
  flag_id: string;
}> =>
  api.post(`/matter/${matterId}/spec/claims/${claimNumber}/qc`, { clauses })
    .then((r) => r.data);

/** BUG-044 — cross-reference form (section 02). Drafter fills a
 * structured form (relationship + prior application info), the
 * backend serialises to the jurisdiction-correct sentence and
 * persists the paragraph. The form payload survives reloads via
 * spec_section_settings.custom_settings_json. */
export interface SpecCrossReferenceApplication {
  relationship: string;       // continuation | cip | divisional | priority | none
  prior_type: string;         // us_provisional | us_nonprovisional | pct | ep | gb | other
  number: string;             // free text
  filing_date: string;        // free text — drafter formats per jurisdiction
}

export const getSpecCrossReferenceForm = (
  matterId: string,
  sectionId: string,
): Promise<{ applications: SpecCrossReferenceApplication[] }> =>
  api
    .get(`/matter/${matterId}/spec/sections/${sectionId}/cross-reference/form`)
    .then((r) => r.data);

export const postSpecCrossReferenceGenerate = (
  matterId: string,
  sectionId: string,
  applications: SpecCrossReferenceApplication[],
): Promise<{
  applications: SpecCrossReferenceApplication[];
  paragraphs: SpecParagraph[];
}> =>
  api
    .post(
      `/matter/${matterId}/spec/sections/${sectionId}/cross-reference/generate`,
      { applications },
    )
    .then((r) => r.data);

export const getSpecExaminerFlags = (
  matterId: string,
  status?: 'open' | 'acknowledged' | 'resolved' | 'dismissed' | 'all' | 'open_or_acknowledged',
): Promise<SpecExaminerFlag[] | SpecStateResponse> => {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return api.get(`/matter/${matterId}/spec/examiner-flags${q}`).then((r) => r.data);
};

export const getSpecReferenceSigns = (
  matterId: string,
): Promise<SpecReferenceSign[] | SpecStateResponse> =>
  api.get(`/matter/${matterId}/spec/reference-signs`).then((r) => r.data);

export const getSpecDrawingSummaries = (
  matterId: string,
): Promise<SpecDrawingSummary[] | SpecStateResponse> =>
  api.get(`/matter/${matterId}/spec/drawing-summaries`).then((r) => r.data);

export const getSpecRevisionProposals = (
  matterId: string,
  status?: 'draft' | 'sent' | 'consumed' | 'cancelled' | 'all',
): Promise<SpecRevisionProposal[] | SpecStateResponse> => {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return api.get(`/matter/${matterId}/spec/revision-proposals${q}`).then((r) => r.data);
};

// ─── Auto-section render — Phase 7 Slice 7B ──────────────────────

export interface SpecRenderedParagraph {
  paragraph_number: string;
  body_html: string;
  body_text: string;
}

export interface SpecRenderResponse {
  rendered_paragraphs: SpecRenderedParagraph[];
  source_summary: Record<string, unknown>;
  section_id: string;
  section_kind: 'reference_signs_list' | 'claims_mirror';
  render_version: number;
  etag: string;
}

export type SpecRenderResult =
  | { kind: 'render'; data: SpecRenderResponse }
  | { kind: 'not_modified'; etag: string }
  | { kind: 'locked'; data: SpecStateResponse };

export const getSpecSectionRender = async (
  matterId: string,
  sectionId: string,
  opts: { jurisdiction?: 'EP' | 'US' | 'IN'; ifNoneMatch?: string } = {},
): Promise<SpecRenderResult> => {
  const headers: Record<string, string> = {};
  if (opts.ifNoneMatch) headers['If-None-Match'] = opts.ifNoneMatch;
  const params: Record<string, string> = {};
  if (opts.jurisdiction) params.jurisdiction = opts.jurisdiction;
  const r = await api.get(
    `/matter/${matterId}/spec/sections/${sectionId}/render`,
    {
      headers,
      params,
      // Treat 304 as a non-error so the caller can read the
      // ``not_modified`` branch instead of throwing.
      validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
    },
  );
  if (r.status === 304) {
    return { kind: 'not_modified', etag: r.headers.etag ?? opts.ifNoneMatch ?? '' };
  }
  if (r.data && typeof r.data === 'object' && 'locked' in r.data
      && (r.data as SpecStateResponse).locked) {
    return { kind: 'locked', data: r.data as SpecStateResponse };
  }
  return { kind: 'render', data: r.data as SpecRenderResponse };
};


export const getSpecEditLog = (
  matterId: string,
  opts: { paragraphId?: string; sectionId?: string; limit?: number } = {},
): Promise<SpecEditLogEntry[] | SpecStateResponse> => {
  const params = new URLSearchParams();
  if (opts.paragraphId) params.set('paragraph_id', opts.paragraphId);
  if (opts.sectionId) params.set('section_id', opts.sectionId);
  if (opts.limit) params.set('limit', String(opts.limit));
  const q = params.toString() ? `?${params}` : '';
  return api.get(`/matter/${matterId}/spec/edit-log${q}`).then((r) => r.data);
};

// ─── Admin endpoints (settings page in slice 4F) ────────────────

/** Jurisdictions that carry their own word-target rows. Anything else
 * falls back to the global pair. */
export type SpecJurisdiction = 'EP' | 'US' | 'IN';

export interface SpecJurisdictionWordTarget {
  word_target_min: number | null;
  word_target_max: number | null;
  /** True when this jurisdiction has no row of its own and is showing
   * the global default. */
  inherited: boolean;
}

export interface SpecConfigResponse {
  tiers: {
    spec_capable: { provider: string; model: string };
    spec_fast: { provider: string; model: string };
    spec_fallback: { provider: string; model: string };
  };
  defaults: {
    jurisdiction: string | null;
    word_target_min: number | null;
    word_target_max: number | null;
    by_jurisdiction?: Record<SpecJurisdiction, SpecJurisdictionWordTarget>;
    /** Thinking budget for reasoning models. '' = provider default. */
    reasoning_effort?: string;
  };
}

export interface SpecConfigTierPatch {
  tier: 'spec_capable' | 'spec_fast' | 'spec_fallback';
  provider: string;
  model: string;
}

export interface SpecConfigDefaultsPatch {
  defaults: Partial<{
    jurisdiction: string;
    word_target_min: number;
    word_target_max: number;
    reasoning_effort: string;
    /** Per-jurisdiction word targets. Send null for a bound to clear
     * it, which puts that jurisdiction back on the global default. */
    by_jurisdiction: Partial<Record<SpecJurisdiction, {
      word_target_min?: number | null;
      word_target_max?: number | null;
    }>>;
  }>;
}

// ─── Drafter-set word targets (per matter) ──────────────────────────

/** 'inherit' — section catalogue, the default; 'overall' — one
 * document budget spread across the sections; 'section' — each
 * section's own target. */
export type SpecWordTargetMode = 'inherit' | 'overall' | 'section';

export interface SpecWordTargetSection {
  section_id: string;
  section_kind: string;
  title: string | null;
  is_auto_generated: boolean;
  word_count: number;
  target_word_count_min: number | null;
  target_word_count_max: number | null;
  /** The range actually in force, after mode and overrides. Null for
   * sections whose guidance is shape-driven (see `guidance`). */
  effective_min: number | null;
  effective_max: number | null;
  guidance: string | null;
  inherits: boolean;
}

export interface SpecWordTargetsResponse {
  mode: SpecWordTargetMode;
  jurisdiction: string;
  overall: { min: number | null; max: number | null };
  document_word_count: number;
  sections: SpecWordTargetSection[];
}

export interface SpecWordTargetsPatchBody {
  mode?: SpecWordTargetMode;
  overall_min?: number;
  overall_max?: number;
  sections?: Array<{
    section_id: string;
    target_word_count_min: number | null;
    target_word_count_max: number | null;
  }>;
}

export const getSpecWordTargets = (
  matterId: string,
): Promise<SpecWordTargetsResponse> =>
  api.get(`/matter/${matterId}/spec/word-targets`).then((r) => r.data);

export const patchSpecWordTargets = (
  matterId: string, body: SpecWordTargetsPatchBody,
): Promise<SpecWordTargetsResponse> =>
  api.patch(`/matter/${matterId}/spec/word-targets`, body).then((r) => r.data);

// ─── Phase 11 — Spec telemetry (admin-only) ─────────────────────

export interface SpecTelemetryCost {
  total_calls: number;
  total_input_tokens: number;
  total_output_tokens: number;
  estimated_cost_usd: number;
  by_prompt_key: Record<string, {
    calls: number;
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
  }>;
  window_start: string;
}

export interface SpecTelemetryResponse {
  matter_summary: {
    docket_id: string | null;
    jurisdiction: string | null;
    total_words: number;
    drafted_section_count: number;
    frozen_section_count: number;
  };
  cost: SpecTelemetryCost;
  cap: { usd: number; window_hours: number; window_start: string };
  activity: {
    first_event_at: string | null;
    last_event_at: string | null;
    total_spec_events: number;
  };
  examiner_history: Array<{
    run_at: string;
    run_id?: string;
    flag_count?: number;
    severity?: string;
  }>;
  generate_everything_runs: Array<{
    id: string;
    status: string;
    total_duration_ms: number | null;
    error_message: string | null;
    created_at: string;
    completed_at: string | null;
  }>;
}

export const getSpecTelemetry = (
  matterId: string,
): Promise<SpecTelemetryResponse> =>
  api.get(`/admin/spec/telemetry/${matterId}`).then((r) => r.data);


// ─── Phase 11 — Generate Everything ─────────────────────────────

export interface SpecGenerateEverythingResponse {
  job_id: string;
  sections_queued: string[];
  /** OBS-002 — populated when the legacy (internal-orchestrator)
   *  path serves the request. Mirrors the GET poll endpoint so
   *  the FE can hand-link to it without re-constructing the URL.
   *  Optional because the RQ-worker bridge path emits ``poll_url``
   *  pointing at the queue's job-status surface instead. */
  status_url?: string;
}

export interface SpecGenerateEverythingStatus {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: {
    total_sections: number;
    completed: number;
    failed: number;
    skipped: number;
    current_section: string | null;
    percent: number;
    /** Post-completion claim-anchor coverage roll-up. Map keyed by
     * section_kind (currently 'detailed_desc_embodiments' and
     * 'summary'). Absent until the worker writes the final
     * progress row at completion. */
    anchor_check_summary?: Record<string, {
      missing_features: string[];
      dependent_features_checked?: boolean;
    }>;
    /** Post-completion reference-sign check. Map keyed by
     * section_kind, carrying only the kinds that contain a
     * numeral the matter's registry doesn't have. Absent until
     * the terminal progress row is written; an empty map means
     * the document is clean (or the matter has no registry). */
    unregistered_numerals_summary?: Record<string, {
      numerals: string[];
      where?: Record<string, string>;
    }>;
    /** NB-11 — per section_kind, text this run cut for citing a
     * figure that is not frozen. Only kinds with a cut appear. */
    removed_figures_summary?: Record<string, SpecRemovedFigures>;
    /** NB-17 / NB-28 / NB-29 — per section_kind written by this run,
     * what to check: unsupported or contradicting statements, figures
     * cited for numerals they do not show, paragraphs kept with a
     * problem. Only kinds with a finding appear. */
    generation_findings_summary?: Record<string, SpecGenerationFindings>;
  };
  section_results: Array<{
    section_kind: string;
    status: 'success' | 'failed' | 'skipped';
    paragraph_count?: number;
    duration_ms?: number;
    prompt_key?: string;
    shortcut?: string | null;
    reason?: string;
    error_code?: string;
    message?: string;
  }>;
  total_duration_ms: number | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  /** NB-10 — the drafter ticked "skip examiner review" for this run. */
  skip_examiner?: boolean;
}

export const postSpecGenerateEverything = (
  matterId: string,
  body: {
    sections?: string[];
    drafter_intent?: string;
    skip_examiner?: boolean;
    /** When true, GE also picks up non-empty sections whose
     *  word count is below the per-kind floor and overwrites
     *  them. Defaults to false; only sent when the dialog's
     *  repair-short checkbox is enabled. */
    repair_short?: boolean;
  } = {},
): Promise<SpecGenerateEverythingResponse> =>
  api.post(
    `/matter/${matterId}/spec/generate-everything`, body,
  ).then((r) => r.data);

export const getSpecGenerateEverythingStatus = (
  matterId: string, jobId: string,
): Promise<SpecGenerateEverythingStatus> =>
  api.get(
    `/matter/${matterId}/spec/generate-everything/${jobId}`,
  ).then((r) => r.data);

export const cancelSpecGenerateEverything = (
  matterId: string, jobId: string,
): Promise<{ status: string; job_id: string }> =>
  api.post(
    `/matter/${matterId}/spec/generate-everything/${jobId}/cancel`,
  ).then((r) => r.data);

export interface SpecGenerateEverythingRun {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: {
    total_sections?: number;
    completed?: number;
    failed?: number;
    skipped?: number;
    current_section?: string | null;
    percent?: number;
  };
  total_duration_ms: number | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

/** Drafter-scoped list of recent generate-everything runs. Used to
 * (a) resume polling on SpecPage mount when a job is still running
 * server-side, and (b) drive the persistent "Generations" history
 * pill so completed runs don't vanish from the UI. */
export const listSpecGenerateEverythingRuns = (
  matterId: string,
): Promise<{ runs: SpecGenerateEverythingRun[] }> =>
  api.get(
    `/matter/${matterId}/spec/generate-everything`,
  ).then((r) => r.data);


// ─── Phase 10 Slice 10E — Spec exports ──────────────────────────

export interface SpecExportBundleBody {
  include_spec_docx?: boolean;
  include_spec_pdf?: boolean;
  include_drawings_pdf?: boolean;
  include_claims_docx?: boolean;
  include_provenance?: boolean;
  jurisdiction_override?: 'US' | 'EP' | 'IN' | null;
  paper_override?: 'letter' | 'a4' | null;
}

/** Build the URL for the inline-DOCX preview endpoint. The
 * caller opens this URL in a new browser tab; the browser's
 * Office plugin (when present) renders inline, otherwise the
 * browser offers to download. */
export const buildSpecPreviewDocxUrl = (
  matterId: string,
  opts: { includeFigures?: boolean; includeProvenance?: boolean;
          paper?: 'letter' | 'a4'; jurisdiction?: 'US' | 'EP' | 'IN' } = {},
): string => {
  const params = new URLSearchParams();
  params.set('include_figures', String(opts.includeFigures ?? true));
  params.set('include_provenance', String(opts.includeProvenance ?? false));
  if (opts.paper) params.set('paper', opts.paper);
  if (opts.jurisdiction) params.set('jurisdiction', opts.jurisdiction);
  return `${BACKEND_URL}/api/matter/${matterId}/spec/preview-docx?${params}`;
};

/** BUG-049 — Preview DOCX via authenticated fetch + blob URL.
 * The plain ``window.open`` path drops auth cookies on cross-
 * origin BACKEND_URL deploys (the new tab is treated as a
 * top-level navigation; SameSite=Lax cookies don't send for
 * same-site fetches that aren't a click on an anchor with the
 * matching origin). Using axios with ``withCredentials`` always
 * carries the session cookie. The blob URL is valid for the
 * lifetime of the new tab so the browser's DOCX preview / Word
 * handler picks it up the same way a server-streamed DOCX
 * would. */
export const fetchSpecPreviewDocxBlob = async (
  matterId: string,
  opts: { includeFigures?: boolean; includeProvenance?: boolean;
          paper?: 'letter' | 'a4'; jurisdiction?: 'US' | 'EP' | 'IN' } = {},
): Promise<Blob> => {
  const params: Record<string, string> = {
    include_figures: String(opts.includeFigures ?? true),
    include_provenance: String(opts.includeProvenance ?? false),
  };
  if (opts.paper) params.paper = opts.paper;
  if (opts.jurisdiction) params.jurisdiction = opts.jurisdiction;
  const r = await api.get(
    `/matter/${matterId}/spec/preview-docx`,
    { params, responseType: 'blob' },
  );
  return r.data as Blob;
};

/** FEAT-004 — standalone audit / provenance DOCX. Returns the
 * blob + a server-suggested filename so the caller can trigger
 * a download via an anchor click. The endpoint requires no
 * spec content — a partial audit on an unfinished spec is
 * still useful as evidence. */
export const downloadSpecAuditDocx = async (
  matterId: string,
  opts: { jurisdiction?: 'US' | 'EP' | 'IN' } = {},
): Promise<{ blob: Blob; filename: string }> => {
  const params: Record<string, string> = {};
  if (opts.jurisdiction) params.jurisdiction = opts.jurisdiction;
  const r = await api.get(
    `/matter/${matterId}/spec/export-audit-docx`,
    { params, responseType: 'blob' },
  );
  // Pull the filename from the Content-Disposition header when
  // the server sets it; otherwise fall back to a sensible default.
  const disp = String(r.headers?.['content-disposition'] || '');
  let filename = 'audit.docx';
  const m = disp.match(/filename="?([^";]+)"?/i);
  if (m) filename = m[1];
  return { blob: r.data as Blob, filename };
};

// ── Export preflight (BundleExportModal Fix 1) ──────────────
// Lightweight pre-render check so the modal can disable Export
// for hard failures + show advisory warnings before the drafter
// commits to the slow render path. Backend caps the work at the
// data layer (no DOCX render), so it stays under ~500ms.
export interface SpecExportPreflightWarning {
  slug: string;
  severity: 'error' | 'warning';
  message: string;
  detail: string | null;
  /** The provision the check rests on, when it rests on one. */
  legal_ref?: string | null;
}
export interface SpecExportPreflightResponse {
  warnings: SpecExportPreflightWarning[];
  blocking: boolean;
  ok_to_export: boolean;
  jurisdiction: 'EP' | 'US' | 'IN';
  /** Checks that did not run, and why. */
  checks_skipped?: import('@/types').SkippedCheck[];
}
export const getSpecExportPreflight = (
  matterId: string,
  jurisdiction?: 'EP' | 'US' | 'IN',
): Promise<SpecExportPreflightResponse> =>
  api
    .get(`/matter/${matterId}/spec/export-preflight`, {
      params: jurisdiction ? { jurisdiction } : undefined,
    })
    .then((r) => r.data);

/** Trigger a download of the spec DOCX. Reads the
 * X-Spec-Export-Warnings response header so the caller can
 * surface a non-blocking warning dialog after the download
 * starts. Returns {warnings, blob} once the response is
 * fully buffered (acceptable for files in the 100s of KB). */
export const downloadSpecDocx = async (
  matterId: string,
  opts: { includeFigures?: boolean; includeProvenance?: boolean;
          paper?: 'letter' | 'a4'; jurisdiction?: 'US' | 'EP' | 'IN' } = {},
): Promise<{ blob: Blob; filename: string; warnings: string[] }> => {
  const params: Record<string, string> = {
    include_figures: String(opts.includeFigures ?? true),
    include_provenance: String(opts.includeProvenance ?? true),
  };
  if (opts.paper) params.paper = opts.paper;
  if (opts.jurisdiction) params.jurisdiction = opts.jurisdiction;
  const r = await api.get(`/matter/${matterId}/spec/export-docx`, {
    params,
    responseType: 'blob',
  });
  const filename = _filenameFromDisposition(
    r.headers['content-disposition'] ?? '',
    `spec_${matterId.slice(0, 8)}.docx`,
  );
  let warnings: string[] = [];
  try {
    warnings = JSON.parse(r.headers['x-spec-export-warnings'] ?? '[]');
  } catch { /* malformed header — treat as no warnings */ }
  return { blob: r.data as Blob, filename, warnings };
};

export const downloadSpecPdf = async (
  matterId: string,
  opts: { includeFigures?: boolean; includeProvenance?: boolean;
          paper?: 'letter' | 'a4'; jurisdiction?: 'US' | 'EP' | 'IN' } = {},
): Promise<{ blob: Blob; filename: string }> => {
  const params: Record<string, string> = {
    include_figures: String(opts.includeFigures ?? true),
    include_provenance: String(opts.includeProvenance ?? true),
  };
  if (opts.paper) params.paper = opts.paper;
  if (opts.jurisdiction) params.jurisdiction = opts.jurisdiction;
  const r = await api.get(`/matter/${matterId}/spec/export-pdf`, {
    params,
    responseType: 'blob',
  });
  const filename = _filenameFromDisposition(
    r.headers['content-disposition'] ?? '',
    `spec_${matterId.slice(0, 8)}.pdf`,
  );
  return { blob: r.data as Blob, filename };
};

export const downloadSpecBundle = async (
  matterId: string, body: SpecExportBundleBody,
): Promise<{
  blob: Blob;
  filename: string;
  mime: string;
  notes: string[];
  warnings: string[];
}> => {
  const r = await api.post(
    `/matter/${matterId}/spec/export-bundle`, body,
    { responseType: 'blob' },
  );
  const filename = _filenameFromDisposition(
    r.headers['content-disposition'] ?? '',
    `bundle_${matterId.slice(0, 8)}.zip`,
  );
  let notes: string[] = [];
  try {
    notes = JSON.parse(r.headers['x-spec-export-notes'] ?? '[]');
  } catch { /* malformed header */ }
  let warnings: string[] = [];
  try {
    warnings = JSON.parse(r.headers['x-spec-export-warnings'] ?? '[]');
  } catch { /* malformed header */ }
  return {
    blob: r.data as Blob,
    filename,
    mime: String(r.headers['content-type'] ?? 'application/octet-stream'),
    notes,
    warnings,
  };
};

function _filenameFromDisposition(disp: string, fallback: string): string {
  const m = /filename="([^"]+)"/.exec(disp);
  return m ? m[1] : fallback;
}


// ─── Phase 9 Slice 9C — Drawing pop-out fetcher ─────────────────

export interface SpecPopoutFigureDetail {
  id: string;
  fig_label: string | null;
  raster_path?: string | null;
  svg_path?: string | null;
  raster_width?: number | null;
  raster_height?: number | null;
  is_frozen?: boolean;
  frozen_at?: string | null;
  numerals: CanvasPrimitive[];
  annotations: CanvasPrimitive[];
  term_mappings: Array<{
    id: string;
    matter_id: string;
    drawing_numeral_id: string | null;
    claim_term: string;
  }>;
  drawing_numerals?: Array<{
    id: string;
    numeral: string;
    label: string;
  }>;
}

export const getSpecPopoutFigureDetail = (
  matterId: string, figureId: string,
): Promise<SpecPopoutFigureDetail> =>
  api
    .get(`/matter/${matterId}/drawing/figures/${figureId}`)
    .then((r) => r.data);

export const buildFigurePreviewUrl = (
  matterId: string, figureId: string,
): string =>
  `${BACKEND_URL}/api/matter/${matterId}/drawing/figures/${figureId}/preview`;

export const buildFigureRasterUrl = (
  matterId: string, figureId: string,
): string =>
  `${BACKEND_URL}/api/matter/${matterId}/drawing/figures/${figureId}/raster`;


// ─── Phase 9 — Revision proposals + glossary ────────────────────

export interface SpecRevisionProposalCreateBody {
  target_kind: 'claim' | 'drawing';
  target_claim_id?: string | null;
  target_drawing_figure_id?: string | null;
  proposed_change_text: string;
  rationale?: string | null;
}

export interface SpecRevisionProposalResponse {
  proposal: SpecRevisionProposal;
  redirect_url: string;
}

export type SpecRevisionProposalResult =
  | { kind: 'ok'; data: SpecRevisionProposalResponse }
  | { kind: 'locked'; data: SpecStateResponse }
  | { kind: 'error'; status: number; message: string; error_code?: string };

export const postSpecRevisionProposal = async (
  matterId: string, body: SpecRevisionProposalCreateBody,
): Promise<SpecRevisionProposalResult> => {
  try {
    const r = await api.post(
      `/matter/${matterId}/spec/revision-proposals`, body,
    );
    if (r.data && typeof r.data === 'object' && 'locked' in r.data
        && (r.data as SpecStateResponse).locked) {
      return { kind: 'locked', data: r.data as SpecStateResponse };
    }
    return { kind: 'ok', data: r.data as SpecRevisionProposalResponse };
  } catch (e: unknown) {
    const ax = e as {
      response?: { status?: number; data?: { detail?: { error_code?: string; message?: string } } };
      message?: string;
    };
    return {
      kind: 'error',
      status: ax.response?.status ?? 0,
      message: ax.response?.data?.detail?.message ?? ax.message ?? 'submit failed',
      error_code: ax.response?.data?.detail?.error_code,
    };
  }
};

export const patchSpecRevisionProposal = (
  matterId: string, proposalId: string,
  body: { status?: 'cancelled' | 'consumed'; proposed_change_text?: string; rationale?: string },
): Promise<{ proposal: SpecRevisionProposal }> =>
  api.patch(
    `/matter/${matterId}/spec/revision-proposals/${proposalId}`, body,
  ).then((r) => r.data);

export const postSpecRevisionProposalMarkConsumed = (
  matterId: string, proposalId: string, resolutionNote?: string,
): Promise<{ proposal: SpecRevisionProposal }> =>
  api.post(
    `/matter/${matterId}/spec/revision-proposals/${proposalId}/mark-consumed`,
    { resolution_note: resolutionNote ?? null },
  ).then((r) => r.data);

export const getSpecPendingRevisionProposals = (
  matterId: string, stage: 'stage_3' | 'stage_5',
): Promise<SpecRevisionProposal[]> =>
  api.get(
    `/matter/${matterId}/spec/revision-proposals/pending`,
    { params: { stage } },
  ).then((r) => r.data);

export interface SpecGlossaryEntry {
  term: string;
  definition: string;
  source_section: string;
}

export const getSpecGlossary = (
  matterId: string,
): Promise<SpecGlossaryEntry[]> =>
  api.get(`/matter/${matterId}/spec/glossary`).then((r) => r.data);


// ─── Phase 8 — Examiner review + section verify + flag mutations ──

export interface SpecExaminerReviewSummary {
  claim_only_count: number;
  spec_only_count: number;
  antecedent_count: number;
  numeral_undefined_count: number;
  figure_undescribed_count: number;
  severity: 'low' | 'medium' | 'high';
}

export interface SpecExaminerReviewResponse {
  run_id: string;
  flags: SpecExaminerFlag[];
  summary: SpecExaminerReviewSummary;
  duration_ms: number;
  invalidated_paragraph_ids: string[];
  cached: boolean;
}

export type SpecExaminerReviewResult =
  | { kind: 'ok'; data: SpecExaminerReviewResponse }
  | { kind: 'locked'; data: SpecStateResponse }
  | { kind: 'error'; status: number; message: string };

export const postSpecExaminerReview = async (
  matterId: string,
  body: { force?: boolean } = {},
): Promise<SpecExaminerReviewResult> => {
  try {
    const r = await api.post(
      `/matter/${matterId}/spec/examiner-review`,
      { force: body.force ?? false },
    );
    if (r.data && typeof r.data === 'object' && 'locked' in r.data
        && (r.data as SpecStateResponse).locked) {
      return { kind: 'locked', data: r.data as SpecStateResponse };
    }
    return { kind: 'ok', data: r.data as SpecExaminerReviewResponse };
  } catch (e: unknown) {
    const ax = e as { response?: { status?: number; data?: unknown }; message?: string };
    return {
      kind: 'error',
      status: ax.response?.status ?? 0,
      message: ax.message ?? 'examiner review failed',
    };
  }
};

export interface SpecSectionVerifyIssue {
  kind: string;
  paragraph_id: string | null;
  detail: string;
  suggestion: string;
}

export interface SpecSectionVerifyResponse {
  issues: SpecSectionVerifyIssue[];
  score: { completeness_pct: number; issue_count: number };
  duration_ms: number;
}

export type SpecSectionVerifyResult =
  | { kind: 'ok'; data: SpecSectionVerifyResponse }
  | { kind: 'locked'; data: SpecStateResponse }
  | { kind: 'error'; status: number; message: string };

export const postSpecSectionVerify = async (
  matterId: string,
  sectionId: string,
): Promise<SpecSectionVerifyResult> => {
  try {
    const r = await api.post(
      `/matter/${matterId}/spec/sections/${sectionId}/verify`,
      {},
    );
    if (r.data && typeof r.data === 'object' && 'locked' in r.data
        && (r.data as SpecStateResponse).locked) {
      return { kind: 'locked', data: r.data as SpecStateResponse };
    }
    return { kind: 'ok', data: r.data as SpecSectionVerifyResponse };
  } catch (e: unknown) {
    const ax = e as { response?: { status?: number }; message?: string };
    return {
      kind: 'error',
      status: ax.response?.status ?? 0,
      message: ax.message ?? 'section verify failed',
    };
  }
};

export const postSpecFlagResolve = (
  matterId: string, flagId: string, resolutionNote?: string,
): Promise<{ flag: SpecExaminerFlag }> =>
  api.post(
    `/matter/${matterId}/spec/examiner-flags/${flagId}/resolve`,
    { resolution_note: resolutionNote ?? null },
  ).then((r) => r.data);

export const postSpecFlagDismiss = (
  matterId: string, flagId: string, dismissNote?: string,
): Promise<{ flag: SpecExaminerFlag }> =>
  api.post(
    `/matter/${matterId}/spec/examiner-flags/${flagId}/dismiss`,
    { dismiss_note: dismissNote ?? null },
  ).then((r) => r.data);

export const postSpecFlagAcknowledge = (
  matterId: string, flagId: string,
): Promise<{ flag: SpecExaminerFlag }> =>
  api.post(
    `/matter/${matterId}/spec/examiner-flags/${flagId}/acknowledge`,
  ).then((r) => r.data);

export interface SpecFeatureCreateBody {
  feature_tag: string;
  feature_text: string;
  source_claim_id?: string | null;
  anchor_paragraph_id?: string | null;
}

export const postSpecFeatureCreate = (
  matterId: string, body: SpecFeatureCreateBody,
): Promise<{ mapping: SpecFeatureMapping }> =>
  api.post(
    `/matter/${matterId}/spec/feature-mappings`, body,
  ).then((r) => r.data);

export const patchSpecFeatureMapping = (
  matterId: string, mappingId: string,
  body: { anchor_paragraph_id?: string | null; unanchor?: boolean },
): Promise<{ mapping: SpecFeatureMapping }> =>
  api.patch(
    `/matter/${matterId}/spec/feature-mappings/${mappingId}`, body,
  ).then((r) => r.data);

export const deleteSpecFeatureMapping = (
  matterId: string, mappingId: string,
): Promise<void> =>
  api.delete(`/matter/${matterId}/spec/feature-mappings/${mappingId}`)
    .then(() => undefined);


export const getSpecConfig = (): Promise<SpecConfigResponse> =>
  api.get('/admin/spec/config').then((r) => r.data);

export const patchSpecConfig = (
  body: SpecConfigTierPatch | SpecConfigDefaultsPatch,
): Promise<SpecConfigResponse> =>
  api.patch('/admin/spec/config', body).then((r) => r.data);

export interface SpecModelsAvailableResponse {
  providers: Array<{
    id: string;
    models: Array<{ id: string; tier_hint: string }>;
  }>;
}

export const getSpecModelsAvailable = (): Promise<SpecModelsAvailableResponse> =>
  api.get('/admin/spec/models/available').then((r) => r.data);

export interface SpecPromptListItem {
  key: string;
  label: string | null;
  tier: string | null;
  stage: string | null;
  jurisdiction: 'EP' | 'US' | 'IN' | null;
  is_active: boolean;
  last_edited_at: string | null;
  qa_passed: boolean;
}

export const getSpecPrompts = (): Promise<{ prompts: SpecPromptListItem[] }> =>
  api.get('/admin/spec/prompts').then((r) => r.data);


// ─── Phase 5 Slice 5B/5D — section-generate endpoint ─────────────

export interface SpecSectionGenerateBody {
  drafter_intent?: string;
  overwrite?: boolean;
  /**
   * 'rewrite' (default) is the historical behaviour: draft the whole
   * section and replace the unlocked paragraphs.
   *
   * 'cover_missing' drafts ONLY the claim features this section does
   * not yet support and APPENDS them, so adding coverage stops
   * costing the coverage the draft already had.
   */
  mode?: 'rewrite' | 'cover_missing';
}

export interface SpecAnchorCheck {
  ran: boolean;
  expected_features: string[];
  declared_features: string[];
  missing_features: string[];
  all_covered: boolean;
  /** True when the expected set pulled in dependent-claim
   * features as well as independent (detailed_desc_embodiments).
   * False for independent-only sections (summary). FE banner
   * uses this to pick scope-appropriate copy. Optional for
   * back-compat with older job rows that landed before this
   * field. */
  dependent_features_checked?: boolean;
}

export interface SpecSectionGenerationMeta {
  duration_ms: number;
  tier_used: string | null;
  prompt_key: string | null;
  jurisdiction: string;
  uncertainties: string[];
  shortcut: string | null;
  skip_reason?: string;
  /** Post-generation claim-anchor coverage check. Populated only
   * for detailed_desc_embodiments + summary; absent for any
   * other section kind so the FE can distinguish "didn't run"
   * (field absent) from "ran with empty results" (field
   * present, ran=false). */
  anchor_check?: SpecAnchorCheck;
  /** Reference signs in this section that the matter's registry
   * doesn't have — the model invented them. `ran` is false when
   * the matter has no registry to check against, which is why the
   * empty list alone doesn't mean "clean". Advisory: the
   * paragraphs are already written. */
  unregistered_numerals?: SpecUnregisteredNumerals;
  /** NB-11 — text cut before saving because it cited a figure that
   * is not frozen ("Referring now to FIG. 4 …" on a two-figure matter). */
  removed_figures?: SpecRemovedFigures;
  /** NB-17 — sentences stating a result the disclosure does not
   * contain, or contradicting it. Advisory: the text is saved. */
  unsupported_statements?: SpecUnsupportedStatement[];
  /** NB-29 — sentences citing a figure for a numeral it does not show. */
  figure_mismatches?: SpecFigureMismatch[];
  /** NB-36 — sentences naming a registered numeral with another
   * part's name. */
  numeral_label_mismatches?: SpecNumeralLabelMismatch[];
  /** NB-28 — paragraphs kept with a problem, or claim families that
   * wrote nothing, instead of the whole section failing. */
  validation_warnings?: SpecValidationWarning[];
}

export interface SpecUnsupportedStatement {
  paragraph_id: string | null;
  paragraph_number?: string | null;
  kind: string;
  sentence: string;
  contradicts?: boolean;
  disclosure_sentence?: string;
}

export interface SpecFigureMismatch {
  paragraph_id: string | null;
  paragraph_number?: string | null;
  figures: string[];
  numeral: string;
  label?: string;
  on_figures: string[];
  sentence: string;
}

export interface SpecNumeralLabelMismatch {
  paragraph_id: string | null;
  paragraph_number?: string | null;
  numeral: string;
  /** The registered name. */
  label: string;
  /** What the sentence calls it. */
  called: string;
  sentence: string;
}

export interface SpecValidationWarning {
  problem: 'bare_numeral' | 'dropped' | 'family_failed' | string;
  detail: string;
  numeral?: string;
  family?: string;
  paragraph_id?: string | null;
  paragraph_number?: string | null;
}

/** The three finding lists, as one generate response or one GE
 * section carries them. */
export interface SpecGenerationFindings {
  unsupported_statements?: SpecUnsupportedStatement[];
  figure_mismatches?: SpecFigureMismatch[];
  numeral_label_mismatches?: SpecNumeralLabelMismatch[];
  validation_warnings?: SpecValidationWarning[];
}

/** Figures the generated text cited but the matter does not have,
 * with the start of each passage that was cut. */
export interface SpecRemovedFigures {
  figures: string[];
  excerpts: string[];
}

/** Result of the post-generation reference-sign check. */
export interface SpecUnregisteredNumerals {
  ran: boolean;
  numerals: string[];
  /** numeral → paragraph_number of its first use, so the banner
   * can point the drafter at the paragraph to edit. */
  where?: Record<string, string>;
}

export interface SpecSectionGenerateResponse {
  section: SpecSectionRow | null;
  paragraphs: SpecParagraph[];
  generation_meta: SpecSectionGenerationMeta;
}

export const postSpecSectionGenerate = (
  matterId: string,
  sectionId: string,
  body: SpecSectionGenerateBody = {},
): Promise<SpecSectionGenerateResponse | SpecStateResponse> =>
  api
    .post(`/matter/${matterId}/spec/sections/${sectionId}/generate`, body)
    .then((r) => r.data);


// ─── Phase 5 Slice 5E — paragraph PATCH (auto-save) ─────────────

export interface SpecParagraphPatchBody {
  body_html?: string;
  body_text?: string;
  sequence_num?: number;
  state?: SpecParagraphState;
}

export const patchSpecParagraph = (
  matterId: string,
  sectionId: string,
  paragraphId: string,
  body: SpecParagraphPatchBody,
): Promise<{ paragraph: SpecParagraph } | SpecStateResponse> =>
  api
    .patch(
      `/matter/${matterId}/spec/sections/${sectionId}/paragraphs/${paragraphId}`,
      body,
    )
    .then((r) => r.data);

export const patchSpecParagraphLock = (
  matterId: string,
  sectionId: string,
  paragraphId: string,
  isLocked: boolean,
): Promise<{ paragraph: SpecParagraph } | SpecStateResponse> =>
  api
    .patch(
      `/matter/${matterId}/spec/sections/${sectionId}/paragraphs/${paragraphId}/lock`,
      { is_locked: isLocked },
    )
    .then((r) => r.data);


// ─── Phase 6 Slice 6A/6B/6D — paragraph expand + tighten + NL edit ─

export interface SpecParagraphMutationBody {
  drafter_intent?: string;
}

export interface SpecParagraphExpandResponse {
  paragraph: SpecParagraph;
  expansion_meta: {
    duration_ms: number;
    tier_used: string;
    prompt_key: string;
    char_diff: number;
    added_features: string[];
    jurisdiction: string;
  };
}

export interface SpecParagraphTightenResponse {
  paragraph: SpecParagraph;
  tightening_meta: {
    duration_ms: number;
    tier_used: string;
    prompt_key: string;
    char_diff: number;
    removed_phrases: string[];
    jurisdiction: string;
  };
}

export const postSpecParagraphExpand = (
  matterId: string,
  sectionId: string,
  paragraphId: string,
  body: SpecParagraphMutationBody = {},
): Promise<SpecParagraphExpandResponse | SpecStateResponse> =>
  api
    .post(
      `/matter/${matterId}/spec/sections/${sectionId}/paragraphs/${paragraphId}/expand`,
      body,
    )
    .then((r) => r.data);

export const postSpecParagraphTighten = (
  matterId: string,
  sectionId: string,
  paragraphId: string,
  body: SpecParagraphMutationBody = {},
): Promise<SpecParagraphTightenResponse | SpecStateResponse> =>
  api
    .post(
      `/matter/${matterId}/spec/sections/${sectionId}/paragraphs/${paragraphId}/tighten`,
      body,
    )
    .then((r) => r.data);

export interface SpecNlEditBody {
  drafter_request: string;
}

export interface SpecNlEditResponse {
  section: SpecSectionRow;
  affected_paragraphs: SpecParagraph[];
  nl_edit_meta: {
    prompt_key: string;
    tier_used: string;
    duration_ms: number;
    changes_summary: string;
    warnings: string[];
    drafter_request: string;
    jurisdiction: string;
  };
}

export const postSpecSectionNlEdit = (
  matterId: string,
  sectionId: string,
  body: SpecNlEditBody,
): Promise<SpecNlEditResponse | SpecStateResponse> =>
  api
    .post(`/matter/${matterId}/spec/sections/${sectionId}/nl-edit`, body)
    .then((r) => r.data);


// ─── Phase 6 Slice 6E — paragraph create + delete (hover rail) ──

export interface SpecParagraphCreateBody {
  body_html: string;
  body_text: string;
  paragraph_number?: string;
  sequence_num?: number;
  state?: SpecParagraphState;
  /** Phase 12F — mid-section insert. When set, the new paragraph
   * slots in directly after the named paragraph (sequence_num =
   * after.sequence_num + 1; later paragraphs shift up by 1).
   * paragraph_number does NOT renumber. */
  after_paragraph_id?: string;
}

export const postSpecParagraphCreate = (
  matterId: string,
  sectionId: string,
  body: SpecParagraphCreateBody,
): Promise<{ paragraph: SpecParagraph } | SpecStateResponse> =>
  api
    .post(
      `/matter/${matterId}/spec/sections/${sectionId}/paragraphs`,
      body,
    )
    .then((r) => r.data);

// ─── Section Diff — bulk-replace endpoint backing Revert ────────

export interface SpecReplaceParagraphItem {
  body_html: string;
  body_text: string;
  paragraph_number?: string | null;
  sequence_num?: number | null;
  anchor_features?: string[];
}

export interface SpecReplaceParagraphsBody {
  paragraphs: SpecReplaceParagraphItem[];
  /** Origin tag for spec_edit_log.event_data; defaults to
   *  ``diff_revert`` server-side. */
  source?: string;
}

export interface SpecSectionReplaceResponse {
  section: SpecSectionRow | null;
  paragraphs: SpecParagraph[];
}

export const postSpecSectionReplaceParagraphs = (
  matterId: string,
  sectionId: string,
  body: SpecReplaceParagraphsBody,
): Promise<SpecSectionReplaceResponse | SpecStateResponse> =>
  api
    .post(
      `/matter/${matterId}/spec/sections/${sectionId}/replace-paragraphs`,
      body,
    )
    .then((r) => r.data);

export const deleteSpecParagraph = (
  matterId: string,
  sectionId: string,
  paragraphId: string,
): Promise<void> =>
  api
    .delete(
      `/matter/${matterId}/spec/sections/${sectionId}/paragraphs/${paragraphId}`,
    )
    .then(() => undefined);

// ─── Spec section / matter paragraph reset (per-section + bulk) ──
//
// Distinct from ``deleteSpecParagraph`` (which removes one paragraph
// + resequences) and from generate-with-overwrite (which DELETEs and
// regenerates in one shot). These two helpers clear the paragraphs
// without regenerating, so the drafter can re-run Generate or
// Generate-Everything against an empty section / empty matter.

export interface SpecSectionResetResponse {
  status: 'reset';
  section_id: string;
  paragraphs_deleted: number;
}
export const resetSpecSectionParagraphs = (
  matterId: string,
  sectionId: string,
): Promise<SpecSectionResetResponse> =>
  api
    .delete(`/matter/${matterId}/spec/sections/${sectionId}/paragraphs`)
    .then((r) => r.data);

export interface SpecAllParagraphsResetResponse {
  status: 'reset';
  sections_reset: number;
  paragraphs_deleted: number;
}
export const resetAllSpecParagraphs = (
  matterId: string,
): Promise<SpecAllParagraphsResetResponse> =>
  api
    .delete(`/matter/${matterId}/spec/paragraphs`)
    .then((r) => r.data);


// ─── Admin: stage_jobs observability (Brief 4) ────────────────────
//
// Backend endpoints landed in commit 088a6eb (Sub-Chunk 9 of the
// async-migration brief). All three are gated by require_admin
// server-side; the client-side UI also double-gates on role.
//
// rq_job_id values contain colons (e.g. "claims:m1:stage_3:claim_generation");
// callers must NOT encodeURIComponent the whole string — the FastAPI
// path matcher uses :path so the colons pass through unchanged.

export interface StageJobRow {
  id: string;
  rq_job_id: string;
  matter_id: string | null;
  module: 'claims' | 'drawing' | 'spec' | 'document';
  stage: string;
  action: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed'
        | 'refused_all_providers' | 'cancelled';
  final_provider: string | null;
  fallback_count: number;
  error_class: string | null;
  error_message: string | null;
  result_summary: Record<string, unknown> | null;
  params_summary: Record<string, unknown> | null;
  step_history: Array<{ step: string; message: string; ts: number }> | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface StageJobsModuleSummary {
  total: number;
  by_status: Record<string, number>;
}

export interface StageJobsListResponse {
  items: StageJobRow[];
  count: number;
  limit: number;
  offset: number;
  filters: {
    module: string | null;
    stage: string | null;
    action: string | null;
    status: string | null;
    matter_id: string | null;
    fallback_only: boolean;
  };
  summary_24h: Record<string, StageJobsModuleSummary>;
}

export interface StageJobDetailResponse {
  stage_job: StageJobRow;
  live_progress: {
    step: string;
    message: string;
    provider: string | null;
    fallback_count: number;
    updated_at: string;
  } | null;
}

export interface AdminListStageJobsParams {
  module?: string;
  stage?: string;
  action?: string;
  status?: string;
  matter_id?: string;
  fallback_only?: boolean;
  limit?: number;
  offset?: number;
}

export const adminListStageJobs = (
  params: AdminListStageJobsParams = {},
): Promise<StageJobsListResponse> => {
  // Strip undefined / empty values so backend defaults kick in.
  const qs: Record<string, string> = {};
  if (params.module)        qs.module = params.module;
  if (params.stage)         qs.stage = params.stage;
  if (params.action)        qs.action = params.action;
  if (params.status)        qs.status = params.status;
  if (params.matter_id)     qs.matter_id = params.matter_id;
  if (params.fallback_only) qs.fallback_only = 'true';
  if (params.limit !== undefined) qs.limit = String(params.limit);
  if (params.offset !== undefined) qs.offset = String(params.offset);
  return api
    .get<StageJobsListResponse>('/admin/stage_jobs', { params: qs })
    .then((r) => r.data);
};

export const adminGetStageJob = (
  rqJobId: string,
): Promise<StageJobDetailResponse> =>
  // The path matcher uses :path so colons inside rq_job_id pass
  // through as-is; do NOT encodeURIComponent here.
  api
    .get<StageJobDetailResponse>(`/admin/stage_jobs/${rqJobId}`)
    .then((r) => r.data);

export interface StageJobRerunResponse {
  rq_job_id: string;
  status: string;
  poll_url: string;
}

export const adminRerunStageJob = (
  rqJobId: string,
): Promise<StageJobRerunResponse> =>
  api
    .post<StageJobRerunResponse>(`/admin/stage_jobs/${rqJobId}/rerun`)
    .then((r) => r.data);


// ─── Specification-draft intake ──────────────────────────────────
// The second road in: a firm arrives with the claims and drawings
// already done and wants only the specification. These routes produce
// the rows the Specification module reads and then hand over to it
// unchanged — see backend/spec_intake.py.

export interface SpecIntakeDigest {
  overview: string;
  problem: string;
  solution: string;
  implementation: string;
  technical_features: string[];
}

export interface SpecIntakeStatus {
  matter: {
    id: string; title: string;
    jurisdiction: 'EP' | 'US' | 'IN';
    entry_mode: string;
  };
  steps: {
    idf: {
      done: boolean; documents: number;
      /** How many of those have finished extraction and indexing.
       * Uploaded is not readable: the digest reads indexed rows only. */
      indexed: number;
      has_digest: boolean;
      frozen: boolean; tech_bucket: string;
      /** NB-37 — 'drafter' when the area was set by hand. */
      tech_bucket_source?: '' | 'auto' | 'drafter';
      /** The disclosure was replaced after this digest was read. */
      stale?: boolean;
      /** What the matter holds, oldest first: the disclosure, then each
       * piece of additional information the client sent. */
      files?: Array<{
        id: number; name: string; kind: 'disclosure' | 'additional';
        status: string; uploaded_at: string;
      }>;
      /** Names of files added after the digest was last read. */
      new_material?: string[];
      /** Fields the drafter corrected since the disclosure was last
       * read. Resynthesizing keeps them; reading again discards them. */
      edited_fields?: Array<keyof SpecIntakeDigest>;
      digest: SpecIntakeDigest | null;
    };
    claims: {
      done: boolean; total: number; independent: number;
      dependent: number; frozen: boolean;
      /** The last in-place revision with the client's changes. */
      last_revision?: SpecIntakeRevisionRecord | null;
    };
    drawings: {
      done: boolean; figures: number; frozen_figures: number;
      numerals: number; needs_description: string[];
      /** Disclosure images not yet brought into the drawings.
       * Anything left behind will not appear in the specification. */
      candidates: number;
      /** NB-36 — registered signs naming one element under two
       * numerals. */
      similar_signs?: SpecIntakeSimilarSigns[];
    };
  };
  /** ``blocking`` stops the hand-over to the Specification page;
   * ``advisory`` is said plainly and lets the drafter through. */
  ready: { done: boolean; blocking: string[]; advisory: string[] };
}

export interface SpecIntakeParsedClaim {
  claim_number: number;
  claim_text: string;
  claim_type: 'independent' | 'dependent';
  parent_claim_number: number | null;
  depends_on: number[];
  category: string;
  notes: string[];
}

export interface SpecIntakeClaimsParse {
  /** False means the parser could not read the set with certainty.
   * Never commit then — ``errors`` says what to fix. */
  ok: boolean;
  claims: SpecIntakeParsedClaim[];
  errors: string[];
  notes: string[];
  counts: { total: number; independent: number; dependent: number };
  source_filename?: string;
}

export interface SpecIntakeReferenceSign { numeral: string; label: string }

/** NB-36 — two or more numerals naming one element ("vapour
 * treatment" 220 and "vapour treatment step" 230), with the sentence
 * the page shows. */
export interface SpecIntakeSimilarSigns {
  /** 'similar_label' (two names for one element) or, since the 4 Nov
   * retest, 'claim_step_clash' (one claim step under different
   * numerals on different figures). */
  kind?: string;
  numerals: string[];
  labels: string[];
  message: string;
}

const intake = (matterId: string, tail: string) =>
  `/matter/${matterId}/spec-intake/${tail}`;

export const getSpecIntakeStatus = (
  matterId: string,
): Promise<SpecIntakeStatus> =>
  api.get(intake(matterId, 'status')).then((r) => r.data);

/** Read the disclosure. Plain, it reads from scratch and discards the
 * drafter's corrections. With ``keepEdits`` it resynthesizes: the
 * corrections outrank the documents, stay word for word, and the rest
 * of the digest is re-read to agree with them. ``corrections`` carries
 * values not yet saved. */
export const postSpecIntakeDigest = (
  matterId: string,
  opts?: { keepEdits?: boolean; corrections?: Partial<SpecIntakeDigest> },
): Promise<SpecIntakeStatus['steps']['idf']> =>
  api.post(intake(matterId, 'idf-digest'), opts?.keepEdits
    ? { keep_edits: true, corrections: opts.corrections ?? null }
    : {}).then((r) => r.data);

export const patchSpecIntakeDigest = (
  matterId: string, body: Partial<SpecIntakeDigest>,
): Promise<SpecIntakeStatus['steps']['idf']> =>
  api.patch(intake(matterId, 'idf-digest'), body).then((r) => r.data);

/** NB-37 — the matter's technology area (sets the drafting style).
 * ``source`` is 'drafter' when set by hand (never overwritten by
 * detection), 'auto' when detected, '' when none is stored yet. */
export interface TechnologyArea {
  matter_id: string;
  tech_bucket: string;
  label: string;
  source: '' | 'auto' | 'drafter';
  can_redetect: boolean;
  options: Array<{ value: string; label: string }>;
  note: string;
}

export const getTechnologyArea = (matterId: string): Promise<TechnologyArea> =>
  api.get(`/matter/${matterId}/technology-area`).then((r) => r.data);

export const patchTechnologyArea = (
  matterId: string, techBucket: string,
): Promise<TechnologyArea> =>
  api.patch(`/matter/${matterId}/technology-area`, { tech_bucket: techBucket })
    .then((r) => r.data);

export const postTechnologyAreaRedetect = (
  matterId: string,
): Promise<TechnologyArea> =>
  api.post(`/matter/${matterId}/technology-area/redetect`).then((r) => r.data);

export const postSpecIntakeClaimsParse = (
  matterId: string, text: string,
): Promise<SpecIntakeClaimsParse> =>
  api.post(intake(matterId, 'claims/parse'), { text }).then((r) => r.data);

export const postSpecIntakeClaimsParseFile = (
  matterId: string, file: File,
): Promise<SpecIntakeClaimsParse> => {
  const form = new FormData();
  form.append('file', file);
  return api
    .post(intake(matterId, 'claims/parse-file'), form)
    .then((r) => r.data);
};

export interface SpecIntakeQcFinding {
  severity?: string;
  claim_number?: number;
  message?: string;
  suggested_fix?: string;
  [key: string]: unknown;
}

export const postSpecIntakeClaimsQc = (
  matterId: string, claims: SpecIntakeParsedClaim[],
): Promise<{
  findings: SpecIntakeQcFinding[];
  summary: Record<string, unknown>;
}> =>
  api.post(intake(matterId, 'claims/qc'), { claims }).then((r) => r.data);

export interface SpecIntakeRevisionParagraph {
  paragraph_id: string; paragraph_number: string;
  section_kind: string; section_title: string;
}

export interface SpecIntakeNumeralToCheck {
  numeral: string; label: string; term: string; claim_number: number | null;
}

/** A revised claim set compared with the confirmed one. */
export interface SpecIntakeRevision {
  unchanged: number;
  renumbered: Array<{ from: number; to: number }>;
  changed: Array<{
    claim_number: number; previous_number: number;
    old_text: string; new_text: string;
    /** Paragraphs written for this claim — for the drafter to review. */
    paragraphs: SpecIntakeRevisionParagraph[];
  }>;
  added: Array<{ claim_number: number; claim_text: string }>;
  removed: Array<{
    claim_number: number; claim_text: string;
    paragraphs: SpecIntakeRevisionParagraph[];
  }>;
  /** Reference signs bound to a term the changed wording no longer uses. */
  numerals_to_check: SpecIntakeNumeralToCheck[];
  has_changes: boolean;
  /** A removal with specification text written for it. */
  blocked: boolean;
  blocked_reason: string;
  applied: boolean;
}

export interface SpecIntakeRevisionRecord {
  at: string;
  renumbered: Array<{ from: number; to: number }>;
  changed: Array<{ claim_number: number; paragraphs: SpecIntakeRevisionParagraph[] }>;
  added: number[];
  removed: number[];
  numerals_to_check: SpecIntakeNumeralToCheck[];
}

/** Compare the client's revised set with the confirmed claims
 * (``apply`` false), or write it in place (``apply`` true). */
export const postSpecIntakeClaimsRevise = (
  matterId: string, claims: SpecIntakeParsedClaim[], apply: boolean,
): Promise<SpecIntakeRevision> =>
  api.post(intake(matterId, 'claims/revise'), { claims, apply })
    .then((r) => r.data);

export const postSpecIntakeClaimsCommit = (
  matterId: string, claims: SpecIntakeParsedClaim[],
): Promise<SpecIntakeStatus['steps']['claims']> =>
  api.post(intake(matterId, 'claims/commit'), { claims }).then((r) => r.data);

export const postSpecIntakeSignsParse = (
  matterId: string, text: string,
): Promise<{
  ok: boolean; signs: SpecIntakeReferenceSign[];
  errors: string[]; count: number;
}> =>
  api.post(intake(matterId, 'reference-signs/parse'), { text })
    .then((r) => r.data);

export const postSpecIntakeSignsCommit = (
  matterId: string, signs: SpecIntakeReferenceSign[],
  /** NB-33 — the figure the signs were read off, so they are recorded
   * on it. Omitted for a pasted legend. */
  figureId?: string | null,
  /** NB-33 (retest 4 Nov) — the figures a pasted legend's signs are
   * on, as ticked. Omitted: a matter with one figure gets them on it. */
  figureIds?: string[] | null,
): Promise<{
  added: number; unchanged: number; total: number;
  conflicts: Array<{
    numeral: string; existing_label: string; supplied_label: string;
  }>;
  /** Numerals sent without a usable name — refused, not written. */
  rejected?: Array<{ numeral: string; reason: string }>;
  /** NB-33 — the figure's label, how many signs were newly recorded
   * on it, and how many claim terms were bound to a sign. */
  figure?: string;
  figures?: string[];
  placed?: number;
  bound?: number;
  similar?: SpecIntakeSimilarSigns[];
}> =>
  api.post(intake(matterId, 'reference-signs/commit'), {
    signs,
    ...(figureId ? { figure_id: figureId } : {}),
    ...(figureIds ? { figure_ids: figureIds } : {}),
  })
    .then((r) => r.data);

export const postSpecIntakeFreezeAll = (
  matterId: string, acknowledgeWarnings = false,
): Promise<{
  frozen: string[];
  skipped: Array<{ figure: string; reason: string }>;
  failed: Array<{
    figure: string; reason: string; issues?: unknown;
    /** The drawing checks' issues, as sentences — this figure's own. */
    issue_lines?: string[];
  }>;
  /** NB-36 minor — issues that are the whole matter's (the claims,
   * signs on no figure), given once. */
  matter_issue_lines?: string[];
  drawings: SpecIntakeStatus['steps']['drawings'];
}> =>
  api
    .post(intake(matterId, 'figures/freeze-all'), {
      acknowledge_warnings: acknowledgeWarnings,
    })
    .then((r) => r.data);

// ─── The figures themselves ──────────────────────────────────────
// Each of these is the drawing module's own handler behind an intake
// shape, so a figure created here is indistinguishable from one
// created in the workbench. What the intake adds is the one call that
// answers "what is there, and what could be".

export interface SpecIntakeFigure {
  id: string;
  fig_label: string;
  sequence_num: number | null;
  /** The one-line description the freeze gates on, and the sentence
   * the brief description of the drawings is written from. */
  description: string;
  is_frozen: boolean;
  numerals_count: number;
  origin: string;
  thumb_url: string;
  full_url: string;
}

/** A disclosure image eligible to become a figure. */
export interface SpecIntakeCandidate {
  id: number;
  label: string;
  description: string;
  thumb_url: string;
}

export interface SpecIntakeFigures {
  /** True until the claims are confirmed — not an error, just the
   * step not being open yet. */
  locked: boolean;
  locked_reason: string;
  candidates: SpecIntakeCandidate[];
  figures: SpecIntakeFigure[];
}

/** Absolute URL for an image path the intake handed back. The paths
 * are server-relative; an `<img src>` needs the backend origin when
 * the app is served from somewhere else. */
export const specIntakeImageUrl = (path: string): string =>
  path ? `${BACKEND_URL}${path}` : '';

export const getSpecIntakeFigures = (
  matterId: string,
): Promise<SpecIntakeFigures> =>
  api.get(intake(matterId, 'figures')).then((r) => r.data);

export const postSpecIntakeFiguresImport = (
  matterId: string, sourceIds: number[],
): Promise<{
  imported: Array<{
    source_matter_figure_id: number; figure: SpecIntakeFigure;
  }>;
  failed: Array<{ source_matter_figure_id: number; reason: string }>;
}> =>
  api
    .post(intake(matterId, 'figures/import'), {
      source_matter_figure_ids: sourceIds,
    })
    .then((r) => r.data);

export const postSpecIntakeFigureUpload = (
  matterId: string, file: File,
): Promise<{ figure: SpecIntakeFigure }> => {
  const form = new FormData();
  form.append('file', file);
  return api.post(intake(matterId, 'figures/upload'), form).then((r) => r.data);
};

export const deleteSpecIntakeFigure = (
  matterId: string, figureId: string,
): Promise<void> =>
  api.delete(intake(matterId, `figures/${figureId}`)).then(() => undefined);

export const patchSpecIntakeFigure = (
  matterId: string, figureId: string,
  body: { fig_label?: string; description?: string },
): Promise<{ figure: SpecIntakeFigure }> =>
  api.patch(intake(matterId, `figures/${figureId}`), body).then((r) => r.data);

/** A reference sign read off the drawing. ``known`` means the
 * registry already has the numeral; ``conflict`` means it has it
 * under a different label, which the drafter has to settle. */
export interface SpecIntakeReadSign extends SpecIntakeReferenceSign {
  /** The drawing carried the number but no legible name beside it.
   * ``label`` is empty; it is named through the legend. */
  unlabeled?: boolean;
  known: boolean;
  existing_label: string;
  conflict: boolean;
}

export const postSpecIntakeReadSigns = (
  matterId: string, figureId: string,
): Promise<{
  figure: string; signs: SpecIntakeReadSign[]; error: string;
  figure_id?: string;
  similar?: SpecIntakeSimilarSigns[];
}> =>
  api
    .post(intake(matterId, `figures/${figureId}/read-signs`), {})
    .then((r) => r.data);

// ─── Courses (user-guide videos) ─────────────────────────────────
// The catalogue comes from the server; ``available`` says whether the
// video file was found in the folder COURSE_VIDEOS_DIR points at.

/** A chapter as the catalogue lists it: where it starts, and whether
 * it is slides or a live demo. */
export interface CourseChapterRef {
  title: string;
  start: number;
  kind?: string | null;
}

/** One slide shown on screen. ``claim`` is a claim split into parts,
 * each a plain string or ``[text, label]`` naming what the part is. */
export interface CourseSlide {
  title?: string;
  bullets?: string[];
  table?: { head?: string[]; rows?: string[][] };
  flow?: string[];
  claim?: Array<string | [string, string]>;
  note?: string;
}

export interface CourseOutlineChapter extends CourseChapterRef {
  slides: CourseSlide[];
  narration: string;
}

export interface CourseSummary {
  id: string;
  title: string;
  duration: string | null;
  summary: string | null;
  audience: string | null;
  available: boolean;
  has_subtitles: boolean;
  has_chapters: boolean;
  /** "beginner" (F lessons) or "workflow" (D lessons). */
  track?: string;
  seconds?: number | null;
  /** Lesson codes to watch first. */
  prerequisites?: string[];
  /** The catalogue's chapters; empty when it has none. */
  chapters?: CourseChapterRef[];
  /** Slides and narration are available from fetchCourseOutline. */
  has_outline?: boolean;
}

export interface CourseTrack {
  id: string;
  title: string;
  intro: string;
  lesson_ids: string[];
  total_seconds: number;
}

export interface CourseList {
  videos_dir_found: boolean;
  /** Absent from older servers: then every lesson is one list. */
  tracks?: CourseTrack[];
  courses: CourseSummary[];
  /** The reference documents (sample IDF, tea letter, …) as one ZIP. */
  resources?: { available: boolean; count: number };
}

/** The course's reference documents as one ZIP. A plain link so the
 * browser downloads it with the session cookie, like the videos. */
export const courseResourcesUrl = (): string =>
  `${BACKEND_URL}/api/courses/resources.zip`;

/** A lesson's chapters with the slides shown on screen and the narration. */
export const fetchCourseOutline = async (
  id: string,
): Promise<{ id: string; chapters: CourseOutlineChapter[] }> =>
  (await api.get(`/courses/${encodeURIComponent(id)}/outline`)).data;

export const listCourses = async (): Promise<CourseList> =>
  (await api.get('/courses')).data;

/** Handed straight to <video src> so the browser streams the file
 * with range requests. The session cookie goes with it because the
 * page and the API are same-site. */
export const courseVideoUrl = (id: string): string =>
  `${BACKEND_URL}/api/courses/${encodeURIComponent(id)}/video`;

/** Subtitle and chapter files come through axios and reach <track>
 * as blob: URLs. That sidesteps the cross-origin rules <track> has
 * when REACT_APP_BACKEND_URL points the dev server at uvicorn on
 * another port. */
export const fetchCourseVtt = async (
  id: string, kind: 'subtitles' | 'chapters',
): Promise<string> =>
  (await api.get(`/courses/${encodeURIComponent(id)}/${kind}`, {
    responseType: 'text',
    transformResponse: (r) => r,
  })).data;
